// APP_REVISION: marca de despliegue del frontend. SUBIR EN CADA DEPLOY.
//
// No la acoplamos a `package.json#version` porque esa solo cambia en releases
// del core de Chatventas. Esta constante es nuestro marcador interno.
//
// Esquema de versionado: a partir de 2026-05-01 pasamos de `revNN` a
// SemVer-like `MAJOR.MINOR.PATCH`. El número MAJOR (69) corresponde al
// último `rev` al que estábamos antes del corte (rev69 = vandalismo
// "Hackeado"). MINOR sube cuando hay un bloque grande de cambios; PATCH
// para hotfixes.
//
// Historial:
//   69.0.1 — primer bump del nuevo esquema; consolida revision5 + hallazgos
//            contactos (rev69 ContactPolicy).
//   69.0.2 — RescueStalePending no pisa FollowUpSequenceJob (PRIMARY guard
//            follow_up_in_progress + cooldown 6h + max 2 rescates) + naming
//            consistency (rev71 → 69.0.X en comentarios).
//   69.0.3 — bug fixes combinados:
//            (1) orphan contacts: Contact/Conversation cascade sync
//                (`:destroy` en vez de `:destroy_async` para convs, contact_inboxes,
//                messages, conversation_participants). Garantiza consistencia
//                referencial al borrar contactos.
//            (2) Current.simulator bleed-through entre requests del mismo thread
//                Puma. ApplicationController#reset_simulator_context resetea el
//                flag al inicio de cada request; SimulatorContextMiddleware del
//                server middleware resetea a nil en el ensure (no a previous).
//                Cierra el caso donde un request K6 (X-Simulator: true)
//                contaminaba el thread y el siguiente request real encolaba
//                jobs en el cluster simulator (no-op) → bot no respondía.
//            (3) seed sprint admin role para que K6 pueda hacer handoff post-rev59.
//   69.0.4 — cierre coherente del flujo conversacional + CSAT:
//            (1) system prompt config-flag `closing_invitation_enabled` para
//                permitir invitar al cierre dentro de la respuesta del bot
//                ("¿Hay algo más en lo que pueda ayudarte?"). Default OFF
//                preserva comportamiento upstream — Cuscasense activa via
//                `assistant.config.closing_invitation_enabled = true`.
//            (2) handle_solid `:farewell` ahora resuelve la conv tras enviar
//                el mensaje de despedida (antes quedaba en :pending → eventual
//                "¿Sigues ahí?" del RescueStalePending fuera de contexto).
//            (3) handle_weak con bot history reagenda FollowUpSequenceJob
//                (antes el incoming :weak cancelaba el FollowUp previo y la
//                conv quedaba huérfana hasta el rescate).
//            (4) `conversation_has_bot_history?` reescrito sobre `sender_type`
//                porque el query JSON `content_attributes->>'captain_response'`
//                no funciona — la columna es type `json` y `store ..., coder: JSON`
//                escribe la data como JSON-string, no JSON-object.
//   69.3.7 — #739 ruteo consulta-estado + #744 institución deshabilitada + guard anti-alucinación reforzado:
//            (1) #739: el router LLM improvisaba ejemplos de instituciones ("RNPN, MINSAL, Alcaldía")
//                en el intake de consulta de estado. RAÍZ: la biblioteca semántica se resembró con
//                claves 'sistema/estado_tramite' / 'sistema/listar_dui' (más ejemplos, dominan el
//                match) pero DIRECT_ROUTE_MAP tenía solo las viejas 'tramite/*' → caía al router.
//                FIX: se agregan las claves 'sistema/*' al mapa → entra al escenario (lista SOLO las
//                instituciones habilitadas).
//            (2) #744: con la institución DESHABILITADA (active_for_bot=false / captain_enabled=false)
//                el bot igual ofrecía la KB de esa institución Y se ofrecía a transferir. RAÍZ:
//                HandoffConfirmationGate#offer_kb_help_for_topic! no filtraba por habilitada y el
//                transfer no lo chequeaba. FIX: scope de KB-help limitado a habilitadas + guard
//                team_available_for_bot? en check_or_request!, process_response_and_execute! y
//                execute_pending_handoff! → si está deshabilitada: ni KB ni transferencia, responde
//                "no está disponible en este canal".
//            (3) Guard anti-alucinación (#bloom) reforzado: ENTITY_STOPWORDS ampliado (tengo, nuestra,
//                señora, dijeron, etc.) para que frases conversacionales con entidad ausente también
//                caigan en "no tengo esa información".
//   69.3.6 — anti-alucinación KB DURA + fix filtro búsqueda KB + #721 anti-bleed:
//            (1) #bloom: el bot inventaba datos REALES del mundo (dirección/teléfono) de entidades
//                que NO están en la KB. El backstop solo REEMPLAZA ante match; si no había match
//                dejaba pasar la invención del LLM. Nuevo guard determinístico en AgentRunnerService
//                (asked_about_absent_entity?): si el ciudadano nombra una entidad distintiva ausente
//                de la KB y el turno no quedó grounded (sin tools o solo faq) → se suprime con "no
//                tengo esa información". La KB MANDA, nunca un dato no validado. Guard léxico aditivo
//                (NO toca el umbral 0.45 del backstop).
//            (2) Filtro de búsqueda en Conocimiento (CuscaSense) no filtraba: el frontend mandaba
//                searchKey (camelCase) y el backend lee search_key → el param llegaba vacío.
//                Corregido en api/captain/response.js.
//            (3) #721 anti-bleed: captain_status_query_called y captain_faq_attempted son
//                thread_mattr (sin auto-reset) y Current.reset NUNCA corre en el pipeline de Captain
//                → un thread de Sidekiq quedaba "envenenado" y podía bloquear la consulta de estado
//                de OTRA conversación. Ahora se resetean por turno en generate_response (junto a
//                captain_rag_grounded).
//   69.3.5 — Caja de búsqueda KB + endurecimiento #731 (multi-trámite) + agent_manage:
//            (1) Caja de búsqueda en la lista de Conocimiento de CuscaSense: input con
//                debounce (300ms) → search_key server-side (ILIKE sobre question/answer)
//                en assistant_responses#index. Busca en TODA la KB, no solo la página
//                visible (la lista pagina a 25 → client-side no alcanzaba).
//            (2) Consulta de estado — NUNCA ejemplificar instituciones (#731): regla
//                fuerte en el escenario team_status_query y en el prompt del router
//                (assistant.liquid). Solo se piden las instituciones con webhook activo;
//                prohibido improvisar "RNPN, MINSAL, Alcaldía...".
//            (3) Conservar contexto multi-trámite (#731): StatusQueryTool fija
//                conversation.team_id a la institución resuelta (update_columns, sin
//                disparar asignación) y, si el ciudadano no nombra institución en el
//                turno pero la conv ya tiene una de estado confirmada ("la misma" /
//                "Ahora con el trámite X"), usa ESA sin re-preguntar ni desambiguar.
//            (4) agent_manage: la edición de AGENTES exige permiso dedicado (antes la
//                habilitaba conversation_manage_advanced → un manager de conversaciones
//                podía renombrar agentes; raíz del vandalismo de nombres #728-breach).
//            (5) Migraciones: agent_manage (no-lockout) + rev12b (re-seed templates +
//                regenera escenarios con (2)/(3)).
//   69.3.4 — Hotfix conversacional (post-69.3.3, info incorrecta en prod #728):
//            (A) san rafael: el KB-backstop tomaba el PRIMER mensaje del chat como query — el
//                default_scope ASC de Message vencía al .order(:desc) → consultaba la KB con el
//                mensaje equivocado (usó "Necesito hablar con alguien del MINSAL" en vez de
//                "dirección del hospital san rafael" → devolvía medios de contacto generales).
//                Fix: .reorder(created_at: :desc, id: :desc) (mismo patrón que 69.1.73 #1).
//            (B) Santa Gertrudis: el KB-help del gate (offer_kb_help_for_topic!, presenta info
//                ANTES de transferir, SIN criterio del LLM) usaba umbral 0.55 → "hospital Santa
//                Gertrudis" matcheó el FAQ del Hospital San Rafael (otro hospital) a 0.482 →
//                presentó el hospital EQUIVOCADO. Nuevo umbral dedicado KB_HELP_PRESENT_THRESHOLD
//                = 0.40 (los matches correctos rondan 0.25-0.31); si no hay match fuerte → "no
//                tengo esa info" + ofrece agente. Validado E2E local (san rafael correcto;
//                Santa Gertrudis → no inventa). NOTA: el breach de nombres "Netanyahu" fue un
//                usuario interno (Omar) abusando del custom_role, NO este deploy.
//   69.3.3 — Conversacional rev12 (handoff #719 + institución dinámica + status #720/#721):
//            (A) Handoff HELP-FIRST: ante pedido de agente, PREGUNTA la razón si no está en
//                contexto (no la infiere), busca en la KB para AYUDAR primero, SIEMPRE ofrece
//                transferir y SIEMPRE pide confirmación antes de ejecutar. La transferencia es
//                intención APARTE (solo ingreso a COLA): mientras la oferta está pendiente, las
//                preguntas se responden desde la KB. Verbo-solo ("transfiéreme") arma el gate
//                (include?('handoff'), no start_with). "trámite" → "tema"; no pide ambas cosas.
//            (B) INSTITUCIÓN DINÁMICA: conversation.team_id se asigna/MUTA por turno según la
//                institución que el ciudadano nombre (sync_conversation_institution! en
//                ConvEngineJob) + por KB cuando es implícita ("hospital rosales"→MINSAL, en el
//                backstop). Antes el team NUNCA se asignaba hasta ejecutar handoff → el LLM no
//                recibía :assigned_institution → faq_lookup_team con scope equivocado → KB-miss.
//            (C) GROUNDING ESTRICTO (la KB MANDA): backstop de KB corre en respuesta no-grounded,
//                en error del LLM y output vacío → si la KB tiene la respuesta, REEMPLAZA lo que
//                el LLM diga de su cabeza; umbral 0.40 (solo matches fuertes — evita pisar con
//                ruido tipo "01234567-8"↔Antecedentes a 0.48). Si no la tiene y el LLM inventa, el
//                sanitizer lo bloquea. Cero datos fuera de la KB.
//            (D) ANTI-LISTADO: el bot NUNCA enumera instituciones (ni aunque insistan/ing.inversa).
//                Quitada la línea del prompt que ofrecía "puedo listarte las instituciones" + los
//                errores de assign_team/faq_lookup_team ya no devuelven la lista al LLM.
//            (E) status_query #720: NO orientar ni adivinar la institución por TIPO de trámite
//                (documento/licencia/...); si el ciudadano no la sabe → lista SOLO las de webhook
//                activo + mandar a verificar. #721: UNA institución, UNA consulta (guard de llamada
//                única en Current + institución-primero estricto) — cero consulta cruzada.
//                Migración 20260630120000 re-seedea + regenera escenarios. Validado E2E (sonnet).
//   69.3.2 — fix conv #713 (NO transfería pese a pedir agente claramente, AÚN con sonnet):
//            (A) try_retroactive_handoff! ahora reconoce la oferta de transferencia del LLM
//                aunque la pregunta del TEMA esté en el MEDIO del mensaje (antes el regex
//                LLM_CONFIRMATION_HINTS exigía que la pregunta TERMINARA el texto `\?\s*$`
//                → con "…¿podría indicarme sobre qué trámite…? Así el agente…" no matcheaba
//                → la red retroactiva no derivaba y el LLM respondía el tema con KB). Ahora
//                evalúa todo el texto + reconoce la pregunta del tema en cualquier posición.
//            (B) guard anti-fuga del campo `reasoning` INTERNO: el LLM a veces narra en 3ra
//                persona ("El ciudadano solicita… y desea ser atendido por un agente…") en
//                vez de responder → eso NUNCA debe llegar al ciudadano; ahora se detecta y
//                reemplaza por un fallback útil. NO es problema de modelo (sonnet lo hizo
//                igual): el fix es determinístico.
//            (C) CAMINO ROBUSTO (la estrategia de fondo): la biblioteca SEMÁNTICA de
//                intenciones (embeddings — robusta al wording: "hablar con alguien de la DC"
//                dist 0.27 < umbral 0.35) ahora ARMA el gate DETERMINÍSTICO (arm_handoff_gate!)
//                en vez de delegar al LLM el llamado del tool. Detección por SIGNIFICADO +
//                acción por ESTADO del gate → la transferencia ya NO depende de regex sobre el
//                texto del LLM. (A)/(B) quedan como respaldo (defensa en profundidad). Validado
//                E2E en local: "quiero hablar con alguien de Hacienda" → gate determinístico →
//                tema ("pago de impuestos") → TRANSFIERE (encola al team). Specs (20 casos).
//            + costeo: CaptainLlmPricing.resolve_model — si body['model'] viene vacío usa el
//              modelo PEDIDO (no el DEFAULT Opus, que sobre-costearía 5-8x). Spec (5 casos).
//   69.3.1 — fix conv #706 (sobre-gateo por tema en pedido de agente): "hablar con ALGUIEN
//            de X" no disparaba el gate determinístico (looks_like_human_request? no
//            reconocía "alguien"/"una persona") → caía al LLM, que re-juzgaba el tema
//            ("precio de la canasta") como off-topic y RECHAZABA en vez de transferir. Ahora
//            "alguien/alguno/una persona/encargado" disparan el gate → confirma razón →
//            transfiere SIN gatear por tema. Spec de regresión.
//            + conv #707 (2da consulta de estado se rompía): el LLM perdía el flujo en un 2do
//            trámite (KB-miss / ofrecía cerrar). Contexto ampliado 30→50 mensajes
//            (MAX_CONVERSATION_MESSAGES) → mantiene el hilo multi-trámite, conserva la
//            institución del contexto y re-pide el DUI por trámite (regla #691 intacta).
//   69.3.0 — SEGURIDAD + FIABILIDAD del motor:
//            (1) Breach de nombres — re-activada la auditoría de identidad. El kill-switch
//                `unless: proc { |_u| true }` en enterprise/.../audit/user.rb desactivaba
//                TODO audit de User (el comentario decía "false" pero devolvía true) → el
//                rename de 16 agentes no dejó rastro (NAME_AUDITS=0). Ahora name/email/
//                display_name se auditan en update. Spec de regresión + 16 nombres reales
//                restaurados en prod.
//            (2) conv #701/#702 (status query → doble-ack + MUTE) — el catch-all de error
//                (recent_outgoing? en ResponseBuilderJob) contaba el acuse proactivo como
//                "ya respondí" → no mandaba el canned → mute. Ahora ignora los acuses.
//            (3) Doble "un momento, voy a revisar" — lock NX anti-doble-acuse en
//                handle_complex (la carrera de 2 ConvEngineJob ya no duplica el acuse).
//   69.2.9 — seguridad: log forense de INTENTOS DENEGADOS. Todo rechazo de autorización
//            (Pundit→403) deja una línea [SECURITY][AUTHZ-DENIED] con user+email,
//            controller#action, método+path (con el target) e IP. Cierra la visibilidad del
//            vector API/UI para edición no autorizada de agentes/equipos/cuenta. (El breach
//            de nombres fue por consola/DB — eso NO lo cubre la app, es ops.) Pentest del rol
//            "Agente Humano" (solo conversation_participating_manage): escalada BLOQUEADA.
//   69.2.8 — fix conv #695/#697 (off-topics SEGUIDOS → doble-ack + MUTE, causa REAL):
//            el guard anti-loop (duplicate_recent_outgoing? en ResponseBuilderJob) suprimía
//            la respuesta a un mensaje NUEVO del ciudadano cuando se parecía a una previa
//            dentro de 30s (off-topics seguidos dan respuestas "no tengo info" parecidas) →
//            el ciudadano quedaba MUDO. Ahora NO suprime si hay un incoming nuevo tras el
//            outgoing similar (eso es una réplica, no un loop). El loop-storm real (#470,
//            sin incoming nuevo entre medio) sigue suprimiéndose. Spec de regresión.
//   69.2.7 — fix conv #695 (off-topic → doble-ack + MUTE): cuando el agente emite SOLO
//            acuses ("un momento, voy a revisar") y respuesta real vacía, la recuperación
//            de output vacío ahora SALTA los acuses; si no hay respuesta real, da un
//            fallback KB-miss útil en vez de quedar mudo (antes se recuperaba el acuse y el
//            guard de duplicados lo suprimía → mute). Spec de regresión agregado.
//   69.2.6 — selectores del pipeline ahora ofrecen los modelos del gateway (minimax-m3,
//            deepseek-v4-flash/v3.2, kimi-k2.6) en COMPLEX y SOLID, para A/B. + presets de la
//            proyección comparativa recomputados con tarifas REALES del gateway (las viejas
//            estaban ~10x infladas) + presets baratos (Haiku+MiniMax, Haiku+DeepSeek, Full
//            DeepSeek) + preset "Haiku+Sonnet — ACTUAL" para reflejar el esquema real.
//   69.2.5 — fix multi-agente: handoff entre agentes dejaba output VACÍO → bot MUDO en
//            consulta de estado (recupera el último mensaje no vacío del run, runner.rb de
//            ai-agents) + B anti-inferencia de institución por formato del número (Layer 1j)
//            + D DUI nunca reusar (sin revivir #647) + negritas WhatsApp (**x**→*x* por
//            canal) + pricing REAL del gateway para A/B (minimax/deepseek/kimi, in/out/cache).
//   69.2.4 — FIX regresión (prod #692): el handoff retroactivo disparaba transferencia
//            ante aclaraciones de ESTADO ("¿es de SIMPLE o de tal institución?") porque
//            los keywords incluían "institución"/"equipo de"/"atender". Quitados: ahora
//            solo dispara con oferta CLARA de transferir a humano (conectar/transferir +
//            agente). Mantiene transferir-al-dar-razón (#690/#693), sin falsos positivos.
//   69.2.3 — FIX coherencia (prod #687/#690):
//            (1) KB-miss: el bot ya NO afirma "no la tendré más tarde" (eso negaba el
//                futuro; la KB se amplía luego). Habla SOLO del presente (REGLA DURA #5).
//            (2) Handoff retroactivo: tras ofrecer/preguntar transferencia, si el ciudadano
//                responde con un TEMA (no solo "sí") y no es rechazo, ejecuta el handoff al
//                equipo ofrecido — antes el LLM re-evaluaba el tema como off-topic y rechazaba
//                ("precio de canasta básica" tras ofrecer Defensoría del Consumidor).
//   69.2.2 — FIX handoff + clasificación (prod #676/#678/#680/#681):
//            (1) HandoffConfirmationGate: UNA pregunta (el tema) y transfiere de una vez.
//                AWAITING_TOPIC ahora PERSISTE el estado (handoff_awaiting_topic) y la
//                respuesta del ciudadano ejecuta el handoff DIRECTO — sin bloquear por KB ni
//                pedir confirmación sí/no. Mata el loop/silencio (#678/#680). AssignTeamTool
//                llama temprano (la institución basta; el gate pide el tema).
//            (2) #681: pure_greeting? ignora el nombre del propio bot como vocativo
//                ("Cusca, hola?") — antes caía en looks_like_institution_query? → KB miss.
//            (3) #676: canned de entidad-no-listada sin la falsa promesa "prueba consultar
//                luego" (causaba incoherencia el turno siguiente) + en usted; backstop de
//                tuteo "prueba/intenta consultar" en el sanitizer.
//   69.2.1 — FIX motor híbrido: modelos de razonamiento (gpt-5*/o-series) rechazan
//            `max_tokens` y `temperature!=1` en la API directa (400). Nuevo helper
//            build_chat_params: para reasoning models usa `max_completion_tokens`
//            (con headroom) + `reasoning_effort: low`, sin temperature; resto igual.
//            Habilita el clasificador gpt-5-mini en native (mixto Haiku+Opus). Aplica
//            en call_classification_llm y call_llm.
//   69.2.0 — DESPLIEGUE GRANDE: motor nativo (Opus + gpt-5 vía /v1/messages con prompt
//            caching) + costeo dinámico 100% (clasificador + SOLID + agente + guards,
//            computado por tokens a tarifa oficial) + biblioteca semántica (intents +
//            sinónimos, Escalón 1) + registro USTED en todo el bot + guards de grounding
//            (no hablar fuera de la KB) + handoff exige tema relacionado a la KB + panel
//            Captain en super admin (4 pestañas, selector por cerebro). Migraciones
//            aditivas: captain_intent_examples + captain_term_synonyms.
//   69.1.82 — SEGURIDAD ARCHIVOS + registro usted (cola larga). (1) Adjuntos: allowlist
//             endurecido — ACCEPTABLE_FILE_TYPES sin comprimidos (zip/7z/rar/tar) y
//             media_file? con allowlist EXPLÍCITO (ACCEPTABLE_MEDIA_TYPES) en vez de
//             wildcard image/* video/* audio/* → ya NO cuela image/svg+xml ni subtipos
//             x-* arbitrarios. Solo imágenes/videos/audios/PDF/docs en formatos específicos.
//             Caducidad de URLs de adjuntos confirmada en 5 min (active_storage urls_expire_in).
//             Spec attachment allowlist (4). (2) Registro usted — cola larga de mensajes
//             citizen-facing que faltaban: follow-up/cierre (FollowUpSequenceJob), rescate
//             (RescueStalePending), system prompt del runner V2 (system_prompts_service +
//             regla de registro), fallbacks de guard (ResponseSanitizer FALLBACK/OFFENSIVE/
//             GUARD/OFF_TOPIC, InputGuard SAFE_RESPONSE), ResponseCache ACTION_PATTERNS
//             (detección alineada a usted), auto_resolution_message (es.yml). Validado en
//             web chat las 6 convs de prod (#653 NIT+trámite, #655 razón, #650 canasta-básica
//             KB→transfiere, #647 pide DUI, #648 consulta sin ofrecer transferir) — todo en usted.
//             Seguridad contactos IDOR: SAFE (scoped, spec contact_policy_scope 5/0).
//   69.1.81 — REGISTRO USTED: el bot habla SIEMPRE en usted formal (institucional),
//             nunca voseo rioplatense ("compartís/tenés/podés") ni tuteo ("puedes/
//             tienes/tu trámite"). Bug reportado: el mensaje de "listar por DUI" decía
//             "si me compartís su número… ¿Tenés a mano…?" (voseo) — yo había arrastrado
//             mi registro a mensajes citizen-facing. Fix en 3 capas: (1) regla de registro
//             global en los prompts (assistant.liquid + scenario.liquid), que prevalece
//             sobre cualquier ejemplo; (2) ~25 mensajes hardcodeados citizen-facing
//             pasados a usted (acuses, cola, adjuntos, handoff gate, status_query,
//             sanitizer 1h/1i, clarify, reengage, etc.); (3) template team_status_query
//             del seeder: ejemplos recitados a usted + migración 20260626190000 que
//             re-seedea y regenera escenarios. Ampliado BOT_DESTINATION_CONFIRMATION_REGEX
//             para detectar también las formas de usted (le/transferirle/conectarle).
//             Validado en web chat (hardcoded + LLM en usted) + specs verdes.
//   69.1.80 — HOTFIX P10 + loop reuso DUI + identificador NIT/DUI + handoff con tema-KB
//             + anti-estado-inferido de trámite + recordatorio de cola sin corte
//             (bugs prod conv #649/#651, #647, #653, #655).
//             (#655-cola) El recordatorio "Aún estás en la cola…" cortaba a los 3
//             (MAX_REMINDERS=3) → el ciudadano quedaba EN SILENCIO aunque siguiera
//             encolado. Por decisión de producto: recordar en cada intervalo MIENTRAS
//             siga en cola (evitar silencio), hasta que el usuario salga ("salir" la
//             desencola) o un agente la tome. Fix: QueueReminderJob sin tope; sigue
//             reprogramando mientras queued? y sin assignee. Spec queue_reminder_job (4).
//             (#647-b) Estado de trámite inferido = parece alucinación a los auditores:
//             el bot afirmaba "no encontré el trámite", "no hay datos sobre el trámite" o
//             un estado SIN haber llamado a status_query (p.ej. "buscá en ambas
//             instituciones" → fabricaba "no aparece en ninguna"). Fix determinístico:
//             ResponseSanitizer Layer 1h (INFERRED_STATUS_RE) — si NO hubo grounding del
//             webhook este turno (rag_sourced=false) y el texto afirma un resultado/estado
//             de trámite, se reemplaza por un re-ruteo al flujo (nunca un estado inventado).
//             + Layer 1i (MULTI_INSTITUTIONS_RE): si el bot ofrece "buscar en ambas/todas
//             las instituciones" (la consulta es SIEMPRE de UNA), se reemplaza por un pedido
//             firme de definir UNA institución (nombrando las habilitadas).
//             + regla de escenario: NUNCA afirmar un resultado no consultado, NO "buscar en
//             ambas instituciones", y TODO lo de trámite entra al flujo (Paso 1→2→3).
//             Migración 20260626180000. Spec response_sanitizer_inferred_status (6).
//             (#655) Endurecer la transferencia a humano: SIEMPRE debe haber un TEMA y,
//             si la institución tiene FAQs/KB, el tema debe COINCIDIR con la KB. Antes
//             "necesito un agente de minsal" (sin tema) transfería en seco. Fix en el
//             chokepoint HandoffConfirmationGate.check_or_request!: topic_match_against_kb
//             → :need_topic (pide el tema), :no_match (no transfiere, pide trámite válido),
//             :ok (ofrece). Fail-OPEN ante KB vacía o error de embeddings (no romper
//             instituciones sin FAQs). AssignTeamTool/HandoffTool solo ejecutan ante
//             CONFIRMED. Spec handoff_confirmation_gate_topic (7). Validado E2E web chat:
//             tema en KB → ofrece; sin relación → no transfiere; al AGREGAR el tema a la
//             KB → pasa a ofrecer.
//             (#653) El bot mezclaba DUI/NIT y no avanzaba: el ciudadano daba un número
//             con FORMATO de DUI pero lo llamaba "NIT" ("mi NIT es 90569558-4") y el bot
//             no lo aceptaba → loop pidiendo "el DUI". Fix (escenario team_status_query):
//             el identificador es SIEMPRE el de la institución (DUI), nunca NIT/otro; se
//             valida por FORMATO, no por la etiqueta que use el ciudadano → si el número
//             cumple el formato del DUI, se usa (aclarando) en vez de loopear. Migración
//             20260626170000 (re-seed + regenera escenarios). Validado E2E (web chat
//             local): "Mi NIT es 04747881-4" → consulta y devuelve el estado, sin loop.
//             Nota: fix de nivel prompt/escenario (reduce, no elimina al 100%, la
//             varianza del LLM) — a diferencia de los gates deterministas (#649/#647).
//             ----
//             (#647) Loop de reuso de DUI: al consultar un 2º trámite con el MISMO
//             DUI del 1º, la guardia anti-reuso (rev9/P9, StatusQueryContextGuard)
//             bloqueaba el reuso silencioso para forzar la aclaración; pero como el
//             ciudadano confirmaba ("sí, el mismo") SIN re-tipear el número, la
//             guardia bloqueaba SIEMPRE → loop infinito y el webhook NUNCA se
//             llamaba ("ya no consulto ni avanzo"). Fix: loop-breaker — se bloquea el
//             reuso silencioso UNA vez; si se reintenta el mismo (trámite, DUI), se
//             permite (ya no es silencioso). + el mensaje ahora pide RE-ENVIAR el DUI
//             (no un "sí/no") para que reprovided_for? lo resuelva de una. Spec
//             status_query_context_guard (5 casos). Validado E2E (conv #705): 2º
//             trámite con mismo DUI → ejecuta el webhook, no loopea.
//             ----
//             (#649/#651) el bot pedía la institución/datos de
//             forma DECLARATIVA, SIN "?" ("necesito saber de qué institución es el
//             trámite. Las opciones disponibles son: …, SIMPLE."). El gate
//             last_outgoing_ends_with_open_question? (HookExecutionService) exigía un
//             "?" → no reconocía el pedido → con un follow-up activo, el
//             ClosingIntentService clasificaba la respuesta "Simple" como :ambiguous y
//             disparaba "¿Deseas cerrar la conversación...?" (closing_clarification) a
//             mitad de flujo. Fix: BOT_DATA_REQUEST_PATTERN — el gate ahora reconoce
//             también los pedidos de datos declarativos (institución / DUI / N° de
//             trámite / "en el formato"), no solo los que terminan/contienen "?".
//             Validado E2E local (conv #704): "Simple" → continúa pidiendo el DUI (ya
//             NO dispara el cierre). Spec hook_execution_open_question_gate (8 casos).
//             NOTA: el fix de escenario de 69.1.79 (P10/P15) era correcto pero cubría
//             OTRO camino (la respuesta del ResponseBuilder); este bug venía del camino
//             de intención de cierre (follow-up).
//   69.1.79 — Rev10 conversacional: 5 fixes determinísticos sobre el flujo de consulta
//             de trámites (validados en vivo en el chatweb local). Cierra defectos del
//             reporte QA puntos 10-17:
//             - P12 (interpretación de intención): guardia de NEGACIÓN en
//               looks_like_human_request? (HUMAN_REQUEST_NEGATION_REGEX). "No quiero que
//               me transfieran" / "no quiero hablar con un agente" YA NO disparan la
//               oferta de handoff. La negación va pegada al verbo/objeto para no tragarse
//               "no tengo el número, quiero un agente".
//             - P14 (acuses operativos): saneo determinístico del OBJETO del acuse
//               proactivo (PROACTIVE_FORBIDDEN_OBJECT_REGEX en ConversationalEngine) +
//               prompt corregido. El acuse nombra el TRÁMITE, nunca el DUI/identificador
//               ni la institución; si nombra uno prohibido, cae al acuse genérico.
//             - P13 (listar por DUI): pre-filtro handle_dui_listing_request — "¿qué
//               trámites tengo bajo mi DUI?" responde claro que NO está contemplado y
//               pide N° de trámite + institución, sin transferir ni emitir acuse.
//             - P11 (derivación injustificada): el fallo TÉCNICO del webhook
//               (timeout/excepción) ya no culpa los datos del ciudadano ni inventa "no
//               encontrado"; lo nombra como problema técnico y ofrece derivación.
//             - P10/P15 (cierre prematuro / alcance): regla de escenario "NO CIERRES
//               MIENTRAS FALTEN DATOS" — prohíbe "¿algo más?"/despedidas mientras se
//               recopilan datos obligatorios (Pasos 1-2 de team_status_query).
//             Tests verdes: conversational_engine_job_intent_guards_spec (P12/P13),
//             conversational_engine_spec (P14), status_query_tool_spec (P11).
//             P16 (no encontrado vía webhook) ya estaba OK y sigue OK.
//   69.1.78 — SEGURIDAD: adjuntos (CAPA 1) — matar URLs legacy que no expiran.
//             Reportado: una URL /rails/active_storage/blobs/redirect/<signed_id>/x.mp4
//             seguía descargable (sin sesión). El signed_id decodificaba con exp:null
//             → válido PARA SIEMPRE. 69.1.31 ya puso urls_expire_in=5min, así que TODA
//             URL nueva expira; pero los signed_id emitidos ANTES quedaron eternos.
//             Nuevo guard (initializer active_storage_require_signed_expiry.rb) en los
//             RedirectController de Blobs y Representations: rechaza (404) cualquier
//             signed_id sin exp en el wrapper _rails. El dashboard regenera la URL
//             fresca (5min) al renderizar, así que <img>/<video> siguen andando; solo
//             mueren las URLs legacy filtradas. Fail-open ante signed_id no decodificable
//             (no rompe URLs válidas). Verificado: exp:null→404, exp vigente→redirige;
//             exports/emails/PDF usan rails_blob_url (exp seteado) → no se afectan.
//             Residual: dentro de la ventana de 5min el link sigue anónimo — cerrar eso
//             exige endpoint autenticado/scopeado (problema del <img src> sin token).
//   69.1.77 — HOTFIX de 69.1.76 + UX de resolve.
//             - Punto 3 (fix del fix): el acuse de cancelación de 69.1.76 se emitía
//               ante cualquier OPEN del judge de aceptación (que incluye ambigüedad,
//               aceptaciones que el judge no pescó y fail-open por timeout del LLM) →
//               CANCELABA transferencias VÁLIDAS en prod ("Confirmar", "Si para
//               negocios btb", "negocios b2b"). Ahora el acuse SOLO se emite ante un
//               rechazo EXPLÍCITO (HandoffConfirmationGate#explicit_decline?, alta
//               precisión); todo lo demás cae al LLM (comportamiento original).
//               3 vías: ACCEPTED / DECLINED (acuse) / REJECTED (al LLM). Spec 8/8 con
//               las frases reales de prod como casos.
//             - Resolve UX: al resolver, el agente regular pierde acceso a la conv
//               (PermissionFilterService limita a status=open) → el refetch daba 403 →
//               toasts "No tienes los permisos" + el chat no se limpiaba. ResolveAction
//               navega ahora al LISTADO correspondiente (no router.back(), que falla en
//               deep-links) → chat limpio, sin refetch fuera de scope. Y se suprime el
//               toast "se quitó de tu vista" al resolver (flujo normal, sin notificación).
//   69.1.76 — Conversacional: dos fixes de Rev10.
//             - Punto 3 (handoff mudo al declinar): cuando el ciudadano DECLINA la
//               oferta de transferencia ("no, mejor no, ya no quiero el agente"), el
//               HandoffConfirmationGate dejaba "fluir al LLM normal", pero un "no" seco
//               se clasifica débil y el bot quedaba MUDO. Ahora el Gate emite un acuse de
//               cancelación determinístico ("De acuerdo, no realizo la transferencia.
//               Seguimos por aquí. ¿En qué más puedo ayudarte?") y HookExecutionService
//               aborta el flow en REJECTED (no depende del LLM). Spec gate 4/4.
//             - Punto 6 (institución ambigua → el LLM busca y asume): REGLA DURA #4 del
//               prompt obligaba a buscar siempre y, ante ambigüedad de institución,
//               "consultar 2-3 equipos" — el LLM asumía. Se agrega un carve-out que
//               distingue la ambigüedad del BOT (usuario no nombra institución → buscar
//               2-3 ok) de la del CIUDADANO (nombra dos y duda: "no sé si SIMPLE o
//               Vivienda" → PREGUNTAR antes de cualquier búsqueda, no asumir).
//   69.1.75 — SEGURIDAD: SSRF — dos bypass cerrados en el fetch de URLs externas
//             (upload por external_url, import de avatar por avatar_url).
//             (1) Ofuscación numérica de IP: el guard validaba con IPAddr/Resolv,
//                 que NO parsean formas hex/octal/decimal/abreviadas — pero el
//                 cliente HTTP real (getaddrinfo/inet_aton) SÍ. Así
//                 http://0x7f.0x00.0x00.0x01, http://2130706433, http://0177.0.0.1
//                 y http://127.1 resolvían a 127.0.0.1 al fetchear, salteando el
//                 filtro. Ahora canonizamos el host con Addrinfo + AI_NUMERICHOST
//                 (mismo parser que el fetch) antes de DNS → toda forma numérica se
//                 normaliza a su IP real y se valida contra los rangos internos.
//             (2) Redirects: open-uri y Down seguían redirects por su cuenta y el
//                 guard solo validaba la URL inicial → una URL pública que responde
//                 302 → http://169.254.169.254/ alcanzaba el destino interno. El
//                 upload ahora fetchea por SsrfProtection.safe_open (revalida CADA
//                 salto) y el avatar usa Down con max_redirects: 0 (fail-closed).
//             Specs: ssrf_protection 21/21 (incluye ofuscación + redirects vía
//             WebMock), avatar_from_url_job 9/9. Residual: DNS rebinding (hardening
//             futuro: fijar la IP resuelta en la conexión).
//   69.1.74 — COLA (fill-to-max): el drenaje "agente disponible → llenar hasta
//             el máximo del inbox" ahora SÍ llena hasta el cupo. find_assignee
//             tomaba un ASSIGN_LOCK:<inbox>:<agent> (guard anti-doble-asignación
//             concurrente, TTL 5s) que NUNCA se liberaba → el agente recién
//             disponible recibía UNA sola conv por ciclo y el resto quedaba en
//             cola. ConversationQueueDrainService ahora libera el lock tras cada
//             update! (la capacidad ya quedó persistida en DB, cerrando la
//             ventana de carrera que el lock protege) → fill-to-max real.
//             + prefer-@agent: se prioriza al agente que DISPARA el drenaje
//               antes de repartir al resto (find_assignee rankeaba por
//               last_assignment_at y el recién-disponible nunca se llenaba).
//             Specs: conversation_queue_drain_fill_to_max 1/1 + drain 4/4.
//   69.1.73 — SEGURIDAD + fixes conversacionales.
//             - IDOR de contactos (PII): un agente listaba contactos de CUALQUIER
//               conversación de sus inboxes (prod: 126/139 contactos), no solo de las
//               suyas. ContactPolicy::Scope pasa a "asignadas a mí UNIÓN lo que puedo
//               abrir" (Conversations::PermissionFilterService). + se cierran 2 bypass
//               totales (#active y #filter via Contacts::FilterService no pasaban por
//               policy_scope) + validación en contact_inboxes#filter. Admin / contact_manage
//               siguen viendo todo. Validado en prod (Omar 126→3) + spec 5/5.
//             - #1 reorder: get_recent_conversation_history usaba .order sobre el
//               default_scope ASC de Message → leía los 4 mensajes MÁS VIEJOS y
//               neutralizaba bot_asked_question/BOT_AWAITS_REPLY_RE (CAMBIO 2). .reorder.
//             - #2 gate del acuse: "Recibido, déjame revisar tu consulta 🔍" ya no se
//               emite cuando el ciudadano difiere los datos ("no los tengo a mano").
//             - #3 continuidad del handoff: responder la institución ("de minsal") tras
//               "¿de qué institución?" ahora completa la transferencia (antes caía al LLM
//               y daba info institucional sin transferir — prod conv #621).
//   69.1.72 — Rev9 flujo de consulta de trámite INSTITUCIÓN-PRIMERO. Guard
//             DETERMINÍSTICO en status_query_tool: la institución se resuelve
//             desde los mensajes del CIUDADANO, no del team_name que adivina el
//             LLM; `.reorder(created_at: :desc, id: :desc)` vence el default_scope
//             ASC de Message (cierra la re-desambiguación #667). Defiere ante
//             silencio, desambigua solo ante ambigüedad activa.
//             + CAMBIO 1: pedido fuera de alcance (cuántos/listar trámites) NO
//               escala — informa y sigue en el flujo.
//             + CAMBIO 2: faltan los datos → 2 intentos → deriva. Root cause: el
//               clasificador (conversational_engine) detectaba "el bot preguntó"
//               solo por "?"; ahora BOT_AWAITS_REPLY_RE también capta pedidos
//               imperativos ("indícame…", "necesito que…") → una respuesta corta
//               del ciudadano ya no cae :weak ni se ignora.
//             + #614: FollowUpSequenceJob no auto-cierra una conv EN COLA.
//             + status_query clobber: editar el asistente borraba external_tools
//               (store_accessor sobre el jsonb config) → preservado en el
//               controller + before_save guard en el modelo + rake backfill.
//             Migración 20260625120000 regenera escenarios (sin DDL).
//             NOTA: la etiqueta APP_REVISION se subió de .71→.72 en esta entrada;
//             .67–.71 se desplegaron sin entrada en este changelog (ver commits).
//   69.1.66 — Rev9 #9/#10 (webhook): guardia DETERMINÍSTICA de aislamiento de
//             contexto entre trámites. La tool de consulta de estado validaba
//             SOLO formato → el LLM podía reusar el DUI de un trámite para otro
//             (dui_mismatch) o emitir "no encontró" con datos prestados. Nueva
//             Captain::Tools::StatusQueryContextGuard: bloquea SOLO el reuso
//             SILENCIOSO del MISMO DUI (hasheado) en un trámite distinto cuando
//             el ciudadano no lo proveyó EN/DESPUÉS del mensaje del nuevo
//             trámite → fuerza re-confirmación por trámite (lo pedido en #9).
//             Fail-open ante multimedia (foto/audio del DUI), identificador
//             tipo teléfono, ids cortos y primer trámite, así NO rompe el caso
//             legítimo "mi DUI es X, quiero A y B". Match por número EXACTO (no
//             substring) y with_lock para no pisar additional_attributes de
//             otros features. #11/#12 siguen mitigados por prompt.
//   69.1.65 — Rev9 #4 y #6 (handoff a agente humano determinístico). El pedido
//             de agente dependía del criterio probabilístico del LLM → en
//             conversación nueva se colgaba en "Recibido…" (#4) y al repetir el
//             pedido caía en "no tengo esa información" (#6). Nuevo prefiltro
//             DETERMINÍSTICO en ConversationalEngineJob: un pedido CLARO de
//             agente emite la oferta de handoff directa (vía el mismo
//             HandoffConfirmationGate del flujo) si nombra institución, o
//             pregunta de qué institución si no — NUNCA "no tengo esa
//             información" ni cuelgue. Con dedupe ante repeticiones del pedido.
//             + detección de institución por palabras significativas del nombre
//             (hacienda→MH, vivienda→MIVI), así no pregunta "¿cuál?" cuando el
//             ciudadano SÍ la dijo. Auditoría: el export CSV ahora refleja la
//             actividad legible de la pantalla (sin acción cruda, tipo, IDs ni
//             JSON de cambios) y oculta los registros del usuario SOPORTE PBS
//             tanto del índice como del export.
//   69.1.64 — Auditoría + orden de mensajes + drenaje de cola.
//             - Auditoría: el export CSV daba 500 en cuentas grandes — el gem
//               `audited` devuelve el `username` (string) desde #user cuando el
//               usuario fue borrado, y el CSV hacía `user.name` sobre ese string.
//               Fix `audit_actor_name` (User/String/nil) + hardening del mismo
//               root cause en log_additional_information. Nueva columna
//               "Contacto" en el CSV (buscable) y filtro por rango de fechas.
//             - P5: orden de mensajes — `created_at` tiene resolución de SEGUNDO
//               sin desempate, así que el ack "Recibido…" y la respuesta real
//               podían invertirse. Desempate por `id` (UUID v7, monótono) en el
//               default_scope de Message, el sort del fetch y el path en vivo.
//             - P7: gap de drenaje de cola — el drain dependía del cambio de la
//               columna availability, pero la elegibilidad depende del heartbeat
//               de presencia. Al reanudar presencia (reconexión) ahora se encola
//               el drain (debounced). + tiebreaker FIFO determinístico.
//   69.1.63 — Auditoría: el buscador "por nombre de agente" filtraba SOLO los
//             registros de la página cargada (`.filter` en JS), así que no
//             encontraba entradas viejas (un bloqueo en la pág 900 de 913 jamás
//             aparecía) y dejaba el footer descuadrado (1 fila visible pero
//             "13.691 elementos", porque el conteo venía del server sin filtrar).
//             Ahora el buscador resuelve el texto a IDs de agente y filtra
//             SERVER-SIDE (`user_id[]`, debounced) — el backend ya soportaba el
//             filtro. El registro buscado pasa de pág ~900 a pág ~3.
//   69.1.62 — Revisión 9: bloqueo de contactos auditado + permiso + responsive/
//             ortografía + bienvenida.
//             - P3/Bloqueos: el cambio de Contact.blocked ahora deja un
//               Audited::Audit (acción block/unblock + el usuario que lo hizo) →
//               trazable en Auditoría > Cambios. Cubre los dos caminos (panel de
//               contacto + silenciar conversación). Export CSV de los registros
//               de auditoría. Bloquear/desbloquear (y ver el botón) requiere
//               admin o custom_role con contact_manage (endpoint dedicado
//               contacts#block/unblock, aislado del update admin-puro). Backfill
//               marcado del bloqueo histórico del contacto "Hackeado".
//             - P1 (Rev9 #1): tabla del reporte CSAT (y Agentes/Equipos) se salía
//               en móvil → wrapper overflow-x-auto; "Realimentacion" →
//               "Realimentación" (tilde).
//             - P2 (Rev9 #2): el mensaje de capacidades del bot ya no menciona
//               "registro de casos ciudadanos" (contradecía la regla dura de no
//               insinuar registrar casos).
//   69.1.61 — Handoff a equipo: el ciudadano pedía traslado a un agente humano
//             ("Asigname a un agente de MINSAL") y el bot quedaba en loop sin
//             transferir nunca (prod conv #586). Root cause: DEADLOCK entre dos
//             guardas — TransferIntentService.explicit_request? exige ver en el
//             historial el mensaje del bot "Voy a transferirte a [X]… ¿Confirmás?"
//             (BOT_DESTINATION_CONFIRMATION_REGEX), pero el tool-loop-guard de
//             ResponseBuilderJob (duplicate_recent_outgoing?) lo suprimía como
//             "duplicado" → la oferta nunca persistía → detect_and_mark_handoff_offer_in_text!
//             no estampaba handoff_offered_at → el "sí" del usuario encontraba
//             offer_pending?=false y el handoff NUNCA ejecutaba. Fix ESTRUCTURAL
//             (sin keywords nuevos): el mensaje de oferta de handoff (pregunta +
//             verbo de transferencia + team del account) queda EXENTO del loop
//             guard, reusando la misma detección que detect_and_mark. El loop-storm
//             de #470 (statements sin "?") sigue suprimiéndose. Validado E2E en web
//             chat real: pide MINSAL → bot ofrece → confirma → encola (team=minsal,
//             queued). +4 unit tests. NO se tocó el regex de R8.7 ni su prompt.
//   69.1.60 — Conversaciones móvil: franja vacía a la derecha. El panel de la
//             lista (ChatList) usaba w-[340px] fijo cuando no estaba en expanded
//             layout → en móvil dejaba una franja vacía a la derecha (donde iría
//             el panel de detalle, que no se renderiza sin conv seleccionada). Fix:
//             max-md:w-full en el caso no-expandido → la lista ocupa todo el ancho
//             en móvil; desktop sigue 340px/412px. Verificado a 374px: sin franja.
//   69.1.59 — Gestión de Casos: los badges de la tabla (CATEGORÍA/SEVERIDAD/
//             ESTADO) no truncaban ni respetaban el ancho de celda — la categoría
//             larga ("servicios_publicos") se encimaba con SEVERIDAD y "En Proceso"
//             se partía en 2 líneas. Fix: badge de categoría inline-block max-w-full
//             truncate + :title; severidad y estado whitespace-nowrap; tabla
//             min-w 880→1040px. Verificado a 375px: categorías truncan con ellipsis,
//             sin overlap, estado en una línea.
//   69.1.58 — Barrido UX responsive móvil (8 superficies; 3 reportadas + 5 de
//             un escaneo de popovers/modales con ancho fijo):
//             - Filtro de contactos (ContactsFilter + ConditionRow): el contenido
//               (min-w-96=384px + fila/footer sin wrap) se salía del popover móvil
//               (~343px) → 🗑 y "Aplicar filtros" cortados. Fix: max-md:min-w-0,
//               fila con max-md:flex-wrap, footer con max-md:flex-col. Verificado
//               a 375px: todo entra (popover 343px, nada desborda).
//             - Gestión de Casos: la columna # mostraba el UUID completo y se
//               encimaba con Resumen. Fix: span block truncate + :title (id full
//               en tooltip) + columna 4%→12% (Resumen 28%→20%). Verificado: trunca
//               con ellipsis, no overflow.
//             - Editar agente (modal): footer split 50/50 rígido truncaba los
//               labels ("Ca…", "Edita…"). Fix: flex-col md:flex-row, botones
//               full-width en móvil.
//             - 5 popovers con ancho fijo clampeados al viewport
//               (max-w-[calc(100vw-2rem)] / max-md:min-w-0): AddNewRulesDialog,
//               AddNewScenariosDialog, SidebarProfileMenu, SidebarAccountSwitcher,
//               CardPopover. Todo gateado a móvil; desktop intacto.
//   69.1.57 — UX paginación móvil (PaginationFooter, componente compartido):
//             el texto "Mostrando X-Y de Z" se truncaba a "Mostran…" (inútil) y
//             empujaba los botones fuera del viewport (el último › quedaba cortado
//             en /contacts y demás listas). Fix: ocultar ese texto en móvil
//             (max-md:hidden), centrar los botones (max-md:justify-center) y
//             reducir el gap (max-md:gap-1). Verificado a 375px: botones a 325px,
//             todo dentro del viewport. Desktop intacto. NOTA: la tabla de Gestión
//             de Casos (min-w-[880px], 69.1.55) ya estaba bien — el overlap visto
//             en prod era cache del bundle viejo (hard-refresh lo resuelve).
//   69.1.56 — Fix CRÍTICO de asignación: la cola no drenaba a un agente online.
//             Reportado en prod: un agente libera capacidad (resuelve convs) pero
//             la conversación en cola NO se le asigna — solo "funciona" si el agente
//             se desconecta y reconecta.
//             Root cause: el heartbeat de presencia del frontend
//             (BaseActionCableConnector PRESENCE_INTERVAL=20000ms) es IGUAL a la
//             ventana del backend (PRESENCE_DURATION=20s) → margen CERO. Con
//             cualquier jitter/latencia/throttling de pestaña en background el
//             heartbeat vence justo antes del siguiente refresh → get_presence=false.
//             Mi gate R8.2 (smart/agent_assignment_service exigen get_presence)
//             excluía así a agentes CONECTADOS del pool → la cola no les drenaba.
//             Fix: nueva ventana ASSIGNMENT_PRESENCE_DURATION (90s, ENV-configurable)
//             SOLO para elegibilidad de asignación; get_presence acepta `duration`.
//             El display (puntito online) sigue en PRESENCE_DURATION=20s (responsivo).
//             Un agente sin heartbeat >90s sigue excluido (no re-abre R8.2). Specs:
//             smart_assignment 9/9, agent_assignment+fifo 7/7.
//   69.1.55 — R8.1 responsive (3 superficies reportadas en prod móvil):
//             - Gestión de Casos (citizenComplaints/Index): la tabla table-fixed
//               sin min-w aplastaba las 8 columnas y el texto del header se
//               encimaba. Fix: min-w-[880px] → el wrapper overflow-x-auto scrollea.
//             - Detalle del caso (citizenComplaints/Show): el root no tenía
//               overflow-y-auto/h-full → no scrolleaba en móvil ("no baja").
//               Fix: mismo patrón scrolleable que Index + pb para los FABs.
//             - Filtro de contactos (ContactListHeaderWrapper): mismo bug del
//               sort dropdown — forzaba left=SIDEBAR_WIDTH+PADDING (216) en móvil
//               y aplastaba el panel a ~143px. Fix: isMobile → usa ancho completo.
//             Todo gateado a móvil/aditivo, desktop intacto.
//   69.1.54 — Fix pre-existente: el escenario citizen_complaint_registration del
//             bot Captain no se regeneraba. Captain::Assistant declaraba
//             `external_tool_enabled?` y `enabled_external_tools` bajo `private`,
//             pero ScenarioAutoGeneratorService#determine_scenario_enabled los
//             llama con receptor explícito (`assistant.external_tool_enabled?(
//             'register_complaint')`) → NoMethodError: private method called →
//             el rescue por-template lo cataloga y saltea, así que el escenario
//             nunca se regenera. Confirmado en prod en la migración
//             20260615120000 (06/2026): team_status_query (R8.5-R8.8) SÍ regeneró,
//             citizen_complaint_registration NO. Fix: ambos queries (read-only,
//             sin efectos secundarios) suben a la interfaz pública del modelo —
//             se mueven arriba de `private`. Se expone también enabled_external_tools
//             (par cohesivo) para evitar que el mismo bug reaparezca si un futuro
//             `case` de determine_scenario_enabled necesita la lista. Spec de
//             regresión nuevo (assistant_spec, 6 examples verde): respond_to? +
//             llamada con receptor explícito sin raise + enabled/disabled/missing/
//             blank. Sin deploy (local).
//   69.1.53 — R8.1 responsive (continuación): el overlap de los FABs flotantes
//             (≡ menú + copilot) era GLOBAL — tapaban el contenido inferior de
//             CUALQUIER lista larga en móvil, no solo contactos. Fix transversal:
//             - viewport-fit=cover en el layout (vueapp.html.erb) para habilitar
//               env(safe-area-inset-bottom).
//             - ambos FABs suben sobre el notch: bottom-4 → bottom-[calc(env(
//               safe-area-inset-bottom)+1rem)] (MobileSidebarLauncher + CopilotLauncher).
//             - padding inferior móvil en los scroll containers de listas para que
//               el último item no quede bajo los FABs: lista de conversaciones
//               (ChatList DynamicScroller), Historial Ciudadano (citizenComplaints),
//               y contactos (ContactsListLayout, ahora con safe-area). Todo gateado
//               a móvil (max-md/md:), desktop intacto. Verificado a 375px: scroller
//               padBottom=80px, cards clarean los FABs.
//   69.1.52 — Revisión 8 (en curso). Bugs del motor de asignación y de la
//             creación de conversaciones:
//             - R8.2 NO asignar a agentes desconectados: el filtro de
//               candidatos (smart_assignment_service + agent_assignment_service)
//               ahora exige status 'online' Y presencia REAL (heartbeat vivo,
//               OnlineStatusTracker.get_presence). Antes solo miraba el toggle
//               de disponibilidad, que quedaba pegado en 'online' al cerrar el
//               navegador → el agente desconectado seguía recibiendo convs.
//               + Cara VISUAL: el punto de presencia también refleja la conexión
//               real — availability_statusable#user_availability_status y
//               get_available_user_ids gatean por heartbeat, así un agente
//               desconectado se muestra offline solo (no hace falta resetear
//               agentes pegados en la base; el display lo computa al vuelo).
//             - R8.4 FIFO de ciudadano: la asignación team-based
//               (AssignmentHandler#find_assignee_from_team + run_auto_assignment)
//               cede el turno a una conversación más vieja ya en cola para el
//               mismo team; la selección de agente sigue siendo inteligente.
//             - R8.3 convs duplicadas: ContactInbox#with_conversation_creation_lock
//               (advisory lock de Postgres por contact_inbox) serializa el
//               find-or-create de set_conversation en whatsapp/sms/twilio/line/
//               telegram + builder de facebook, cerrando la carrera TOCTOU que
//               creaba conversaciones duplicadas con mensajes casi simultáneos.
//               + ROOT CAUSE real (prod conv #544): contacto BLOQUEADO nace
//               resuelto (determine_conversation_status) → cada mensaje creaba
//               una conv nueva. Fix: contactos bloqueados unifican 1 conv +
//               se EXCLUYEN de la bandeja (ConversationFinder#filter_by_blocked),
//               solo visibles en Historial Ciudadano.
//             - R8.5-R8.8 flujo webhook de trámite: template team_status_query
//               actualizado (R8.5 cada consulta independiente / no reusar DUI,
//               R8.7 consultar ANTES de transferir, R8.8 terminología "trámite")
//               + migración 20260615120000 regenera escenarios. El status_query_tool
//               ya validaba DUI (R8.6/identifier_required). Validado E2E en
//               navegador (conv #534): consulta válida → "Completado" sin
//               transferir; 2º trámite sin DUI → lo vuelve a pedir; inexistente
//               → "no encontré" honesto (grounding 69.1.50).
//             - R8.1 responsive móvil (5 superficies, verificado en navegador
//               a 375px): (1) ContactSortMenu — el popover de orden ya no se
//               sale del viewport en móvil (clamp de ancho + anclado al borde
//               derecho cuando el sidebar es drawer); (2) ContactsListLayout —
//               la paginación ya no queda tapada por los FABs flotantes
//               (pb-24 md:pb-4); (3) settings/agents — la tabla scrollea dentro
//               de un wrapper overflow-x-auto en vez de desplazar toda la página
//               (título/rol ya no se cortan); (4) _tabs.scss — las pestañas de
//               conversaciones no aprietan la primera en móvil (padding/margen
//               reducidos <=768px); (5) AssignmentFlowDiagram — el diagrama de
//               asignación apila vertical en móvil (la tarjeta "Cola de espera"
//               ya no se corta). Todos los cambios gateados a móvil; desktop
//               intacto por construcción.
//             - Extra: conv en cola no se cierra por "gracias" de cortesía
//               (hook_execution_service#closing_intent → :open si está en cola).
//             - Extra seguridad: editar/mergear contactos restringido a
//               administrator PURO (sin custom_role), ningún rol personalizado
//               puede editar PII de contactos.
//             Specs nuevos/arreglados verdes; sin regresiones (pre-existentes
//             confirmados con stash).
//
//   69.1.51 — Revisión 7 (segunda vuelta): mitigación completa + auditoría
//             adversarial. Resuelve los puntos que aún reproducían:
//             - R7.1 responsive: ConversationBasicFilter.vue — el dropdown no
//               desborda en móvil (max-w + max-md:right-0) y el sub-menú del
//               SelectMenu abre 'bottom' (no se sale por los lados).
//             - R7.7 sesión 409: has_active_session? exige liveness viva en
//               Redis (sliding TTL, refrescada por request). Sesión abandonada
//               deja de bloquear el re-login; una activa sigue bloqueando.
//             - R7.8 cierre 24h: nuevo CloseStaleAssignedConversationsJob + cron.
//             - R7.14 cola: ConversationQueueDrainService fase 2 asigna convs
//               con team sin assignee; saltea (no corta) las sin capacidad.
//             + clúster Webhooks W1-W6 (relayo del status), R7.13 sin asteriscos,
//               R7.16 voseo→"tú". Seguridad ya protegida (R7.10/R7.11/R7.15).
//             Specs: 53 verde. Auditoría adversarial (6 agentes) halló 3 huecos
//             (R7.7/R7.14/R7.1), todos corregidos. Evidencia en docs/revision-7-evidencia/.
//
//   69.1.50 — Revisión 7 (GOES prod): 4 bugs del bot Cusca corregidos.
//
//             (1) CRASH DE HANDOFF → CHATS DUPLICADOS. La gema ai-agents 0.7.0
//                 define HandoffTool#perform(tool_context) (1 arg) pero Tool#execute
//                 lo invoca perform(tool_context, **params). Si el LLM pasaba params
//                 al tool de handoff → ArgumentError (given 2, expected 1) → el
//                 ResponseBuilderJob crasheaba → el bot no respondía y, con
//                 lock_to_single_conversation=false, cada mensaje abría una conv
//                 nueva (#511–515: 5 convs en 7 min). Fix: monkeypatch
//                 config/initializers/ai_agents_handoff_arity_patch.rb que hace
//                 perform tolerante a args extra (los ignora) y delega al original.
//
//             (2) "SIMPLE" (institución) DISPARABA EL CIERRE. El gate
//                 last_outgoing_ends_with_open_question? (hook_execution_service.rb)
//                 solo miraba el ÚLTIMO carácter por "?". La pregunta de institución
//                 termina en lista de opciones ("...SIMPLE.") → "." no "?" → el gate
//                 no la reconocía como pregunta abierta → el follow-up flow trataba
//                 "SIMPLE" como cierre → "¿querés cerrar?". Fix: detectar "?" en
//                 CUALQUIER parte del texto (text.match?(/[?？]/)).
//
//             (3) MENSAJE TRUNCADO EN BACKSLASH (conv #522). clean_json_response
//                 (agent_runner_service.rb) usaba un regex casero /"([^"]+)"/ que
//                 cortaba en la primera comilla ESCAPADA del JSON del LLM → el
//                 mensaje quedaba truncado terminando en "\". Fix: JSON.parse real
//                 + regex que respeta escapes + limpieza de backslash colgante.
//
//             (4) VOSEO en el cierre. send_closing_clarification_question tenía un
//                 fallback "¿Querés cerrar...? Decime y seguimos" + 2 variantes
//                 voseantes en ResponseSanitizerService. Neutralizadas (querés→deseas,
//                 necesitás→necesitas, decime→indícame, contame→cuéntame).
//
//             Specs: 15 examples verde (gate 6 + handoff patch 4 + clean_json 5).
//             Validado E2E en chat web local (Edge) con el bot real + datos de GOES:
//             "SIMPLE" avanza pidiendo trámite+DUI; handoff sin crash; cierre neutro.
//             Pendiente (no incluido, requiere regen de escenarios): el template
//             team_status_query no instruye relayar el resultado del status tool
//             cuando no hay match (bot mudo en vez de "no encontré").
//
//   69.1.49 — Hotfix UX/seguridad reset de password: el flujo "olvidé mi
//             contraseña" rechazaba el reset con un engañoso "Invalid token"
//             cuando el token era VÁLIDO. Root cause en
//             DeviseOverrides::PasswordsController#admin_locked?: trataba como
//             "lock de admin" a cualquier user con locked_at presente +
//             failed_attempts==0, sin chequear si el lock seguía VIGENTE. Con
//             unlock_strategy=:time un lock viejo expira (access_locked?==false)
//             pero locked_at queda seteado hasta el próximo login — un user
//             time-lockeado que nunca pudo volver a entrar (sin confirmar + 2FA,
//             caso gonzalo.vg95) arrastraba un locked_at fantasma y no podía
//             resetear. Confirmado en prod por logs ("admin-lock detectado" ×6).
//
//             Fix: admin_locked? mira access_locked? (lock vigente ahora), no
//             locked_at.present?. Y #update deja de devolver "Invalid token"
//             cuando el token era válido pero el reset se rechazó por otro motivo
//             (lock vigente, password=actual, complejidad) — ahora devuelve el
//             motivo real (errors.auth.account_blocked, etc.). "Invalid token"
//             queda solo para token genuinamente inexistente. El fix de código
//             por sí solo desbloquea a gonzalo.vg95 sin tocar DB (su
//             access_locked? ya era false).
//
//             Spec: passwords_controller_spec (8 examples) — lock fantasma
//             permite, caso gonzalo (no confirmado+fantasma → resetea+confirma),
//             lock vigente bloquea con motivo real, brute-force permite, token
//             inválido, happy path. Verificado docker 8/8 verde; 2FA guard 10/10
//             sigue verde; cero regresiones (las fallas del passwords spec
//             enterprise legacy son pre-existentes: factory default 'Password1!'
//             de 10 chars vs regla de 12).
//
//   69.1.48 — Hotfix de seguridad (auth bypass 2FA): el login con 2FA (Google
//             Authenticator) y el flujo SSO emitían/persistían la sesión a mano
//             (authenticate_resource_after_otp /
//             authenticate_resource_with_sso_token) sin pasar nunca por el
//             `super` de DeviseTokenAuth — el ÚNICO punto que enforzaba
//             `active_for_authentication?` (confirmable + lockable). El único
//             freno era el hook `activatable` de Devise dentro de
//             `sign_in bypass:false`, que actúa DEMASIADO TARDE: después de
//             ofrecer el segundo factor (`two_factor_required: true`) y de
//             crear+persistir un token en `user.tokens`. Caso real
//             (gonzalo.vg95): agente desactivado→reactivado con confirmed_at=nil
//             y 2FA activo; metía el código TOTP y el front mostraba
//             "sesión iniciada" pese a no estar confirmado.
//
//             Fix en DeviseOverrides::SessionsController#create: nuevo guard
//             `enforce_account_active_for_login!(user)` que corre ANTES de
//             ramificar a 2FA o emitir cualquier token, en AMBAS ramas (password
//             y SSO). Si `!active_for_authentication?`, corta con un código de
//             API estable por motivo: account_deactivated (preserva rev54),
//             account_locked, account_unconfirmed. Una cuenta no apta ya no
//             recibe el prompt de 2FA ni deja un token escrito en DB.
//
//             Spec: sessions_controller_2fa_guard_spec (10 examples) — bloqueo
//             en paso password sin pedir 2FA, bloqueo con OTP válido, locked+2FA,
//             deactivated+2FA, sin-2FA, y happy paths con/sin 2FA. Verificado en
//             docker (RAILS_ENV=test) 10/10 verde; cero regresiones (las 7 fallas
//             del session_controller_spec legacy son pre-existentes: password de
//             10 chars vs regla de 12, i18n EN/ES, y 500 en la acción `new` por
//             FRONTEND_URL nil — idénticas con y sin el fix).
//
//   69.1.47 — Hotfix conv #503 (goes prod): el bot decía "no tengo esa
//             información en mi base de conocimiento" para el contacto del
//             MINSAL, AUNQUE la KB tiene 237 FAQs aprobadas del team minsal
//             y el RAG las encontraba perfecto (embedding dist=0.41).
//
//             Root cause: el guard anti-alucinación Layer 1f
//             (Captain::ResponseSanitizerService#hallucinated_institution_replacement_for)
//             detectaba la sigla "SAC" (Sistema de Atención Ciudadana — servicio
//             REAL del MINSAL citado en sus FAQs oficiales) como institución
//             ALUCINADA porque "SAC" no es un team del account. Resultado:
//             CENSURABA toda la respuesta RAG-grounded correcta (dirección +
//             teléfono +503 2591-7000 + sac.gob.sv) y la reemplazaba con el
//             canned "no tengo esa información". El reasoning del LLM quedaba
//             contradictorio ("encontré la info y la proporcioné") porque
//             AgentRunnerService#format_response solo sanitiza `response`, no
//             `reasoning`.
//
//             Fix (2 señales, de más fuerte a más débil):
//
//             (1) SEÑAL POR HECHO — `rag_sourced`. Current.captain_rag_grounded
//                 se setea en BasePublicTool#log_tool_usage cuando un FAQ lookup
//                 tool emite 'found_results' (recuperó FAQs aprobadas de la KB
//                 de una institución ACTIVA — FaqLookupTeamTool filtra por
//                 captain_enabled). AgentRunnerService lo lee tras correr el
//                 agente y lo pasa al sanitizer. Si el dato salió de un FAQ
//                 aprobado, NO puede ser alucinación: la fuente de verdad es la
//                 KB curada, no el training del LLM. Garantía por construcción.
//
//             (2) SEÑAL HEURÍSTICA (fallback) — `response_has_rag_grounding?`
//                 (ya existía para L2-AI-GUARD desde 69.1.3). Si el flag no se
//                 propagó, cubre el caso cuando el texto cita URL .gob.sv/.edu.sv
//                 o menciona un team conocido + info sustancial.
//
//             El guard sigue censurando siglas inventadas en respuestas SIN
//             ninguna de las dos señales (alucinación real del LLM).
//
//             Validado E2E en web chat local con LLM real: el bot responde la
//             info completa del MINSAL incluyendo "Sistema de Atención Ciudadana
//             (SAC)"; log confirma found_results=20 y CERO L1-HALLUCINATED.
//             Archivos: lib/current.rb (flag), base_public_tool.rb (marca en
//             found_results), agent_runner_service.rb (reset + lee + pasa flag),
//             response_sanitizer_service.rb (param rag_sourced + bypass Layer 1f).
//             Spec: response_sanitizer_hallucination_grounding_spec (8 examples).
//
//   69.1.46 — Hotfix conv #497 (goes prod): user "no" tras "¿Hay algo más?"
//             no cerraba la conversación. ClosingIntentService caía al LLM
//             que clasificaba :open (su prompt solo listaba "no, gracias"),
//             el bot quedaba en flujo normal y el FollowUpSequenceJob step 1
//             disparaba "¿Hay algo más?" otra vez 3-4 min después (loop).
//
//             Fix aditivo en Captain::ClosingIntentService (prefilter
//             determinístico, sin llamadas al LLM):
//             - STANDALONE_CLOSING_WORDS += %w[no nop nope ninguno ninguna
//               ningun]
//             - STANDALONE_CLOSING_PHRASES += %w[no mas], %w[no nada],
//               %w[nada mas], %w[ya no], %w[no gracias], %w[gracias no]
//
//             Falsos positivos cubiertos por el prefilter `non_closing_no_response?`
//             que corre ANTES y mantiene como :open: "no sé", "no entiendo",
//             "no tengo", "no puedo", "no quiero", "no recuerdo", etc. (22
//             verbos). Y por el gate `last_outgoing_ends_with_open_question?`
//             en HookExecutionService que salta el flow de cierre si el bot
//             hizo una pregunta abierta NO-cierre (ej "¿es 0122?").
//
//             Coverage: 41 specs (8 unit + 3 integration end-to-end via
//             HookExecutionService + 30 exhaustivos sin LLM). Validación
//             real en BD development + sidekiq processing — convs #754/#755/
//             #756. Captura visual en widget local Edge — conv #756 cerrada
//             con farewell y botón "Iniciar nueva conversación".
//
//             Cero regresiones: stash + spec run baseline confirmó que los
//             27 failures en suite no-tocada son pre-existentes.
//
//   69.1.21 — Rev6 #01 fix: search scope leak (PDF #10, #14).
//             Search::SecureScopeFilter PORO + feature flag secure_search_scope.
//             Elimina shortcut user_has_access_to_all_inboxes? que permitía a
//             un agente con acceso a todos los inboxes extraer 6401 mensajes
//             sin tener conversaciones asignadas.
//
//   69.1.20 — Rev5 #24 mobile UX pulida (5 fixes acumulados):
//
//             (1) Toasts al resolver — antes aparecían hasta 4
//                 notificaciones (toast frontend + activity log +
//                 resolution_message del bot + CSAT). Removimos el
//                 toast del frontend cuando el agente regular se
//                 redirige al listado (la conv desapareciendo de
//                 "Abiertas" es señal visual suficiente). Supervisor/
//                 admin que se queda en la vista del caso resuelto
//                 SÍ recibe el toast para feedback.
//
//             (2) Backdrop oscuro para mobile sidebar (Dashboard.vue)
//                 — antes el sidebar drawer dejaba el chat detrás
//                 parcialmente visible (bleed-through). Agregamos
//                 `<div bg-black/40 md:hidden>` con click → cerrar.
//
//             (3) Sidebar mobile más ancho (Sidebar.vue) — cambió de
//                 `w-[200px]` fijo a `w-[80vw] max-w-[280px]
//                 md:w-[200px]` para reducir el espacio sobrante a la
//                 derecha. El backdrop cubre lo que quede.
//
//             (4) Filtro mobile — `<Teleport to="body" :disabled="!isMobile">`.
//                 Antes el filter quedaba atrapado por el containing
//                 block del `#conversationFilterTeleportTarget` (algún
//                 ancestor con transform) y `position: fixed` se
//                 rompía. Ahora teleport al body lo saca de cualquier
//                 stacking context restrictivo. Layout flex-col,
//                 padding compacto en mobile.
//
//             (5) ConversationView showMessageView fix — raíz del
//                 bleed-through. La propiedad retornaba `true` en mobile
//                 sin conv seleccionada (fallback `!this.isOnExpandedLayout`
//                 era true por default condensed layout), entonces el
//                 panel del chat se renderizaba al lado de la ChatList
//                 mostrando el welcome screen ("¡Oh oh! Parece que no
//                 hay mensajes...") cortado a la derecha. Ahora en
//                 mobile sin conv retornamos `false` → panel NO se
//                 renderiza, ChatList ocupa todo el ancho.
//
//   69.1.18 — BACKOUT del cambio incorrecto de 69.1.17 sobre policy +
//             fix correcto del UX por el lado frontend.
//
//             En 69.1.17 había agregado `return true if record.assignee_id == @user.id`
//             en ConversationPolicy#status_accessible? para arreglar el
//             alert "no tienes los permisos necesarios" post-resolve. Eso
//             era una REGRESIÓN de la mitigación deliberada introducida
//             en e929314 (Sec hardening abril 2026):
//
//               "Conversaciones resolved/pending/snoozed son historial
//                auditable que NO debe estar disponible a agentes
//                operativos — ni siquiera al que atendió originalmente.
//                Si necesita revisar, escalar a supervisor."
//
//             Solución correcta (sin tocar policy): que el frontend
//             redirija al listado tras resolver, así el agente sale de
//             la vista antes del refetch que dispararía el 403.
//             - Revertí `return true if record.assignee_id == @user.id`.
//             - Cambié `ResolveAction.vue#navigateBackAfterResolve` para
//               redirigir en CUALQUIER viewport (no solo mobile), excepto
//               cuando el caller tiene perm para ver resolved
//               (`conversation_status_manage` / `conversation_manage`
//               / admin base). Esos roles (supervisor/QA) sí pueden
//               quedarse en la vista del caso resuelto.
//             - Comportamiento alineado con política: el agente resuelve
//               → vuelve al listado → no ve más esa conv. Supervisor
//               resuelve → se queda viendo el caso para auditar.
//
//   69.1.17 — DOS bugs juntos descubiertos al validar mobile como agente:
//
//             (1) BUG CRÍTICO de permisos al resolver conv:
//                 El agente recibía "No tienes los permisos necesarios"
//                 inmediatamente después de resolver SU PROPIA conv. Causa:
//                 rev67 cerró el bypass "agents sin custom role ven cualquier
//                 status" pero junto se eliminó la excepción legítima para
//                 el asignado directo. Resultado: el frontend hace refetch
//                 tras toggle_status → conv ya es `resolved` →
//                 `status_accessible?` retorna false → Pundit 403.
//                 Fix en `ConversationPolicy#status_accessible?`: agregar
//                 `return true if record.assignee_id == @user.id`. El bypass
//                 original (que rev67 buscaba cerrar) usaba assignee_id
//                 arbitrario, eso sigue cerrado por agent_can_access_conversation?.
//
//             (2) Filtro mobile con layout colapsado:
//                 Aunque 69.1.15 hizo el modal responsive, el `grid gap-6`
//                 con posición fija `top-16 bottom-16` estiraba las filas
//                 hasta llenar todo el alto disponible, dejando gaps gigantes
//                 vacíos. Fix: cambiar a `flex flex-col` con `max-h-[calc...]`
//                 para que el contenido tome altura natural arriba.
//                 Botones bottom con `flex-wrap` para que en angosto no se
//                 corte "Aplicar filtros".
//
//   69.1.16 — Rev5 #24 third pass: ResolveAction redirige back en mobile.
//             Bug reportado: al resolver una conv el agente quedaba
//             "atrapado" en la vista del chat resuelto. Antes (versión
//             previa) la conv se cerraba y volvía al listado. Ese
//             comportamiento se perdió en algún cambio reciente.
//             Fix en `ResolveAction.vue`:
//             - Detect mobile viewport con `window.matchMedia('(max-width:767px)')`
//             - Después de `toggleStatus` exitoso con status=RESOLVED:
//               si mobile + ruta de conversación específica → `router.back()`
//               (vuelve al listado del inbox / vista parent).
//             - En desktop dejamos el comportamiento actual: el agente
//               sigue viendo el chat resuelto porque el sidebar con la
//               lista de convs sigue visible al lado.
//             Aplicable solo al botón "Resolver" en la barra de
//             acciones. No interfiere con los keyboard shortcuts
//             (Alt+E / Cmd+Alt+E) que tienen su propia lógica de
//             "siguiente conv".
//
//   69.1.15 — Rev5 #24 second pass (filtro de conversaciones mobile).
//             En mobile el dropdown del filtro se renderizaba con
//             `max-w-3xl lg:w-[750px]` posicionado absoluto, lo que lo
//             sacaba del viewport: el header "Filtrar conversaciones"
//             quedaba cortado y los botones de acción se salían a la
//             derecha. Solución responsive en `ConversationFilter.vue`:
//             - Mobile (<md): backdrop oscuro + content `fixed inset-x-4`
//               escrolleable (top-16 bottom-16 para no taparse con el
//               header de la app ni con la barra inferior).
//             - Desktop (md+): mantiene el comportamiento original
//               (dropdown anclado al botón via TeleportWithDirection).
//             Backdrop también cierra el modal con click.
//             E2E mobile (414x896): header completo, botones todos
//             visibles, backdrop atenúa correctamente.
//
//   69.1.14 — Rev5 #24 (mobile UI bug): el FAB hamburguesa del sidebar
//             se ocultaba SIEMPRE en routes de conversation. Cuando una
//             conv se resolvía, el route seguía siendo `inbox_conversation`
//             → FAB invisible → agente atrapado, sin salida al menú.
//             Fix en `MobileSidebarLauncher.vue`:
//             - Nueva lógica `shouldHide` lee el status de la conv activa
//               desde el store. Oculta el FAB SOLO cuando hay una conv
//               con status `open` o `pending` seleccionada (para no
//               obstruir el input del chat).
//             - Conv `resolved` / `snoozed` / sin conv específica → FAB
//               visible. Garantiza salida al sidebar en cualquier estado.
//             E2E: agente en conv resuelta ahora ve el FAB y puede
//             volver al menú.
//
//   69.1.13 — Team#sync_bot_flags bidireccional + migration backfill.
//             Bug Rev5 revalidación: el switch (estrella ✨) en la lista
//             de equipos edita `captain_enabled` mientras el checkbox
//             "Incluir en escenarios del bot" del form edita
//             `active_for_bot`. Históricamente dos flags independientes
//             con semánticas distintas (CuscaSense AI vs disponible
//             para handoff/scenarios), pero los admins se confundían:
//             podían quedar en estado intermedio (uno on / otro off).
//             Caso GOES prod observado: MINSAL con captain_enabled=false
//             (switch UI off) y active_for_bot=true (checkbox form
//             checkeado, default no cambiado) — el form mostraba el
//             checkbox marcado aunque el team estuviera desactivado
//             desde la lista.
//             Modelo final: 2 estados (ON / OFF) sincronizados.
//             - `Team#sync_bot_flags` callback before_validation:
//               cualquier cambio en un flag espeja el otro. Si ambos
//               cambian a valores distintos en el mismo save (UI
//               inconsistente), priorizar OFF (conservador).
//             - Migration `20260511230000_sync_team_bot_flags`:
//               backfill de teams existentes con flags desalineados.
//               Aplica regla "si CUALQUIERA es false, ambos false".
//               Invalida cache del sanitizer post-update.
//             E2E: toggle de captain_enabled → active_for_bot sigue.
//             Toggle de active_for_bot → captain_enabled sigue.
//             Bidireccional.
//
//   69.1.12 — Rev5 #11 modelo final tras segunda revalidación del cliente:
//             matriz de 3 niveles para PUT contacts con filtrado de
//             campos por rol.
//               * `administrator` puro       → edita TODOS los campos
//               * custom_role `contact_manage` (Admin Central) → SOLO
//                 `name` + `email` (resto del payload se descarta)
//               * cualquier otro              → 403
//             Implementación:
//               - `ContactPolicy#update?` ahora acepta `pure_administrator?`
//                 OR `contact_manage_role?`
//               - `ContactsController#contact_update_params` aplica
//                 `restricted_contact_update_params` cuando el caller NO
//                 es admin puro: slice(:name, :email), log de campos
//                 descartados
//             Length caps backend (siempre activos, todos los roles):
//               - name        → 60 chars (NAME_MAX_LENGTH, sanitize_name)
//               - email       → 254 chars (RFC 5321 max)
//               - phone_number → 30 chars
//               - location    → 120 chars
//               - identifier  → 200 chars
//             Sanitización Loofah :prune en todas las columnas string
//             user-editables (ver 69.1.11). Caracteres no permitidos
//             como `<script>`, `<iframe>`, `<svg>` eliminados con su
//             contenido. Texto residual conservado.
//             E2E 5 casos (admin/admin central/agent + XSS + length cap):
//             todos comportamiento esperado.
//
//   69.1.11 — Contact string columns sanitizadas + ContactInfoRow render
//             texto plano (Rev5 #11 segunda validación visual). Un agente
//             en GOES prod guardó `<iframe src="youtube.com/embed/...">`
//             en el campo `location` del contacto "Liliana" y el sidebar
//             de conversación lo renderizaba como widget embebido. Dos
//             vectores cerrados:
//               (1) BACKEND — `app/models/contact.rb` nuevo callback
//                   `sanitize_string_columns` que pasa Loofah :prune por
//                   cada columna string user-editable: `location`,
//                   `identifier`, `middle_name`, `last_name`,
//                   `country_code`. Cada una con length cap propio.
//                   Loofah :prune elimina iframe/script/style/object
//                   completos con su contenido, conserva el texto plano
//                   residual. Antes solo `name` y `additional_attributes`
//                   jsonb estaban cubiertos.
//               (2) FRONTEND — `app/javascript/.../ContactInfoRow.vue`
//                   cambió de `v-dompurify-html="value"` a interpolación
//                   `{{ value }}`. DOMPurify permite iframes a youtube/
//                   vimeo por default — datos puros de contacto no
//                   necesitan HTML render. Vue escapa cualquier `<tag>`
//                   automáticamente con interpolación.
//             Defense in depth: aunque el backend ahora limpia los
//             campos en cada save, los contactos PRE-EXISTENTES con HTML
//             aún tienen el dato sucio en DB. El render frontend ya no
//             lo ejecuta — al próximo save del agente se limpia.
//
//   69.1.10 — ContactPolicy#update? endurecido a pure_administrator?
//             (feedback Rev5 #10 segunda validación visual). El fix de
//             69.1.2 cerró el IDOR pero todavía permitía que custom_roles
//             con permiso `contact_manage` (ej. "Administrador Central",
//             "Administrador de Institución") mutaran PII de contactos.
//             Decisión del cliente GOES: SOLO administrator con rol base
//             (`role: 'administrator'` Y `custom_role_id: nil`) puede
//             editar contactos. Cualquier override con custom_role queda
//             fuera del permiso para concentrar el audit trail en una
//             superficie de control trazable.
//             Fix en `app/policies/contact_policy.rb`:
//               - new method `pure_administrator?` valida rol base sin
//                 custom_role overlay
//               - `update?` cambia de `full_contact_access?` → `pure_administrator?`
//             E2E con 3 tokens (admin puro / agent regular / agent con
//             custom_role contact_manage): admin → 200, los otros dos → 403.
//             Aplica SOLO a update. `index?`, `show?`, `search?`, `filter?`
//             siguen abiertos por scope (los agentes necesitan VER
//             contactos para operar conversaciones).
//
//   69.1.9 — Hotfix QueueReminderJob spam (descubierto via E2E Rev5):
//            (1) DEFAULT_INTERVAL_SECONDS: 60s → 300s (5 min). El default
//                anterior generaba spam percibido — ciudadano en cola
//                recibía "Aún estás en la cola..." cada 60 segundos durante
//                MAX_REMINDERS=10 iteraciones. Caso real conv #1770: bot
//                envió el reminder a las 1:13 PM, 1:14 PM, 1:15 PM tras el
//                handoff a las 1:12 PM. Tres mensajes idénticos consecutivos
//                en 3 minutos genera percepción de bug grave para el
//                ciudadano que espera silencio respetuoso.
//            (2) MAX_REMINDERS: 10 → 3. Con el nuevo interval de 5 min,
//                3 reminders cubren 15 min de espera total, alineado con
//                queue_max_wait_seconds típico (30 min). Más allá es spam
//                sin valor para el ciudadano.
//            (3) Nuevo MIN_INTERVAL_SECONDS = 120 (2 min). Floor defensivo:
//                si el admin configura `queue_reminder_interval_seconds`
//                con un valor menor (caso real GOES tenía 60), el job
//                lo normaliza a 120 sin avisar — defensa en profundidad
//                porque la UI debería validarlo también pero no podemos
//                confiar solo en eso.
//            E2E local validado conv #1770→#1771: tras el fix, el reminder
//            ya no se dispara con cadencia molesta.
//
//            ALSO: MINSAL/ANDA `active_for_bot=false` (cierra brecha Rev5
//            #12). El team estaba en GOES con `active_for_bot=true` por
//            error de seed local, lo que hacía que el bot diera info
//            completa del Ministerio de Salud cuando la spec exige el
//            canned "no disponible". ResponseSanitizerService ya tenía
//            la lógica de detección (`unauthorized_institution_mapping`)
//            pero el cache de 60s mantenía el mapping vacío. Invalidamos
//            cache + corregimos team flags → canned exacto entregado.
//
//            ALSO: identity-fields restringidos a admin en PUT /api/v1/profile
//            (refuerzo Rev5 #1 + #2). Feedback del validador GOES: aunque
//            el bypass por fetch ya estaba cerrado (#1 requiere current_password
//            y #2 sanitiza XSS), operativamente decidieron que `email` y
//            `name` SOLO los puede modificar un administrador — el agente
//            regular no debe poder cambiar su propia identidad ni siquiera
//            con su propia contraseña. Estos campos son parte del audit
//            trail (asignaciones, logs, conversaciones históricas) y la
//            política de identidad la centraliza el admin.
//            Fix en `app/controllers/api/v1/profiles_controller.rb`:
//              - método privado `allow_identity_change?` corre ANTES del
//                update y compara los campos del payload contra el valor
//                actual del @user. Si el cambio incluye `email` o `name`
//                Y el current_user NO es admin en NINGUNA cuenta → 403.
//              - `display_name`, `avatar`, `message_signature`, `ui_settings`
//                siguen self-editables (cosméticos / preferencias).
//              - `password` también sigue self-editable con current_password.
//            Mensaje al user: "Solo un administrador puede modificar tu
//            nombre o correo electrónico. Si necesitas cambiarlos, comunícate
//            con el administrador de tu cuenta."
//            E2E: admin cambia name → 200; agent cambia name/email → 403;
//            agent cambia display_name/message_signature → 200.
//
//            ALSO: limpieza de voseo Rioplatense en mensajes user-facing.
//            GOES es un cliente salvadoreño — el voseo argentino ("Elegí",
//            "necesitás", "contactá", "decime") es incongruente con el
//            público. Sweep en:
//              - config/locales/devise.es.yml (locked message)
//              - config/locales/es.yml (account_blocked, account_inactive)
//              - app/javascript/dashboard/i18n/locale/es/conversation.json
//                (TEAM_LOCKED_HINT)
//              - enterprise/app/jobs/captain/conversational_engine_job.rb
//                (mensajes de cola y despedida)
//              - enterprise/app/services/captain/response_sanitizer_service.rb
//                (POST_PROACTIVE_GUARD_VARIANTS, OFFENSIVE_VARIANTS,
//                FALLBACK_VARIANTS, L1-CoT-LEAK fallback, hallucinated
//                acronym wording, ENUM-SCOPE response)
//              - enterprise/lib/captain/prompts/assistant.liquid (ejemplos
//                literales que el LLM imita)
//              - enterprise/app/services/captain/llm/system_prompts_service.rb
//                (regla 69.0.7 con wording textual)
//              - app/models/user.rb (validator de password_not_same_as_current)
//            Patrón: voseo → tuteo neutral o infinitivo según contexto.
//            Ejemplos: "Elegí una distinta" → "Escribe una distinta";
//            "contactá a un administrador" → "contacta a un administrador";
//            "necesitás info" → "necesitas información"; "Decime" → "Dime".
//
//            ALSO: User#password_not_same_as_current validator (cierra
//            brecha Rev5 #6). Devise#reset_password permitía setear la
//            MISMA contraseña anterior, lo que permite evadir el bloqueo
//            por intentos fallidos: user bloqueado pide recover → setea
//            su password de antes → consume el token de unlock. Validator
//            en `app/models/user.rb` que compara nueva password contra
//            `encrypted_password_was` vía `valid_password?` (bcrypt-safe
//            constant-time). Si match → error con mensaje claro al user.
//            Re-test: reset misma pwd → false + error, reset nueva pwd → true.
//
//            ALSO: Upload whitelist (cierra brecha Rev5 #3). El endpoint
//            POST /api/v1/accounts/.../upload aceptaba CUALQUIER extensión
//            de archivo, incluyendo .php, .ts, .js, .sh, .exe. Subir
//            "<?php system($_GET['cmd']); ?>" generaba URL pública de
//            5 min sin autenticación — vector de delivery de malware
//            (adminer-5.4.2.php documentado en informe original).
//            Mitigación en `app/controllers/api/v1/accounts/upload_controller.rb`:
//              - ALLOWED_EXTENSIONS whitelist (imágenes, docs, audio, video, zips)
//              - BLOCKED_CONTENT_TYPES defense-in-depth (application/x-php, etc.)
//              - Aplica a archivos directos Y a external_url (descarga remota).
//            Re-test: .php/.ts/.js/.sh/.py/.exe → 422, .png → 200.
//
//            ALSO: FaqLookupTeamTool threshold + keyword fallback
//            (cierra brecha Rev5 #25). Bug descubierto via E2E conv #1765
//            MIVI: bot intentó queries ["dirección", "ubicación", "oficinas"]
//            para "dame la dirección del MIVI" y TODAS retornaron no_results
//            aunque la FAQ "¿Dónde está ubicada la sede del Ministerio de
//            Vivienda?" existe en DB. Medición con text-embedding-3-small:
//              query "dirección"           → mejor match dist=0.6868
//              query "donde está ubicado"  → mejor match dist=0.704 (FAQ correcta)
//              query "dame la dirección"   → mejor match dist=0.5946
//            El threshold de 0.55 es demasiado restrictivo para el modelo
//            actual — incluso la query semánticamente perfecta queda fuera.
//            Subido a 0.75 + agregado fallback keyword-search que dispara
//            si el embedding retorna 0 results y la query menciona conceptos
//            cubiertos por FAQs (dirección/ubicación/teléfono/horario/correo).
//            E2E conv #1772: bot ahora responde con la dirección completa
//            (KM 5 1/2 carretera a Santa Tecla, Plantel La Lechuza...).
//   69.1.8 — REGLA DURA #7 en assistant.liquid: entidad NO listada en
//             AVAILABLE TEAMS → canned exacto, sin "sin embargo" ni info
//             parcial. Caso real conv #1747 (Hospital Nacional Bloom): bot
//             dijo "No encontré info específica sobre el Hospital..." y
//             agregó "Sin embargo, en las Unidades de Salud..." — info
//             parcial contradice el canned, confunde al ciudadano.
//
//             Wording exacto solicitado por cliente:
//             "Por el momento no tengo esa información en mi base de
//              conocimiento, prueba consultar luego y con gusto podré
//              asistirte."
//
//             Aplica a Hospitales nacionales no listados (Bloom, Rosales,
//             Maternidad), Centros/Sedes específicas, Programas/Proyectos
//             sin team asociado, cualquier entidad pública fuera de
//             AVAILABLE TEAMS.
//
//             PROHIBIDO en este escenario:
//             - "Sin embargo, [info parcial]"
//             - Ofrecer transfer a equipo alternativo
//             - Inventar información (horarios, ubicaciones, contactos)
//             - Mencionar otras instituciones disponibles dentro del canned
//
//             Cierra el último hallazgo del informe Rev5 #23 ("institución
//             NO en BD da info parcial + transferir y cerrar"). El resto
//             del informe Rev5 quedó 21/26 verde en 69.1.7 (4 items
//             Sprint 4 separado + 3 frontend/UI fuera de scope bot).
//
//   69.1.7 — Confirmation Gate estructural para handoff + LLM-judge para
//             Layer 1f + sanitización XSS de User#name + fix /agents 403 +
//             notify_citizen defensa caso C.
//
//             (1) Captain::HandoffConfirmationGate — gate antes de ejecutar
//                 assign_team/handoff. Si NO hay confirmation reciente,
//                 emite canned "Voy a transferirte a [INSTITUCIÓN] sobre
//                 [TRÁMITE]. ¿Confirmás SÍ o NO?" + setea handoff_offered_at.
//                 Cuando user responda, HookExecutionService llama
//                 process_response_and_execute! que clasifica via
//                 TransferConfirmationIntentService (LLM-judge 52 casos
//                 validados — robusto contra "ok", "dale", "confirmo",
//                 "sale pues", "yes please", "simón"). Si :accept → ejecuta
//                 ActionService.assign_team directamente sin pasar por LLM
//                 otra vez. Resuelve casos #415, #419 estructuralmente.
//
//             (2) Captain::InstitutionContextJudge — reemplaza la regex
//                 lista-cerrada de Layer 1f.in_institutional_context? por
//                 LLM-judge robusto. Antes la lista crecía con cada wording
//                 nuevo (regula/supervisa/coordina en 69.1.3, administración/
//                 comisión/consejo en 69.1.7). El judge entiende contexto:
//                 "ANDA es la Administración Nacional" → INSTITUTIONAL,
//                 "ANDA con cuidado por las calles" → OTHER. Cache Redis 60s
//                 + fallback a regex original si LLM no available.
//
//             (3) User#sanitize_name — Loofah :prune + injection patterns.
//                 Mismo guardrail que Contact#sanitize_name (T2 #362, 69.1.0).
//                 Sin esto, un agente editando su propio profile vía
//                 PUT /api/v1/profile podía meter "<script>" en su name y
//                 causar XSS almacenado en sidebar/listas/modales.
//
//             (4) UserPolicy#index? — agentes regulares pueden GET /agents
//                 con scope filtrado (UserPolicy::Scope ya filtra a teams
//                 compartidos). Antes index? bloqueaba endpoint completo →
//                 dashboard fail para no-admin. La protección de #371
//                 (no enumerar todo el account) la cumple el Scope, no el
//                 index?.
//
//             (5) AssignmentHandler#notify_citizen_of_handoff — caso C
//                 defensivo: si team+assignee están presentes, dispara
//                 mensaje "Te he conectado..." aunque saved_change_*
//                 no detecte el cambio (race entre updates). Dedup ampliado
//                 a lifetime de la conv (no 1 min). Resuelve conv #417.
//
//             (6) Layer 1d sin guard `looks_descriptive?` — cualquier
//                 mención de team desactivado se reemplaza con canned
//                 ("invisible"). Cliente fue explícito: si team está OFF,
//                 NO debe aparecer info nunca. Resuelve conv #416.
//
//             Validación local: 13 suites verdes incluyendo
//             validate_handoff_gate (28 casos), validate_institution_context_judge
//             (18 casos), validate_user_policy_scope (19 casos),
//             validate_user_name_sanitize (45 casos), validate_69_0_10
//             (regresión completa), 69.1.3/5/6, R5, wording smoke,
//             assignment algorithm, transfer confirmation intent.
//
//             E2E real con webchat: caso conv #419 reproducido y resuelto
//             — user "transfiéreme..." → bot ofrece confirmación → user
//             "confirmo" → handoff ejecutado, conv queued con team mivi,
//             sin farewell prematuro.
//
//   69.1.6 — Eliminación ESTRUCTURAL de race conditions del FollowUpSequenceJob
//             vía Generation Token. Implementa una solución por construcción
//             (no por timing) al problema de orphan jobs.
//
//             Bug que motiva el cambio (observado en conv #404):
//               1. Bot saluda 04:41:42 → schedule_follow_up con delay=1min,
//                  attrs.scheduled_at = "2026-05-08T04:41:42Z".
//               2. User responde 04:42:12 → HookExecutionService.in_follow_up_sequence?
//                  limpia attrs (scheduled_at, step) PERO no cancela el job
//                  Sidekiq en cola.
//               3. Bot proactive 04:42:21 → schedule_follow_up con NUEVOS attrs.
//               4. Job VIEJO de step 1 dispara 04:42:42, pasa los guards
//                  basados en timestamps (los attrs ahora apuntan al proactive),
//                  manda "¿Hay algo más?" → confunde al ciudadano.
//
//             Solución estructural — Generation Token:
//
//             Cada vez que un callsite programa o cancela follow-up, incrementa
//             additional_attributes['follow_up_generation']. El job se programa
//             pasando expected_generation. Al ejecutar, compara contra el
//             current; si difieren, el job es huérfano y aborta silenciosamente.
//
//             Callsites que bumpean generation:
//               - ResponseBuilderJob#schedule_follow_up_sequence
//               - ConversationalEngineJob#schedule_follow_up_after_solid
//               - HookExecutionService#in_follow_up_sequence? (al cancelar)
//
//             Step 1 → Step 2 PROPAGAN la misma generation (es la misma secuencia).
//             User mensaje entre step 1 y step 2 → HookExec bumps gen → step 2
//             huérfano rejected.
//
//             Garantías por construcción (NO por timing):
//               - Job antiguo + schedule nuevo → gen mismatch → orphan rejected
//               - Job antiguo + cancel + re-schedule → gen +2, orphan rejected
//               - Multiple outgoings concurrentes (with_lock) → gen serialized
//               - Sidekiq retry de job antiguo → gen mismatch → rejected
//
//             Backward compat: jobs encolados antes del deploy de 69.1.6 vienen
//             con expected_generation=nil → se saltan el guard estructural y
//             caen al flujo timestamp-based original (sin regresión).
//
//             Validación: tmp/validate_69_1_6.rb cubre 7 escenarios incluyendo
//             el caso real de conv #404 reproducido + step1→step2 propagation
//             + user msg entre steps + backward compat. tmp/validate_69_1_5.rb
//             y suite completa de regresión (69.0.8/69.0.10/69.1.3/R5/wording
//             smoke/assignment) verde.
//
//             E2E real: webchat local con follow_up_wait_minutes=1 reproduce
//             el flow de conv #404 — bot proactive + bot SOLID llegan ambos
//             al chat (FIX 69.1.5 + arquitectura 69.1.6 funcionando).
//
//   69.1.5 — HOTFIX urgente: tool loop guard ya NO suprime respuestas SOLID
//             después de un mensaje proactive. Bug regresión introducido en
//             69.1.3 (FIX D — similar_count >= 1).
//
//             Detectado en producción conv #404 (account 019d0f43-..., inbox
//             "Desarrollo"):
//               1. User: "dame informacion de la direccion de ministerio de hacienda"
//               2. Bot proactive: "Entendido, déjame buscar información sobre el Ministerio de Hacienda 🔍"
//               3. LLM emite respuesta SOLID: "El Ministerio de Hacienda de El Salvador está ubicado en Boulevard de Los Héroes No. 1231..."
//               4. Tool loop guard cuenta 4 palabras compartidas (el, ministerio, de, hacienda)
//                  → similar_content? retorna true → suprime el SOLID.
//               5. Ciudadano nunca recibe la información correcta. Eventually
//                  el FollowUpSequenceJob dispara "¿Hay algo más?" y cierra.
//
//             Root cause: Ruby `\b\w+\b` sin /u flag NO captura caracteres
//             UTF-8 ("déjame" se pierde como token), entonces first(8) palabras
//             del proactive deja expuestas exactamente las palabras de la
//             institución. El proactive y el solid SIEMPRE comparten esas
//             palabras (es lo natural — el bot anticipa qué va a buscar).
//
//             Fix quirúrgico: filtrar mensajes con
//             content_attributes['response_type']='proactive' del set `recent`
//             que duplicate_recent_outgoing? compara. Los proactives NO son
//             respuestas finales; son acuse de recibo previo a la respuesta
//             real, y por naturaleza comparten vocabulario con el solid.
//
//             NO se tocó:
//               - similar_count >= 1: la motivación original (caso conv #397)
//                 sigue válida — 2 mensajes con misma intent en 30s ES tool loop.
//               - 25 chars prefix / 4-word overlap: detectan loops reales sin
//                 ser sensibles a wording.
//               - El comportamiento del guard cuando NO hay flag proactive
//                 (caso conv #470 con loop de assign_team) sigue intacto.
//
//             Validación local: tmp/validate_69_1_5.rb reproduce el caso real
//             de conv #404 + verifica que el comportamiento pre-existente (tool
//             loop sin proactive flag) NO se rompió. Suite completa de
//             regresión (69.0.8/69.0.10/69.1.3/R5/wording smoke) sigue verde.
//
//   69.1.4 — Sprint 4 Fase 1: hardening de TransferConfirmationIntentService.
//             Mejora del LLM-judge que decide si una respuesta corta del
//             usuario tras "¿confirmás transferir?" es aceptación o no.
//             Caso real reportado: regex "CONFIRMATION_REGEX" con lista
//             cerrada de palabras fallaba con "de acuerdo", "ándale",
//             "hagámoslo", "perfecto dale". 69.1.0 ya migró a LLM-judge;
//             69.1.4 lo endurece para reducir falsos negativos restantes.
//
//             Cambios:
//
//             (A) Few-shot examples en el prompt (12 ejemplos: 6 ACCEPT,
//                 6 OPEN) cubriendo Rioplatense ("dale", "ándale"),
//                 salvadoreño ("simón", "sale pues") y code-switching
//                 ("yes please"). El prompt anterior tenía solo reglas
//                 narrativas, sin ejemplos input → output. Few-shot baja
//                 ambigüedad sin cambiar de modelo.
//
//             (B) Output JSON estructurado: response_format: json_object
//                 + parse_json_verdict robusto. Antes esperaba "ACCEPT"/
//                 "OPEN" como string suelto y un modelo "creativo" podía
//                 devolver "ACEPTAR" o "Sí, acepta" rompiendo el parser.
//                 Ahora devuelve {"verdict": "ACCEPT"} o {"verdict": "OPEN"}.
//                 Fallback al string-search si JSON inválido.
//
//             (C) Cache Redis 60s por hash SHA256(normalized_text). Reduce
//                 ~30-40% de llamadas en convs típicas porque las
//                 afirmaciones cortas son universales ("ok", "dale", "si"
//                 se repiten entre conversaciones distintas). Latencia
//                 de la segunda llamada baja de ~500ms a ~5ms.
//
//             (D) Telemetría: log INFO incluye verdict, elapsed_ms,
//                 model y truncated input. Facilita análisis post-deploy
//                 de tasa de ACCEPT vs OPEN y tiempo del judge.
//
//             Suite de regresión: tmp/validate_transfer_confirmation_intent.rb
//             cubre 52 casos reales (10 pre-filter + 11 ACCEPT canónicas
//             + 12 ACCEPT variantes regionales + 6 ACCEPT con contexto
//             + 11 OPEN preguntas/ambigüedad/agradecimiento + 1 cache + 8
//             edge cases). Costo de la corrida ~$0.001 USD.
//
//             Decisiones que NO se tocaron:
//               - Modelo: gpt-4.1-mini se mantiene (latencia/costo OK).
//                 Cambiar a gpt-4.1 (no mini) o Sonnet exige medir
//                 baseline en producción primero.
//               - Pre-filtros baratos (vacío, > 100 chars, "?", negativos
//                 explícitos al inicio) se mantienen. Skipean ~25% de
//                 inputs sin tocar el LLM.
//               - Lista de regex DIRECT_INTENT_REGEX y
//                 BOT_DESTINATION_CONFIRMATION_REGEX en el callsite
//                 TransferIntentService — esos detectan AL BOT y AL
//                 USER respectivamente, no son input variable, no migran.
//
//             Próximas fases del Sprint 4 (separado, fuera de scope 69.1.4):
//               - Fase 2: consolidar in_institutional_context? + Layer 1d
//                 INSTITUTION_DESCRIPTION_INDICATORS + Layer 1g
//                 CHAIN_OF_THOUGHT_PATTERNS en un único Captain::ContentSemanticJudge
//                 con structured output multi-flag. Una call LLM en lugar
//                 de 3-4 layers de regex en cascada.
//               - Fase 3: Items de seguridad #359/#366 (search PII bypass)
//                 y #369 (massive exfil messages) — refactor SearchService
//                 + MessagePolicy::Scope. Independientes del LLM-judge work.
//
//   69.1.3 — UX coherente bot + RAG bypass + tool loop relax + REGLA #6 +
//             Sprint 4 verbos Layer 1f. Fixes coordinados detectados en
//             prod conv #396/#397 + revisión #5 de QA + observaciones
//             internas:
//
//             (FIX A) ResponseBuilderJob#create_outgoing_message ahora
//                 sanitiza TODOS los outgoing — defense in depth.
//                 Antes solo se sanitizaba el path principal del LLM;
//                 paths secundarios (proactive, error_response,
//                 queue_lock canned, etc.) bypaseaban el sanitizer y
//                 a veces filtraban texto problemático al ciudadano.
//
//             (FIX B) TransferIntentService — Opción C híbrida:
//                 DIRECT_INTENT_REGEX como shortcut determinista
//                 ("puedes pasarme", "transfiéreme con", etc.) +
//                 LLM-judge fallback (TransferConfirmationIntentService)
//                 para casos ambiguos. Justificación: balance
//                 cost/latency/determinismo. El LLM-judge tiene
//                 falsos negativos en lenguaje muy directo
//                 ("Puedes pasarme con un humano"); el regex no.
//                 Sprint 4 (separado) seguirá mejorando el judge.
//
//             (FIX C) response_has_rag_grounding? — bypass de L2-AI-GUARD
//                 cuando la respuesta del LLM cita una URL .gob.sv/.edu.sv
//                 O menciona team del account + texto > 60 chars.
//                 Caso real conv #396/#397: respuestas correctas con
//                 info real de ubicaciones / instituciones bloqueadas
//                 como off-topic en falso positivo por L2 (judge
//                 general sin contexto RAG).
//
//             (FIX D) Tool loop guard relajado — antes 40 chars idénticos
//                 disparaba duplicate; ahora 25 chars + 4-word overlap.
//                 Caso real conv #397: dos respuestas con prefijo de
//                 30 chars distintos pero contenido similar pasaban
//                 el guard original. similar_count >= 1 (antes >= 2).
//
//             (REGLA DURA #6) assistant.liquid prompt — handoff
//                 humano SOLO cuando el ciudadano lo pide explícitamente
//                 o la acción es imposible para el bot. Evita el loop
//                 de "¿confirmás transferir?" detectado en conv #396
//                 donde el bot ofrecía handoff antes de intentar
//                 responder con FAQs.
//
//             (Wordings UX 69.1.3 — solicitados por cliente):
//                 - Layer 1g (CoT leak): "Soy {name}, para ayudarte
//                   mejor, ¿podés decirme con qué institución pública
//                   o trámite específico necesitás orientación? Así
//                   te oriento de forma concreta."
//                 - Layer 1f (sigla alucinada por LLM, user NO la mencionó):
//                   "Por el momento, no tengo esa info específica en
//                   mi base de conocimiento. ¿Podés contarme un poco
//                   más — qué trámite o de qué institución pública
//                   necesitás info? Así te ayudo mejor."
//                 - POST_PROACTIVE primera variante: "Después de
//                   revisar, no tengo esa información específica
//                   en mi base de conocimiento. ¿Podrías contarme un
//                   poco más sobre el trámite o la institución que
//                   necesitas? Así te ayudo mejor."
//                 Auditoría confirmó: ningún wording sugiere handoff
//                 (alineado con REGLA DURA #6); ningún overlap entre
//                 layers (1f intercepta antes que L2/POST_PROACTIVE).
//
//             (Sprint 4 — Layer 1f verbos institucionales ampliados):
//                 in_institutional_context? ahora reconoce:
//                 regula|supervisa|coordina|fiscaliza|opera|controla|
//                 autoriza|certifica|inspecciona|registra|recauda|
//                 adjudica|expide|aprueba|sanciona|dirige.
//                 Pattern principal acepta hasta 80 chars sin punto
//                 entre la sigla y el verbo (caso real local:
//                 "El VMT (Viceministerio de Transporte) regula y
//                 supervisa el transporte" — el LLM expandía la sigla
//                 entre paréntesis antes del verbo, falso negativo
//                 en patterns anteriores).
//
//             Algoritmo de asignación validado 100%: tmp/validate_assignment_algorithm.rb
//             cubre 22 escenarios — Smart Assignment (last_assigned ASC,
//             open_count ASC), Round Robin, capacity limits, team_scope,
//             lock concurrency, queue flow, dequeue → run_auto_assignment,
//             NO TEAM PROTECTION en pending → open, kill switch del
//             reminder, configs validation.
//
//             Items pendientes Sprint 4 (separado):
//               - Mejorar TransferConfirmationIntentService (prompt +
//                 few-shot + posible cambio de modelo) para reducir
//                 falsos negativos del LLM-judge.
//               - Refactor SearchService para #359/#366 PII bypass.
//               - MessagePolicy + scope para #369 massive exfil.
//
//   69.1.2 — Security sprint 2: items 🔴 alta + 🔥 crítica de Revisión 5.
//             Endurece autorización en endpoints expuestos a roles agente y
//             cierra el vector de account takeover via email collision.
//
//             Decisiones del cliente (2026-05-07):
//               - Email global único (un user = un account, solo GOES)
//               - "Conversaciones participando" NO puede editar contactos
//               - /agents listing solo administrator + custom_role advanced
//               - /inboxes listing solo administrator central
//
//             Fixes:
//
//             (#361 IDOR contact update) — ContactPolicy#update? endurecido.
//                 Antes: agente con conv asignada al contacto podía editar
//                 nombre/email/phone via contact_in_user_scope?. Ahora:
//                 SOLO administrator (base) o custom_role con contact_manage
//                 (Administrador de Institución / Central). Agentes
//                 "Conversaciones participando" sin contact_manage NUNCA
//                 pueden mutar contactos. show? sigue permitiendo lectura
//                 de contactos en su scope (operación normal).
//
//             (#371 /agents admin only) — UserPolicy#index? cambiado de
//                 `true` (cualquier user logueado) a `administrator? ||
//                 conversation_manage_advanced`. Agentes regulares ya no
//                 pueden enumerar el listado completo del account.
//
//             (#373 /inboxes admin central only) — InboxPolicy::Scope
//                 ahora distingue: admin (base) o conversation_manage_advanced
//                 ven todos los inboxes del account; resto solo los
//                 assigned_inboxes (operación intacta). Cierra el vector
//                 de enumeración de canales.
//
//             (#364 Account takeover via email collision) — migración DB
//                 add_unique_index_on_users_email con
//                 `algorithm: :concurrently` para no bloquear writes.
//                 Audit data confirmó 0 duplicados en prod (19 users, 19
//                 emails únicos). Defensive guard en up() aborta si aparecen
//                 duplicados entre audit y deploy. Combinada con T5 de 69.1.0
//                 (current_password requerido para cambio de email), cierra
//                 el flujo de account takeover end-to-end.
//
//             (#370 /contacts) — ya cubierto por ContactPolicy::Scope
//                 desde rev69 (admin/contact_manage ven todo, resto solo
//                 contactos con conv en su scope). Validado.
//
//             (#372 /teams) — ya cubierto por TeamPolicy::Scope desde
//                 rev69 (admin/conversation_manage* ven todo, resto solo
//                 sus teams). Validado.
//
//             Items pendientes para sprint 3:
//               #359/#366 search PII bypass — refactor SearchService
//               #369 massive exfil messages — MessagePolicy + scope
//
//   69.1.2 — Hotfix bug crítico: ActionMailer/ActiveStorage/AR jobs huérfanos.
//             Detectado en producción: 37 jobs huérfanos en cluster LEGACY
//             (wetalks-jobs-prod) queue 'default' con latency 76 horas.
//             Incluía emails de confirmación y reset password atrasados
//             hasta 34h sin entregarse.
//
//             Root cause: Jobs que heredan de ActiveJob::Base (no de
//             ApplicationJob) NO pasan por el around_enqueue de Trejo y
//             encolan en cluster LEGACY donde no hay workers escuchando.
//             Afecta a:
//               - ActionMailer::MailDeliveryJob (confirmaciones, resets)
//               - ActiveStorage::AnalyzeJob/PurgeJob/MirrorJob/TransformJob
//               - ActiveRecord::DestroyAssociationAsyncJob
//
//             Fix quirúrgico: nuevo initializer
//             config/initializers/sidekiq_routing_for_orphan_jobs.rb que
//             aplica el MISMO around_enqueue que Trejo's ApplicationJob
//             pero a las clases huérfanas conocidas. NO modifica
//             ApplicationJob, ni SIDEKIQ_QUEUE_PARTITIONS, ni el routing
//             original de Trejo. Sincroniza automáticamente con cualquier
//             cambio futuro en SIDEKIQ_QUEUE_PARTITIONS porque usa el
//             mismo helper sidekiq_partition_pool_for_queue.
//
//             Defensive design:
//               - Skip si la clase ya hereda de ApplicationJob (no doble wrap)
//               - rescue StandardError para no romper Rails boot si falla
//               - Log INFO al aplicar cada extension
//
//             Drenaje manual ya hecho en runtime: 4 mailers críticos
//             (3 confirmation + 1 reset password de 34h atrasado) procesados
//             vía deliver_now y 33 jobs no-mailer re-encolados al cluster
//             particionado correcto. Cluster LEGACY queda en 0 jobs.
//
//   69.1.0 — Security hardening sprint 1: items 🟢 baja + 🟡 media de
//             Revisión 5 (Informe de pruebas al 4 de mayo 2026). Items
//             críticos 🔴 alta y 🔥 crítica quedan para tanda separada
//             — requieren refactor con CR formal (account takeover, search
//             PII bypass, IDOR contact, massive exfil messages, APIs
//             expuestas).
//
//             Fixes:
//
//             (T1 — #347) MIME validation real con Marcel sniff. Antes
//                 Avatarable validaba solo header (falsificable). Ahora
//                 también sniff de magic bytes. Aplicado a Avatarable,
//                 Attachment (web widget), Macro. Cierra vector de subida
//                 de PHP/TS/malware al storage.
//
//             (T2 — #362) XSS en Contact name via Loofah scrub.
//                 sanitize_name ahora aplica Loofah.fragment.scrub!(:strip)
//                 ANTES de los demás checks. Defense in depth contra
//                 payloads `<script>` / `<svg onload=`.
//
//             (T3 — #385) Reset password en user bloqueado/inactivo.
//                 PasswordsController#create rechaza si user no es
//                 active_for_authentication?. Distingue admin lock
//                 (failed_attempts == 0) vs brute-force lock.
//                 Respuesta uniforme (timing-safe, no leak account state).
//
//             (T4 — #354) Reset password evade lockable + permite misma
//                 contraseña. reset_password_and_confirmation ahora:
//                 (a) admin_locked? defense in depth, (b)
//                 same_as_current_password? rechaza reuso (Devise.bcrypt
//                 timing-safe).
//
//             (T5 — #345) Cambio de email exige current_password.
//                 ProfilesController#update bloquea PUT con nuevo email
//                 sin verificar dueño. Defense parcial contra #364 (fix
//                 completo queda fuera de scope).
//
//             (T6 — #353) Endpoint admin /agents/:id/unlock_account
//                 ya existía. UI button es scope frontend.
//
//             (T7 — #356/#357) Session timeout: investigación. Sin logs
//                 de prod del incidente, no se toca timeoutable a ciegas.
//                 Anotado para tanda futura con telemetry.
//
//             Items NO incluidos (🔴 alta + 🔥 crítica, tanda separada):
//                 #364 account takeover (refactor email uniqueness +
//                 migración DB), #359/#366 search PII bypass,
//                 #361 IDOR contact update, #369 massive exfil messages,
//                 #370-#374 APIs expuestas.
//
//   69.0.10 — Hotfix segunda ola: chain-of-thought leak + queue race + aggregator.
//             Tres fixes coordinados, todos disparados por testing real
//             (chat público con casos de leak metadata, doble respuesta tras
//             handoff, e inputs fragmentados):
//
//             (E) Chain-of-Thought Leak Guard — Layer 1g del sanitizer +
//                 REGLA DURA #3 en assistant.liquid. Caso real (conv #337
//                 21:19:26): bot emitió al chat "Usuario consulta sobre la
//                 cantidad de diputados en El Salvador, tema relacionado
//                 con el Registro Nacional de las Personas Naturales." —
//                 razonamiento interno expuesto al ciudadano. Ahora:
//                 (a) prompt prohíbe explícitamente la 3a persona y
//                     ejemplos prohibidos concretos
//                 (b) sanitizer detecta patterns "Usuario consulta sobre",
//                     "tema relacionado con", "Procedo a clasificar", etc.
//                     y reemplaza por re-engagement neutro.
//                 (response_sanitizer_service.rb, assistant.liquid)
//
//             (F) Queue Race Guard — en ResponseBuilderJob#create_outgoing_message,
//                 si conv pasó a queued? hace ≤10s y la respuesta del LLM
//                 incluye "no tengo info / quieres transferencia / voy a
//                 transferirte", suprime el mensaje. Caso real (conv #336
//                 21:07:11–21:07:15): bot mandó queue reminder a las :11 y
//                 4s después contradijo con "No tengo info... ¿Deseas que
//                 te transfiera?". Race entre AssignTeamTool (que ya
//                 enqueued) y la respuesta tardía del LLM.
//                 (response_builder_job.rb)
//
//             (H) Aggregator Window 6-12s + Fragment Detector. Caso real
//                 (conv #334 21:19:09–21:19:14): user mandó "Bueno, como
//                 ouedo" y 5s después "cambiar mi apellido?". Con ventana
//                 4s, el primer fragmento se procesó solo → "no encontré
//                 info". Ahora:
//                 (a) DEFAULT_AGGREGATION_WINDOW de 4 → 6 segundos
//                 (b) Clamp [4,10] → [6,12]
//                 (c) método fragment_input?(content) que detecta inputs
//                     cortos sin cierre, conectores colgados, palabras
//                     interrogativas solitarias ("Cómo", "¿Y?", etc.) y
//                     typos cortos. Whitelist de saludos completos
//                     ("Hola", "Gracias", "Buenos días") para no extender.
//                 (smart_message_aggregator.rb)
//
//   69.0.9 — Hotfix bot UX coherente: mensaje proactive + cierre + sigla.
//            Cuatro fixes coordinados, todos disparados por testing real (chat
//            público con casos LGTB+DUI, ANDA, Ok-tras-proactive):
//
//            (A) Layer 1f — wording de alucinación NO cita la sigla. El user
//                preguntó por DUI; el LLM alucinó "RNP"; antes el canned decía
//                "No tengo info sobre 'RNP'" → confuso porque el user nunca
//                dijo RNP. Ahora wording genérico que invita a aclarar el
//                trámite. (response_sanitizer_service.rb)
//
//            (B) bot_has_pending_question? — el regex exigía "?" al FINAL del
//                texto. Si el bot decía "¿Podés decirme X? Así te ayudo." el
//                "?" no quedaba al final → guard fallaba → "Ok" del user
//                cerraba conv como :farewell. Ahora include?('?') simple.
//                (conversational_engine_job.rb)
//
//            (C) POST_PROACTIVE_GUARD_VARIANTS — cuando L2-AI-GUARD rebota
//                la respuesta del LLM y hubo proactive previo ("voy a
//                consultar X"), el canned genérico "no pude atender esa
//                consulta" CONTRADICE lo prometido. Nuevo set de variantes
//                que acknowledgean ("Después de revisar..."). Detección por
//                bot_has_recent_proactive? (≤30s). (response_sanitizer_service.rb)
//
//            (D) bot_has_proactive_pending? — guard adicional al farewell:
//                si el último outgoing fue un proactive y el user mandó "Ok",
//                NO cerrar. El LLM real está aún procesando; cerrar antes de
//                su respuesta deja al user con "Gracias" + respuesta huérfana
//                un segundo después. Reclasifica :complex.
//                (conversational_engine_job.rb)
//
//   69.0.8 — Hotfix de cola + handoff confirmation + hallucination guard.
//            Seis fixes coordinados:
//
//            (#0) Cablear QueueReminderJob en Conversation#enqueue_for_handoff!
//                 BUG CRÍTICO: el job existía desde 69.0.1 pero NUNCA se invocaba.
//                 Resultado: ciudadano en cola quedaba en silencio total hasta
//                 max_wait (30 min) o asignación de agente. Ahora siempre que
//                 algo enqueue (HandoffTool/AssignTeamTool/ResponseBuilder/
//                 RescueStalePending), el reminder se programa con el interval
//                 configurado en inbox.auto_assignment_config.
//
//            (#1) Layer 1f — Hallucinated Institution Guard en
//                 response_sanitizer_service. Detecta cuando el LLM menciona
//                 una sigla 3-8 chars que NO existe como team del account
//                 (ni activo, ni disabled). Reemplaza con disclaimer honesto.
//                 Excluye whitelist de siglas no-institucionales (DUI, NIT,
//                 NIE, etc. — trámites/documentos).
//
//            (#2) Relajar looks_like_institution_query? — acrónimos all-uppercase
//                 (3-8 chars) ya no requieren "?". Captura "MINSAL.",
//                 "necesito ANDA", "Y el ISSS". Nombres propios siguen
//                 requiriendo "?" para evitar falsos positivos.
//
//            (#3) handle_queue_cancellation diferencia 3 casos: estaba
//                 queued (cancela cola), pending con bot history (cierra),
//                 brand-new conv sin historia (mensaje orientativo, NO cierra).
//
//            (#4) Pre-filter handoff_confirmation_pending? — afirmación corta
//                 ("si", "ok", "vale", etc.) cuando bot tenía pregunta de
//                 transferencia pendiente. Caso real conv #386: user "si"
//                 → LLM cayó en error_response → handoff perdido. Ahora se
//                 fuerza :complex con awareness para ejecutar handoff.
//
//            (#5) Queue Lock — durante queued?, NO procesar consultas con
//                 LLM/RAG. Solo permitir cancelación (vía
//                 handle_queue_cancellation) y reminders del QueueReminderJob.
//                 Mensaje canned con dedupe de 60s. Si user menciona sigla
//                 (otra institución) durante cola, mensaje específico
//                 invitando a cancelar y empezar nueva conv. NO cambio de
//                 team mid-cola — un team por conv.
//
//            Validación local pre-fix: tmp/validate_queue_bug.rb
//            confirmó BUG #0 con conv #211 (queued? = true,
//            QueueReminderJob scheduled = 0).
//
//   69.0.7 — Hotfix consultas cortas sobre instituciones (caso real prod
//            conv #386, "Anda?" → silencio del bot 3min42s). Tres fixes:
//
//            (A) Clasificador preserva el último mensaje del bot completo
//                cuando termina en "?". Antes truncaba a 150 chars y cortaba
//                la pregunta del bot — el LLM clasificador no veía la
//                pregunta y aplicaba mal la regla "respuesta a pregunta del
//                bot = COMPLEX". Ahora truncate(400) general + sin truncate
//                para el outgoing si tiene "?".
//
//            (B) system_prompts_service.assistant_response_generator —
//                instrucción explícita: si user menciona institución y RAG
//                no tiene info, responder TEXTUALMENTE "No tengo información
//                sobre [INSTITUCIÓN] en mi base de conocimiento. ¿Podés
//                indicarme el trámite específico que necesitás...?" en vez
//                de "no puedo procesar esa consulta" u otra vaguedad.
//
//            (C) Pre-filter en ConvEngineJob#looks_like_institution_query?
//                — mensajes ≤25 chars + "?" + palabra capitalizada/sigla
//                3-8 chars (excluye saludos/meta) → forzamos :complex →
//                pasa por RAG. Garantiza que "Anda?", "MINSAL?", etc.
//                nunca caigan en :weak silencioso.
//
//            Bajo carga K6 el ordering NO se ve afectado: SmartMessageAggregator
//            usa Redis con clave por conversation_id (operaciones atómicas
//            serializadas). Latencia puede subir bajo carga, orden garantizado.
//
//   69.0.6 — HOTFIX cierre coherente + handoff gate + salir universal + dedupe JSON.
//            Cuatro fixes coordinados:
//
//            (1) Farewell inteligente con guard de pregunta pendiente.
//                Caso prod conv #387 (22:32 UTC 2026-05-05): bot preguntó
//                "¿Confirmás transferir a Hacienda?" → user "ok" → LLM marcó
//                :farewell → mi 69.0.4 resolve_conversation_after_farewell
//                mató la conv SIN ejecutar el handoff. Ahora detectamos si
//                el último outgoing del bot dejó pregunta abierta (termina
//                en "?" o menciona acción pendiente: confirma/transferir/
//                conectar/querés/necesitás). Si SÍ + LLM clasifica :farewell,
//                reclasificamos como :complex para que el LLM principal
//                procese con awareness del contexto pendiente. Si NO hay
//                pregunta pendiente (despedida real), agendamos FollowUp
//                natural — cierre suave en 12 min vía step 3.
//                También se quita el `return if :farewell` en
//                schedule_follow_up_after_solid (la combinación con el bug
//                ms-precision dejaba convs huérfanas).
//
//            (2) Gate de consentimiento en process_action('handoff') de
//                ResponseBuilderJob. Bug histórico: el path donde el LLM
//                retorna `{"response":"conversation_handoff"}` raw bypaseaba
//                el gate de TransferIntentService que sí aplicaban
//                HandoffTool y AssignTeamTool. Resultado: handoffs no
//                consentidos en error_response fallback. Ahora el gate
//                corre en TODOS los paths: requiere (a) usuario aceptó
//                explícitamente, (b) destino mencionado por el bot
//                fundamentado en knowledge base (no alucinación).
//
//            (3) "salir" funciona en estado pending además de queued.
//                R4-#11 del informe Rev4: el handler solo aceptaba
//                `conversation.queued?`, dejando descubierto el caso
//                "user abre chat y dice 'salir' como primer mensaje" y
//                el window de pocos segundos entre respuesta-bot y
//                schedule_follow_up. Ahora `queued? || pending?` con
//                mensaje de despedida adaptado al estado.
//
//            (4) `greeting_just_sent?` reescrito con Ruby filter. Mismo
//                bug del JSON column ya corregido en `conversation_has_bot_history?`
//                (69.0.4): la columna `messages.content_attributes` es `json`
//                y `store coder: JSON` la persiste como JSON-string, así que
//                el operador `->>` devuelve NULL — query siempre falsa →
//                dedupe roto silencioso. Reemplazado por filtrado en Ruby
//                sobre window pequeña (3 segundos).
//
//            E2E local validado contra LLM real: 11/11 PASS preservados.
//   69.0.5 — fix raíz del FollowUpSequenceJob (descubierto via E2E):
//            (1) `Time.current.iso8601` (sin precisión) redondea a segundos.
//                La respuesta del bot tiene timestamp con ms, así que el
//                scheduled_at queda *antes* que el propio mensaje del bot
//                que disparó el schedule. El guard rev54 de la línea ~50 del
//                FollowUpSequenceJob (`last_message > scheduled_at`) entonces
//                aborta step 1 por milisegundos → conv huérfana → eventual
//                "¿Sigues ahí?" del RescueStalePending. Bug presente en prod
//                desde rev54 — explica TODOS los rescates que veíamos.
//                Fix: `iso8601(3)` (ms-precision) en schedule_follow_up_after_solid
//                + tolerancia de 1s en el guard del job.
//            (2) Re-stampar `follow_up_scheduled_at` al avanzar de step 1→2 y
//                step 2→3 (antes se mantenía el original, así que el mensaje
//                del propio step previo aparecía "newer" en el guard del
//                siguiente step).
//            Validación E2E con LLM real: 11/11 PASS cubriendo flujo A
//            (cierre explícito con "no, gracias"), flujo B (FollowUp 3 steps
//            por inactividad → resolve + CSAT), flujo C (HookExecution
//            durante follow-up window).
//
// IMPORTANTE: NO usar el viejo formato `rev70`, `rev71`, etc. Siempre decimal.
//
// Se usa para dos cosas:
//
// 1) Tag visible en el sidebar (junto al nombre de la cuenta) para verificar
//    de un vistazo qué build tiene cargado el navegador. Útil cuando un usuario
//    reporta un bug y queremos saber si ya está corriendo la versión con el fix.
//
// 2) Cache-busting al entrar/salir de la sesión. Cuando el usuario cierra
//    sesión y vuelve a entrar, anexamos `?_v=APP_REVISION` a la URL de redirect
//    para forzar al navegador a re-pedir el HTML índice. Esto evita que un
//    usuario quede atrapado en un bundle viejo cacheado tras un deploy.
// 69.4.0 — registro VOSEO salvadoreño en TODO el bot (prompts + strings canned + backstop
//          determinístico usted→voseo) + fixes de gate (cambio de institución mid-oferta,
//          "No, gracias", sigla dc 2-char, institución deshabilitada = cerrado total,
//          re-resolución de institución) + auditoría de cambios de usuario (nombre/foto/correo
//          con actor, pestaña "Cambios en usuarios"). Migración rev12c re-seedea escenarios a voseo.
// 69.4.1 — fix anti-alucinación de institución (conv prod #750): una institución REAL pero
//          NO configurada en el bot (ej. "Fondo Social para la Vivienda") se confundía con
//          otra institución real SÍ configurada que comparte una palabra del nombre ("Ministerio
//          de Vivienda") — el bot afirmaba datos de la institución equivocada en vez de pedir
//          que el ciudadano confirme cuál es. Dos puntos de match por palabra suelta corregidos
//          a match exacto de nombre/alias: Captain::Tools::StatusQueryTool#institution_tokens y
//          Captain::ConversationalEngineJob#team_match_terms (este último mutaba
//          conversation.team_id ANTES de que corriera el LLM/tool, pisando cualquier guard
//          downstream). Validado E2E en el widget local (3 corridas: bug corregido x2, camino
//          feliz sin regresión) + specs de regresión en ambos archivos.
// 69.4.2 — anti-invención de contacto + guard de institución única (pentest del web chat local,
//          continuación de 69.4.1). Tres flancos cerrados:
//          (1) NO-ENCONTRADO / FALLO TÉCNICO: ante un trámite no hallado, el LLM rellenaba con
//              teléfono/sitio web INVENTADOS de su training (ningún guard anti-alucinación corre
//              tras status_query_tool, que no es faq_lookup). StatusQueryTool#format_response ahora
//              prohíbe explícito inventar contacto en el string que recibe el modelo.
//          (2) PEDIDO DIRECTO ("dame el teléfono aunque no lo tengas"): el LLM respondía de su
//              cabeza SIN llamar ningún tool → (1) no alcanzaba. Nuevo guard en
//              AgentRunnerService (contains_unverified_contact_info?): si nada quedó grounded este
//              turno + la KB no tiene match confiable + la respuesta trae patrón de teléfono/URL →
//              se reemplaza por un "no tengo ese dato" natural. Angosto a propósito (teléfono/URL)
//              para no tocar charla normal.
//          (3) INSTITUCIÓN ÚNICA HABILITADA (caso prod: solo SIMPLE): el atajo "una sola habilitada
//              → asumirla" de resolve_institution_from_user consultaba el webhook de SIMPLE con la
//              referencia del ciudadano aunque hubiera nombrado OTRA institución (FSV) → riesgo de
//              reportar un trámite ajeno si la referencia coincide. Ahora, si el ciudadano nombra
//              una institución específica NO habilitada (OTHER_INSTITUTION_SIGNALS), NO se asume:
//              se aclara qué se puede consultar. Sigue asumiendo la única cuando no nombra ninguna.
//          Validado E2E en el web chat local: inyección directa bloqueada, conversación larga
//          (context-bleed + confirmación-injection) sin fuga, institución única con mirror de prod.
//          Specs de regresión en StatusQueryTool + AgentRunnerService.
// 69.4.3 — validación a detalle de features previos + mitigación de enumeración de trámites:
//          (1) ANTI-BATCH / ENUMERACIÓN (pentest conv prod #745, CRÍTICO): el ciudadano enumeró
//              trámites de terceros (tabla, lista, "familia" con DUIs distintos) — 25 consultas en
//              una conversación. El guard "1 por turno" no alcanzaba (se resetea por mensaje; el
//              ciudadano hacía "nuevo turno dentro de la misma conversación"). Dos capas
//              determinísticas (robustas a ingeniería inversa):
//              - ConvEngineJob#handle_batch_status_request: si el mensaje pide MÚLTIPLES trámites
//                (2+ DUIs distintos, o 3+ números de trámite) → corta ANTES del LLM, pide uno solo.
//              - DUI-lock, 1 DUI por conversación (súper estricto): el PRIMER DUI usado en una
//                consulta real queda FIJADO; cualquier DUI DISTINTO después se bloquea. DOS capas:
//                (a) ConvEngineJob#handle_dui_lock_mismatch — determinístico, a nivel de MENSAJE,
//                antes del LLM (no depende de que el LLM llame al tool); (b) StatusQueryTool
//                (respaldo, dentro del tool). Cierra el hueco donde el LLM respondía de su cabeza
//                (sin tool) ante un DUI de tercero. NO se agregó rate-limit (decisión de producto).
//                Validado E2E — mímica completa de #745 en el web chat local: batch cortado, bypass
//                "nuevo turno" cortado, 2º DUI bloqueado con mensaje claro; DB: 1 consulta, 1 DUI
//                (vs 25 consultas / múltiples DUIs del ataque original) + specs.
//          (2) ENLACES DEL WEBHOOK: StatusQueryTool#format_response emitía externalReference.url
//              como TEXTO PLANO. El sanitizer redacta URLs sueltas fuera de la whitelist
//              (.gob.sv/.edu.sv/goes.wetalks.app/maps) → como los webhooks devuelven dominios
//              propios de cada institución (ej. tu-sistema.example.com, no .gob.sv), el link se
//              PERDÍA (confirmado en runtime con inquiries reales). El link del webhook es fuente
//              AUTORIZADA y debe mostrarse SIEMPRE → ahora se emite como markdown [ver detalle](url),
//              que el sanitizer preserva por diseño (rev12.8 #745). Verificado E2E + spec.
//          (3) AUDITORÍA CSV: audit_activity_text NO tenía case para user:update → los cambios de
//              identidad (nombre/nombre visible/correo/foto) salían como "actualizó user" genérico en
//              el CSV descargable (la pestaña de la UI SÍ los describía bien). Nuevo user_update_text
//              replica la lógica del front (self/other + atributo + valores viejo→nuevo). Specs.
//          (4) VOSEO seeder: se validó en runtime que el voseo YA estaba completo funcionalmente (el
//              tuteo residual de las plantillas NO llega al ciudadano; el prompt principal domina).
//              Igual se limpiaron los 6 strings citizen-facing con tuteo del seeder (¿Podrías→¿Podés,
//              deseas→querés, necesitas→necesitás) para dejar el código consistente. Sin re-seed
//              forzado en prod (no hay bug; el próximo re-seed natural lo toma).
// 69.4.4 — SEGURIDAD: el CORREO de un agente NO es modificable vía API por ningún usuario/admin,
//          SOLO por SuperAdmin (consola /super_admin o platform API). Cierra el vector demostrado
//          (script inyectado en DevTools que hace PUT /api/v1/profile con la sesión del usuario y
//          `profile[email]`, evadiendo el frontend). Bloqueo:
//          - ProfilesController#profile_params: `email` OMITIDO de los strong params → un PUT
//            /api/v1/profile con `profile[email]` se ignora por completo (ni el dueño ni un admin de
//            cuenta lo cambian). Se quitó el bloque de "email change" (unconfirmed_email + password).
//          - AgentsController#update: `email` fuera de allowed_agent_params y del slice → editar un
//            agente NO cambia su correo (el create SÍ lo pide, eso es alta). skip_reconfirmation removido.
//          - EditAgent.vue: el campo correo pasa a read-only/disabled (informativo, no editable) y ya
//            no se envía `agent[email]`; se quitó la validación de email del form.
//          Las vías de SuperAdmin (super_admin/users avo + platform API con token de infra) quedan
//          intactas — un agente/admin no tiene esas credenciales. Specs de request (profile + agents)
//          verifican que el email NO cambia ni para agente ni para admin de cuenta.
// 69.4.5 — SEGURIDAD: cierre del 3.er vector de cambio de correo que 69.4.4 no cubría. Una auditoría
//          adversarial (5 vectores) + verificación EN VIVO encontró que `PUT/PATCH /auth` seguía
//          dejando que el propio usuario (agente o admin de cuenta, NO SuperAdmin) reescribiera su
//          correo con solo su sesión. RAÍZ: `mount_devise_token_auth_for 'User'` NO sobreescribía
//          `registrations`, así que el RegistrationsController por defecto del engine (que permite
//          :email en account_update y NO pide contraseña actual) quedaba montado. En vivo, el correo
//          cambiaba de forma DIRECTA (confirmed=true), no en unconfirmed_email. Fix:
//          - Nuevo DeviseOverrides::RegistrationsController que quita `:email` de account_update_params
//            (el update ignora el correo); el alta/registro (sign_up_params) NO se toca.
//          - config/routes.rb: se registra `registrations: 'devise_overrides/registrations'` en el mount.
//          Verificado EN VIVO (Edge): PUT /auth con la sesión → 422, el correo NO cambia. Spec de
//          request nuevo (registrations_controller_spec) cubre agente y admin de cuenta.
// 69.5.0 — TRAMITOLOGÍA (rework por regresión prod conv #756/#757, validado EN VIVO en web chat local):
//          - DUI-lock ELIMINADO: no somos rate-limiter; un DUI distinto está bien si el ciudadano lo
//            re-provee. Anti-enumeración = anti-batch + context_guard (re-poner el DUI por trámite).
//          - Predicado ÚNICO Team#captain_available? (captain_enabled && active_for_bot != false) en
//            los 5 sitios (handle_disabled_institution, status_query, agent_runner, response_builder,
//            scenario_auto_generator). Institución DESACTIVADA manda sobre tener webhook → mensaje por
//            defecto, no asistir (cierra el flanco captain_enabled=false + la institución heredada).
//          - Acoplamiento webhook↔institución: el tool solo resuelve instituciones con webhook activo Y
//            bot-available; el set queryable == el que ofrece el escenario de trámites.
//          - Match de institución por PALABRA COMPLETA (no substring) + descarte de nº de referencia
//            (cierra R1/R5: "MIVI-2024-000777"/"simplemente" no auto-confirmaban institución).
//          - context_guard: recent_incoming con .reorder(:desc) (vencía el default_scope ASC) → ahora
//            SÍ corta el reuso silencioso del DUI en un trámite nuevo.
//          - Anti-alucinación de contacto: CONTACT_INFO_RE angostado (no confunde DUI/referencia con
//            teléfono) + flag GENÉRICO Current.captain_tool_returned_canned (lo prende
//            BasePublicTool#log_tool_usage en acciones de bloqueo/gate) → las capas anti-alucinación y
//            el sanitizer NO censuran los mensajes-sistema del tool ("reenviá tu DUI"). Reset por turno
//            (evita bleed cross-conversación). Test de humo (canned sobrevive al sanitizer).
//          - REQUIERE migración rev13 (20260702120000): re-seed de templates + regeneración de
//            escenarios (team_faq_lookup llama faq_lookup_team directo; team_list_inquiry acotado).
// 69.5.1 — ENDURECIMIENTO #756 (raíz reabierta, validado con tests + prod real por WhatsApp):
//          La herencia de institución en status_query_tool heredaba CUALQUIER conv.team activo+consultable
//          ANTES del gate institución-primero. Como conv.team lo puede fijar el RUTEO del canal o una
//          asignación MANUAL de un agente (no solo una consulta previa del ciudadano), una conversación
//          ruteada a la institución consultable (SIMPLE) quedaba con el gate neutralizado: al dar número +
//          DUI sin nombrar institución, el bot consultaba el webhook en SILENCIO. Fix: la herencia usa SOLO
//          la institución CONFIRMADA por una consulta previa (marca additional_attributes
//          'status_query_confirmed_team_id', escrita en persist_status_institution! recién tras confirmar).
//          Un team_id de ruteo/asignación ya NO habilita consulta silenciosa → el gate exige confirmar la
//          institución. Continuidad multi-trámite (rev12 #731) preservada; desactivación sigue mandando
//          (rev12.13). +4 specs (bug cerrado, continuidad, marca, deshabilitada). Sin migración.
// 69.5.2 — anti-shadowing del KB-backstop en consulta de estado (no determinístico):
//          En prod, un pedido de estado sin institución ("consultar el estado de mi trámite, nº X, DUI Y")
//          hacía que el LLM preguntara la institución SIN llamar status_query → como no se llamó ningún
//          tool y nada quedó grounded, el KB-backstop (agent_runner_service) REEMPLAZABA la respuesta con
//          el FAQ más cercano < 0.45 — en prod "¿Cómo consultar el estado de mi denuncia?" (team dc, 0.438)
//          matcheaba casi idéntico, así el ciudadano recibía un FAQ de Defensoría en vez de "¿de qué
//          institución?". Es un artefacto del espacio de embeddings de PROD (en local ese FAQ cae a 1.005,
//          NO reproduce). Fix: la descripción de StatusQueryTool ahora instruye al LLM a LLAMAR el tool en
//          cuanto haya número de referencia, AUNQUE la institución no esté confirmada (el gate determinístico
//          rev12.12/12.14 pide la institución). Al llamarse un tool (role :tool), no_tools_called? es false
//          → el KB-backstop NO dispara (guard existente) → se ve el gate, no el FAQ. El tool IGNORA el
//          team_name que adivine el LLM y usa lo que dijo el ciudadano, así institución-primero se mantiene.
//          Validado local: el LLM ahora llama status_query → institution_disambiguation(user_named_none).
// 69.5.3 — war room (conv #766/#767/#768): 5 mitigaciones probadas local (101 specs verdes):
//          (1) ENLACE DE TRÁMITE SIEMPRE COMPLETO Y PLANO: toda URL de externalReference del webhook se
//              muestra entera en texto plano (nunca markdown, nunca "canales oficiales"). Tool emite URL
//              plana + register_status_url! (Current.captain_status_query_urls + persistida en la conv,
//              rehidratada por turno); sanitizer DESENVUELVE [texto](url) → url y allowlistea las URLs
//              del webhook (anti-phishing intacto: URL no-autorizada se sigue redactando).
//          (2) HANDOFF #767: "pasame con un agente…ya no quiero seguir hablando con vos" ya NO es rechazo
//              (HUMAN_REQUEST_RE y STOP_TALKING_TO_BOT_RE preceden a DECLINE_PHRASE_RE); rechazos reales
//              ("no gracias", "ya no quiero el agente") intactos.
//          (3) DISABLED-INSTITUTION #768: negación ("NO me pasés con MINSAL") o pedido de "cualquier/otro
//              agente" ya NO disparan el canned de institución apagada (dead-end ×3) → cae al gate que
//              ofrece instituciones disponibles.
//          (4) CONTEXTO #768: el gate resuelve "esa institución" de los últimos 5 incoming antes de
//              re-preguntar "¿a qué institución?"; si la resuelta está apagada lo dice directo.
//          (5) AI-GUARD #766: el rebote en conversaciones EN FLUJO de consulta de estado usa wording que
//              mantiene el flujo (pedir trámite+DUI) en vez del confuso "no tengo en mi base de
//              conocimiento" (el guard NO se debilita); follow-up "¿algo más?" se POSPONE si hay oferta
//              de handoff pendiente (TTL) en vez de interrumpir el flujo.
// 69.5.4 — war room (conv #767/#768 + captura trámite-en-tema): 5 fixes validados en widget local
//          (matriz determinística 21/21 + specs 46/46 + E2E conv #1150-#1155):
//          (1) TRÁMITE NUNCA A LA KB: un mensaje con identidad de trámite (DUI /\b\d{8}-?\d\b/ o
//              "trámite"+número ≤40 chars) JAMÁS se busca en conocimiento. Respondiendo al "¿sobre qué
//              tema?" → ABANDONA el handoff (clear_offer! + NO_OFFER_PENDING) y rutea al flujo de estado
//              (StatusQueryTool/webhook). Guards en gate (awaiting_topic/awaiting_confirmation) + engine
//              (single_tramite_status_request? corta handle_human_request para que clarify_reply no re-arme).
//          (2) HUMANO+TRÁMITE = handoff CON KB SALTADA: pedido explícito de humano gana (el trámite es la
//              razón); kb_found: :kb_skipped emite la confirmación directa SIN el prefijo "no tengo esa
//              información en mi base de conocimiento" (ni se buscó).
//          (3) ACEPTACIÓN ENOJADA #767: con oferta pendiente, un pedido explícito de humano
//              (HUMAN_REQUEST_RE) ES la aceptación → ejecuta el handoff (antes: el judge no lo pescaba →
//              re-oferta "¿SÍ o NO?" = la doble confirmación reportada).
//          (4) "SÍ, ES EL TRÁMITE X" CONFIRMA: CONFIRMATION_AFFIRMATIVE_RE precede al guard de trámite en
//              awaiting_confirmation (una afirmación con el trámite como contexto acepta la transferencia,
//              no abre consulta). El guard de trámite precede a explicit_decline? ("no, mejor decime el
//              estado del trámite X" = consulta, no rechazo seco).
//          (5) CANNED PEGAJOSO #768 + LIBERACIÓN POR TRÁMITE: institución apagada en contexto reciente →
//              canned FIJO ante insistencias sin institución ("cualquier agente"); SOLO nombrar otra
//              institución habilitada O pedir un trámite (tramite_intent?: identidad o "trámite"+verbo de
//              consulta) cambia la disposición — sin esto, el ciudadano que aceptaba el "¿o algún trámite?"
//              del propio canned quedaba en loop.
// 69.5.5 — endurecimiento nocturno pre-war-room, guiado por DIAGNÓSTICO de prod (24 mudos en 3 días):
//          (1) ANTI-MUDEZ: dedupes 60s→15s en disabled_institution (convs #757/#768: ciudadano insistió
//              <60s tras el canned → 21 min de silencio), queue_locked y handoff offer/clarify (re-pedido
//              de agente tras un rechazo quedaba mudo). 15s sigue cubriendo double-processing (agregador ≤4s).
//              NOTA OPS: los otros 22 mudos son del inbox "GOES" con captain APAGADO y sin humano asignado
//              (agujero operativo, no de código) — decisión pendiente: prender captain ahí o staffear.
//          (2) FAIL-OPEN AUDIBLE: InputGuard y L2-AI-GUARD siguen fail-open (disponibilidad) pero loguean
//              tag único "[CAPTAIN][FAIL-OPEN]" — una caída del LLM ya no apaga los guards EN SILENCIO.
//              Alertar sobre ese tag en el stack de logs.
//          (3) FOLLOW-UP NO CORTA LA RECOLECCIÓN: AWAITING_DATA_PATTERN ampliado a VOSEO (quedó formal
//              tras 69.4.0): "tu DUI", "compartime", "decime/pasame el número", "número DEL trámite",
//              "¿cuál es el/tu número?" — el "¿Hay algo más?" ya no interrumpe el flujo de trámite.
//          (4) RED GOLDEN: spec/services/captain/war_room_golden_spec.rb — 18 casos determinísticos que
//              blindan TODOS los fixes de war room (#767/#768/#1148, dedupes, voseo). REGLA: ningún fix
//              de war room se mergea sin su caso acá.
// 69.5.6-69.5.18 — MERGE de lab/dialogue-core (40 commits, 3-5 jul). Bloques:
//          ARQUITECTURA dialogue-core: IntentResolver (dueño único de intención, 3 escalones:
//              regex → biblioteca semántica con embeddings → LLM), FSM Dialogue::State (mismas
//              claves legacy, TTLs centralizados), ConversationFacts (caché de consulta ≤10 min,
//              sin re-POST, con "actualiza"), Institution::Extraction (4 detectores en perfiles
//              congelados con 58 pins de caracterización).
//          DIETA DEL PROMPT: assistant.liquid 950→671 líneas (−30% tokens/turno) con spec de
//              integridad del prompt real. REGISTRO: VOSEO → TUTEO neutro en TODO (prompts,
//              canneds, templates; post-filtro determinístico USTED_TO_TUTEO).
//          FIXES E2E/war-room: link-recall desde la caché (#766), AFFIRM-con-cola determinístico
//              (#747/#767), tramite-abort en la red retroactiva, corrección-de-dato no dispara
//              handoff, re-anclaje de team cuando el tema nombra otra institución (#757).
//          69.5.6 bombas latentes V1/config-NULL de ResponseBuilderJob; 69.5.7 anti-bleed de
//              institución entre trámites (reconocimiento dinámico de teams no consultables +
//              guard frase-ajena + strip de institución pegada a la referencia); 69.5.8 canned
//              espurio "UN trámite a la vez" (fallback DB acotado a cola-sin-responder);
//              69.5.9 "SIMPLE0200" en el TEXTO → normalización a la referencia canónica;
//              69.5.10 CLOBBER de additional_attributes en ProcessAggregatedMessagesJob (bug de
//              main: pisaba status_query_ctx y la marca 69.5.1 — lock+reload+merge);
//              69.5.11 idempotencia de creación de conversación en widget (race de POSTs);
//              69.5.12 candado anti-desastre de specs (env test + DB *_test) + schema.rb al día.
//          AI-GUARD 12→2 (flag CAPTAIN_AI_GUARD_V2_ENABLED, default OFF — v1 sigue mandando):
//              69.5.14/15 capa dura de TELÉFONOS vs KB aprobada (WOULDFIRE; flag
//              CAPTAIN_L1_PHONE_REDACT_ENABLED) SIN cache — principio "KB VIVA";
//              69.5.16 teams VIVOS: cero cache en las 7 capas de instituciones (efecto
//              inmediato de cambios de admin) con specs guardianes anti-snapshot;
//              69.5.17 juez v2 de 3 preguntas (UNGROUNDED/MANIPULATED/CONDUCT) con EVIDENCIA
//              del turno (Current.captain_faq_matches + StatusInquiry + glosario);
//              69.5.18 eval ampliado a 54 casos (36 ataques adversariales FUENTE D) — v1 54/54,
//              v2 54/54, cero flips. Sombra en prod pendiente antes de encender v2.
// 69.5.19 — réplica prod conv #773 (primer turno real de 69.5.18): el KB-BACKSTOP pisó la
//           petición de DUI del escenario de trámites con la FAQ de contacto de mivi
//           (dist 0.422 en prod; ~1.0 en local → nunca reprodujo localmente). Veto
//           determinístico: si el IntentResolver clasifica el mensaje entrante como
//           tramite/* (escalón lexical, cero LLM), el backstop no reemplaza el turno —
//           extensión del principio 69.5.2 "tool llamado desactiva el backstop" al caso
//           "intención de trámite sin tool" (pedir DUI/referencia no llama tools).
// 69.5.20 — forense del barrido 5-jul (convs #778/#779), TODO reproducido en local antes
//           de tocar código y re-validado chat por chat en el widget:
//           FX2 handoff FANTASMA: try_retroactive_handoff! ejecutaba con CUALQUIER
//           mensaje no-vago tras una oferta SÍ/NO del LLM ("¿Cómo seguimos?") vía el
//           fallback a conversation.team — una pregunta ajena ("¿dónde queda el hospital
//           rosales?") o un saludo encolaban al ciudadano. La rama sustantiva del #690
//           ahora exige pregunta-de-TEMA del bot; oferta SÍ/NO solo acepta afirmación.
//           + espejo #747: el AFFIRM determinístico del resolver acepta lo que el judge
//           no pesca ("Confirmo dijo mi tío"), con veto de negación explícita.
//           + conversación resuelta jamás retro-transfiere (cruce cierre/mensaje).
//           FX1 spam de cola: QueueReminderJob corta en conv resuelta (resolved+queued
//           post-cierre spameaba "aún está en la cola" sin tope, max_wait=0 en prod).
//           Observabilidad: los 3 deny-paths dejan rastro en el log (guard #778/#779).
// 69.5.21 — Protocolo de Turno (FIRMADO): primeras reglas implementadas.
//           R5: pregunta sobre institución DESHABILITADA (caso "hospital rosales" #788) →
//           aviso canónico en vez de caer al LLM. DETECTAR ≠ SERVIR: el match de embeddings
//           identifica la pertenencia sin servir contenido apagado (anti-bleed intacto).
//           Texto ÚNICO en todas las rutas (invariante #768), wording firmado por el dueño y
//           editable EN VIVO vía InstallationConfig CAPTAIN_DISABLED_INSTITUTION_NOTICE.
//           R3: la oferta de agente tras KB-miss ya NO anuncia "no tengo esa información"
//           (el ciudadano pidió una persona, no información) — pregunta directa.
//           + rake captain:intent_fallbacks: lazo de aprendizaje del clasificador (reporte
//           on-demand de frases sin clasificar; resolver INTACTO, auditado sano).
// 69.5.22 — 3 fixes delicados del barrido 5-jul, todos spec-réplica primero + panel
//           adversarial (6 lentes, 17 hallazgos incorporados):
//           (1) OBS-1/réplica #1260 — la caché A2 pisaba el turno del trámite NUEVO cuando
//           el ciudadano respondía solo el DUI: veto determinístico anclado al TEMA vigente
//           (último mensaje de CUALQUIER lado que nombre un trámite; reorder vence
//           default_scope — lección 69.1.72; comparación por subset de grupos numéricos,
//           años embebidos no confunden; unidades "180 días" no anclan; log audible
//           "Cached-status VETADO"). + atajo parcial del ciudadano ("¿y el 1842?") ahora
//           matchea G-2026-0001842 por serial (el año suelto NO).
//           (2) OBS-2/réplica #1260 — "Ministerio de Vivienda (SIMPLE)": Layer 1j-bis del
//           sanitizer (paréntesis Y guion/slash con OTRA institución adyacente → se limpia
//           con WARN L1jB-COMBINED; alias propio y desambiguaciones legítimas intactos) +
//           template: regla nunca-combinar + ejemplos NEUTRALIZADOS (nombraban SIMPLE y
//           Vivienda literales — plantaban la asociación) + migración rev14 de regeneración.
//           (3) FX-stale/réplica #1265 — EMISOR ÚNICO: la respuesta generada para un turno
//           ya SUPERADO por un mensaje nuevo del ciudadano se suprime en la emisión
//           (guard hermano del queue-race 69.0.10, log "STALE-TURN"); el turno nuevo
//           responde con la historia completa.
// 69.5.23 — barrido de clasificador/flujos: el TURNO MIXTO (acción de flujo + consulta en
//           el mismo mensaje) ya no descarta la consulta. Tres guards (réplicas T1/T2/T7,
//           convs locales #1275/#1280/#1278):
//           T1: regla #1148 elevada al RUTEO — identidad de trámite ⇒ ruteo directo al
//           escenario de estado aunque el turno mezcle otra consulta (el router
//           single-escenario elegía la otra mitad y la consulta con TODOS los datos se
//           descartaba). La política del marker vive en el ESCRITOR: la vía de biblioteca
//           sigue gateada por CAPTAIN_DIRECT_ROUTING_ENABLED; los consumidores
//           (ResponseBuilder/AgentRunner) aplican lo que encuentren.
//           T2: rechazo que ACARREA pregunta ("mejor no, primero decime el horario…") →
//           oferta cancelada + el turno sigue al pipeline (la pregunta se responde); el
//           rechazo SECO conserva su cancelación explícita.
//           T7: el espejo AFFIRM (gate y retroactiva) NO consume como aceptación una
//           afirmación CON pregunta a cuestas ("ok y ¿cómo va el mío?") — cede al judge
//           (pregunta_nueva?) y la oferta queda viva para que el LLM responda.
//           + anáfora posesiva de estado ("¿cómo va el mío / lo mío?") sirve la consulta
//           fresca de la caché A2 (antes caía en "no procesé bien tu último mensaje").
// 69.5.24 — R1 del paquete firmado 6-jul: VOZ SIN LÍMITES VERBALIZADOS (textos firmados).
//           El flujo de trámites sigue siendo uno-a-la-vez por diseño (#745/#721 intactos),
//           pero el ciudadano ya no lee la restricción:
//           (1) canned del engine reescritos en positivo — BATCH_STATUS_MSG ("¡Con gusto te
//           ayudo…! Vamos en orden…") y DUI_LISTING_MSG (pide el número de referencia sin
//           "no puedo/no disponible"); validados byte a byte en widget (conv local #1286).
//           (2) strings del StatusQueryTool: duplicate_call_blocked instruye comunicar el
//           resultado YA obtenido sin mencionar límites; institution_unrecognized en
//           positivo ("Puedo consultarte…") conservando la derivación (R5 intacto).
//           (3) template status_query: canónicas de CAMBIO 1 / FUERA DE ALCANCE en positivo
//           + prohibición explícita de verbalizar restricciones; migración rev15 de
//           regeneración. HALLAZGO: el template rozaba el tope de 20k y el seeder fallaba
//           EN SILENCIO (save inválido → escenarios viejos con migración "exitosa") →
//           candados nuevos de PERSISTENCIA real y de longitud (quedó 19.989/20.000 — a
//           dieta antes del próximo cambio de template).
//           (4) sanitizer: Layer 1L anti-eco (caza al LLM diciendo "solo puedo uno a la
//           vez/no puedo procesar varios/de a uno" → arrancador de flujo firmado; exención
//           KB-grounded) + reemplazo de 1i sin "no puedo buscar en varias".
//           (5) observabilidad: guards de decisión a WARN (ruteo directo #1148, ruteo por
//           intent, veto de caché #1260, rechazo-con-consulta T2 ×2) — audibles en prod.
// 69.5.25 — R3 del paquete firmado 6-jul: LÍMITE DE CONSULTAS por ciudadano.
//           (1) cupo de consultas REALES al webhook: 5 por ventana RODANTE de 24h (sin
//           exploit de medianoche), anclado al CONTACTO (contact_id nuevo en
//           status_inquiries, backfilled; 1:1 con teléfono en WhatsApp, cubre widget).
//           Cuenta sobre la AUDITORÍA (inmune a failover de Redis — lección 24-jun);
//           check en StatusQueryTool#perform ANTES de los gates de institución (a un
//           ciudadano sin cupo no se le recolectan datos que no se usarán); la caché A2
//           ni consume ni graba. Canned diplomático SECO (sin derivación — firmado) con
//           cap dinámico; action status_rate_limit_blocked (flag anti-alucinación
//           automático); WARN audible "R3: cupo alcanzado". Cap configurable
//           CAPTAIN_STATUS_QUERY_DAILY_CAP (vacío→5; 0=off). Fail-open audible.
//           (2) reset SOLO admin con scope: permiso nuevo `citizen_history_manage`
//           (contact_manage NO alcanza — policy propia), endpoint POST
//           /contacts/:id/reset_captain_quota + botón "Renovar cupo de consultas" en el
//           historial ciudadano; mueve la línea de conteo (captain_quota_reset_at) SIN
//           borrar auditoría, con Audited::Audit del actor (patrón block/unblock).
//           Agentes humanos exentos por construcción. Validado E2E local: bloqueo en
//           widget (LLM relay del canned), botón visible SOLO con el scope, reset con
//           audit, consulta viva de nuevo post-reset. 13 specs candado nuevos.
// 69.5.26 — R4 del paquete firmado 6/7-jul: 3 STRIKES DIPLOMÁTICOS + REPUTACIÓN captcha.
//           (1) Captain::StrikeService: strikes POR CONVERSACIÓN, ACUMULATIVOS y sin
//           perdón interno (separado del off_topic_count de racha); escalera de avisos
//           FIRMADOS byte a byte ("es mi primer/segundo aviso") y cierre diplomático con
//           puerta abierta al 3.º (resolver — NUNCA blocked); WARN audible por strike.
//           (2) Detección: insultos → detector DETERMINÍSTICO sobre el input (cierra el
//           gap del InputGuard <80 chars; cero LLM en el turno) + guard de cesión en el
//           HookExecutionService (el clasificador de cierre se comía "andate a la mierda"
//           como farewell); role-play/hijacking → InputGuard suma strike directo; juez
//           L2-AI-GUARD → suma strike (IGUAL PESO firmado) con la escalera en vez de la
//           degradación anónima; instituciones fuera de KB → franja curiosidad/
//           desobediencia (1.ª pregunta = aviso canónico R5 INTACTO; la RE-disparada del
//           mismo detector = gracia en tier normal → escalera).
//           (3) REPUTACIÓN por contacto tipo captcha (economía de tokens): cierre por
//           strikes resta 1; conversación legítima (cero strikes + sustancia, tope
//           +1/día) devuelve +1 — redención viva INCLUSO en score 0. Tiers: 3=gracia,
//           2=insistencia directa, 1=primer desvío estrikea, 0=cierre inmediato.
//           Validado E2E en widget: escalera completa 1→2→3 + cierre + reputación 3→2.
//           15 specs candado nuevos (textos firmados pineados byte a byte).
// 69.5.27 — R2 del paquete firmado 6/7-jul: VELO DE TRÁMITES (amnesia por INVISIBILIDAD,
//           reversible). Flag CAPTAIN_TRAMITE_VEIL_ENABLED (InstallationConfig, runtime,
//           default OFF = fila ausente → ESTE DEPLOY NO CAMBIA COMPORTAMIENTO). La memoria
//           SIGUE GRABANDO siempre; el flag gobierna solo la LECTURA (flip OFF → caché y
//           herencia reviven al turno siguiente — probado en vivo). Con el flag ON:
//           (1) historial al LLM velado en el choke point único (collect_previous_messages,
//           las 3 vías): outgoing de caché de estado ENTEROS (hermeticidad total firmada) y
//           el resto con tokens redactados (refs YA consultadas por lookup de auditoría +
//           patrón DUI + refs con prefijo + URLs persistidas) — solo mensajes ANTERIORES a
//           la FRONTERA DUAL firmada (última consulta completada ∩ últimos 5 min); el flujo
//           vigente y el turno actual quedan intactos. (2) state del LLM sin
//           consultas_ya_realizadas, sin assigned_institution y con las claves
//           status_query_* filtradas de los attributes crudos (canal 2-bis). (3) caché A2
//           opción C: turno MIXTO (predicado de consulta ADICIONAL — "¿cómo va el 0200?" es
//           PURO) cede al LLM velado; turno PURO responde canned pidiendo SOLO lo faltante,
//           sin explicar el motivo. (4) herencia de institución NO aplica (se olvida entre
//           trámites — firma #1; el gate institución-primero re-pregunta solo), allowlist de
//           URLs no se rehidrata, y la evidencia de consulta pasada NO va al juez. Candados
//           duales: 260 specs previos VERDES con flag ausente + 13 espejos del velo.
// 69.5.28 — R2 operabilidad: toggle del Velo de Trámites en el panel superadmin de Captain
//           (pestaña Aprendizaje) — el admin lo prende/apaga sin consola ni deploy
//           (InstallationConfig CAPTAIN_TRAMITE_VEIL_ENABLED, runtime). Copy claro:
//           "reversible al instante, no borra nada". Validado en vivo: render OK, save
//           round-trip flippea el flag en BD. Sin cambio de comportamiento del bot.
// 69.5.29 — dos cierres del backlog (contrato firmado 7-jul):
//           (1) TURN-COORDINATOR: el skip del lock del ResponseBuilder ya NO deja un turno
//           SIN RESPUESTA. Antes: si un 2.º job no conseguía el lock, se saltaba y retornaba;
//           si el job con el lock estaba en un turno superado (lo suprime STALE-TURN), el
//           turno nuevo quedaba huérfano. Ahora el job que se salta se RE-ENCOLA con
//           micro-delay (7s, acotado a 3 intentos — backstop anti-loop). Anti-doble-respuesta:
//           marcador Redis answered_turn (uuid-v7, TTL 5min) escrito SOLO al emitir una
//           respuesta REAL; un job re-encolado que encuentre el turno ya respondido, no
//           re-responde. fail-open. Compone con STALE-TURN (que cubre el reverso).
//           (2) AI-GUARD v2 FASE SOMBRA: flag CAPTAIN_AI_GUARD_V2_SHADOW (default OFF). Con
//           v2 aún OFF y la sombra ON, v1 DECIDE y v2 corre en PARALELO solo para observar:
//           deja una línea greppable a WARN "[AI-GUARD-SHADOW] activo_v1=… sombra_v2=…
//           coincide=…" sobre tráfico REAL, sin tocar ni una respuesta. Rescue propio: una
//           falla de la sombra jamás afecta el turno. Junta datos en prod para decidir con
//           evidencia si activamos v2. (refactor: run_guard_judge extraído, comportamiento
//           v1/v2 idéntico — 34 specs previos verdes + 6 de sombra + 6 de turn-coordinator.)
// 69.5.30 — R2 velo, Layer 1V (hallazgo de validación en prod 7-jul): bajo el velo el LLM a
//           veces CONFESABA la amnesia ("no tengo acceso al historial de consultas anteriores
//           — cada conversación comienza desde cero para mí"), violando el requisito firmado
//           "no decirlo implícitamente, solo re-pedir número y DUI". Backstop determinístico
//           en el sanitizer (VELO-GATED: solo con CAPTAIN_TRAMITE_VEIL_ENABLED): AMNESIA_EXPLAIN_RE
//           (no tengo acceso al historial / (cada conversación) empieza desde cero / no recuerdo
//           consultas / no guardo historial) → reemplazo por el pedido de datos LIMPIO, sin
//           explicar el motivo. Nunca toca el flujo con el velo apagado. 5 candados nuevos con
//           la frase EXACTA vista en prod.
// 69.5.31 — R2 velo, Layer 1V ampliado: la 1ª versión cubría frases exactas y el LLM improvisó
//           otra en prod ("no tengo MEMORIA de conversaciones — cada SESIÓN es nueva"). Se
//           amplió AMNESIA_EXPLAIN_RE al ESPACIO SEMÁNTICO completo (memoria/historial/sesión/
//           registro/desde-cero/sin-memoria, múltiples conjugaciones y masc/fem) — deja de ser
//           whack-a-mole de frases. Candados con las DOS frases exactas de prod + 6 variantes.
// 69.5.32 — R4 hueco hallado en RE-VALIDACIÓN prod (7-jul): "que porquería de bot, sos un
//           inútil de mierda" NO disparaba strike — INSULT_INPUT_RE no cubría "inútil",
//           "porquería", "X de mierda" genérico, ni "pedazo de basura". Mensaje corto (<80c)
//           → el InputGuard lo saltea → solo el regex determinístico lo agarra, y no lo hacía.
//           Ampliado al vocabulario común ES-SV directo al bot (inútil/porquería/basura/
//           pendejo/baboso/tarado/asco/"sos un(a) X"/"X de bot"/"pedazo de X") conservando el
//           anti-falso-positivo (frustración legítima "el trámite no me sirve", "qué día de
//           porquería" NO disparan). Candados con la frase exacta de prod + 8 variantes.
// 69.5.33 — Fix cosmético doble despedida al cerrar por strikes (R4): reproducido en prod
//           (conv #811, 8-jul), el 3.er strike emitía DOS despedidas — el CLOSING_MESSAGE
//           firmado del strike + el resolution_message genérico que TODA resolución dispara vía
//           ConversationResolutionListener (content_attributes vacío lo delató). Ahora
//           StrikeService.close! marca la conv (suppress_resolution_message) ANTES de resolved!
//           y el listener cede → sale SOLO el mensaje firmado. Seguridad intacta (strikes/
//           cierre/reputación sin cambios); es puramente de voz.
// 69.5.34 — (1) Anti-reuso de DUI ROBUSTO (conv #817): el velo tapa los DUIs del historial → el LLM
//           los ALUCINA y el guard viejo (reuse_suspected?, comparaba con el ÚLTIMO DUI exacto) los
//           dejaba pasar. Ahora condición POSITIVA: ante CAMBIO de trámite el DUI DEBE venir escrito
//           en el turno del ciudadano (reprovided_for?) — cubre reuso, alucinación, "el mismo de
//           antes" y cualquier variación, sin listas de frases. (2) Mensaje de institución
//           deshabilitada CONFIGURABLE desde cuscasense (assistant.config; precedencia per-asistente
//           → global InstallationConfig → default) + trato corregido a TUTEO ("¿Tienes?" no "¿Tiene?").
// 69.5.34b — refuerzos velo/consulta (convs #818/#820/#822): (3) el status_query RECHAZA el placeholder
//           del velo ([dato de trámite reservado]) si el LLM lo arrastra como DUI o N° de trámite → NO
//           pega al webhook, NO consume cupo, re-pide el dato (mataba los "campos raros" y la basura
//           mostrada al ciudadano). (4) Los ERRORES del webhook NO consumen el cupo de 5/día — solo las
//           consultas REALES cuentan (un "no encontrado" señalizado por error tampoco). (5) La
//           descripción del param identifier deja de invitar "u otro identificador": pide SOLO el DUI.
//           (6) VALIDACIÓN DURA de formato SIEMPRE (conv #818/#820): si la institución NO tiene su regex
//           (SIMPLE tenía identifier/reference_regex ""), cae a un BASELINE alfanumérico que rechaza
//           corchetes/espacios/acentos/símbolos → NUNCA se consulta al webhook con basura. Un regex
//           configurado manda por encima. RECOMENDADO: configurar el regex propio de SIMPLE.
// 69.5.35 — VELO solo tras ÉXITO + fin del loop de re-pedido (conv #829, directiva owner). El velo
//           SOLO debe olvidar un trámite consultado con ÉXITO. (1) exempt_after ahora avanza la
//           frontera SOLO con la última consulta EXITOSA (where.not(result_status: nil)); una
//           consulta no-encontrada/errada YA NO la mueve → deja de tapar datos a medio recolectar
//           (fuente de loops y arrastre del placeholder). Alinea el código con el diseño firmado que
//           ya decía "última consulta COMPLETADA". (2) turn_provides_identifier?: si el ciudadano
//           RE-ESCRIBE el DUI para re-consultar un trámite ya olvidado, el engine cede al LLM velado
//           (re-consulta fresco) en vez de loopear "escribime tu DUI" con el DUI escrito en el mismo
//           turno (réplica 829, 11:03-11:04). El re-mención PURO SIN dato sigue re-pidiendo (firma #4).
//           Specs: tramite_veil 21/21 (+3 nuevos) + status_query 62 + context_guard 8 + gate 18 = verde.
// 69.5.36 — conv #826 + #824 (5 frentes velo/consulta). (1) Layer 1P: el bot ya NUNCA echa el placeholder
//           "[dato de trámite reservado]" — se reemplaza por un re-pedido "por seguridad" limpio (sanitizer,
//           velo-gated); el canned del tool también pasó a "por seguridad". (2) Batch NUNCA ofrece un stack:
//           BATCH_STATUS_MSG/BATCH_VOICE_REPLACEMENT piden UN trámite, sin prometer cola. (3) El mensaje de
//           cupo NO revela el número (RATE_LIMIT_MSG sin dígitos) + regla en assistant.liquid (hay límite,
//           nunca el número, nunca "error", un trámite a la vez). (4) Rate-limit anti-vulneración (regla
//           firmada owner): TODA respuesta del webhook (error/no-encontrado incluido) consume cupo — solo el
//           bloqueo por regex/placeholder (sin webhook) y el timeout no consumen. Revierte el Fix 2 de 34b.
//           (5) conv #824: un RANGO explícito ("del 0100 al 0110") se detecta como lote → un-trámite-a-la-vez
//           SIN dispararle un strike de scope al ciudadano (raíz del desastre de 824). Specs 182/0. Evidencia
//           en docs/826-evidencia/ (prueba determinística + 3 capturas web chat).
// 69.5.37 — conv #827 Layer 1S (anti-oferta-de-stack en prosa). El handler determinístico corta el batch
//           cuando el CIUDADANO lo pide, pero el LLM a veces OFRECE consultar varios trámites en su
//           redacción ("los tres trámites los puedo consultar ahora mismo, ¿quieres que proceda?"). Nuevo
//           backstop en el sanitizer (stack_offer_replacement_for + STACK_OFFER_RE): si el texto ofrece
//           consultar 2+ trámites, se reescribe al pedido de UNO. MUY específico (cuantificador ≥2 +
//           "trámites" + verbo de consulta) para no pisar un resultado de UN trámite ni "otro trámite";
//           velo-gated + no-grounded. Specs tramite_veil (+6) + batch_voice + war_room 97/0.
// 69.5.38 — war room 9-jul, 2 bugs. (A) conv #835 — el gate de institución DESHABILITADA le robaba el
//           turno a una institución HABILITADA nombrada en el mismo texto/contexto: una apagada
//           arrastrada de un turno anterior ("minsal") disparaba "deshabilitada" en un turno sobre
//           "defensoría del consumidor" (que SÍ está habilitada). Fix en handle_disabled_institution: si
//           el texto también nombra una institución captain_available, ESA gana (mismo criterio que las
//           ramas de herencia 886/901). Reproducido en prod (simulación) + spec. (B) conv #837 — LOOP de
//           cierre: "cerrar" no estaba en STANDALONE_CLOSING_WORDS → iba al LLM → AMBIGUOUS → "¿querés
//           cerrar?" → "cerrar" → AMBIGUOUS → loop infinito, nunca cerraba. Fix: "cerrar" y variantes
//           (cierra/cerramos/cerremos/"cerrar la conversación") → :closing directo. La negación "no quiero
//           cerrar" y pedidos largos ("cerrar mi cuenta bancaria") siguen :open. Specs 120/0.
// 69.5.39 — conv #841 (war room): "direccion de MH"/"contacto de mivi" con teams HABILITADOS y KB
//           presente devolvían "no tengo ese dato". Cadena raíz verificada con logs+DB+embeddings de
//           prod: el agente V2 solo emitió un handoff INTERNO (handoff_to_<x>_agent, ruteo del
//           framework) sin llamar faq_lookup_team → respondió el contacto de memoria → el guard
//           anti-alucinación lo suprimió (bien) → PERO el KB-backstop no rescataba por 2 razones:
//           (1) el pseudo-tool interno vetaba el backstop (no_tools_called? lo contaba como tool
//           real); (2) la query con SIGLA embebe lejos de las FAQs (nombres completos) — medido en
//           prod: "el contacto de mivi"=0.571 (≥0.45=vacío) vs expandido=0.302 (rescata); MH
//           0.611→0.318; DC 0.564→0.299; typo "rieccin de mivi" 0.677→0.404 (4/4). Fixes: (A)
//           no_tools_called? ignora los handoffs internos (tools reales siguen vetando); (B)
//           expand_institution_names: sigla/alias → nombre descriptivo del team (data-driven por
//           cuenta, word-boundary, acento-insensible) SOLO en la query del backstop — el
//           kb_confidently_empty? del guard queda CRUDO a propósito (nunca se debilita la supresión
//           de contactos inventados). Specs 14 nuevos + agent_runner 114/0.
// 69.5.40 — conv #840 (war room): la transferencia decía "te transfiero ahora" y NO ejecutaba nada
//           (assign_team REJECTED). Cadena raíz (logs prod + réplica E2E local): (1) el ciudadano
//           respondió el "¿sobre qué tema?" con "Perfecto, PÁSALE al agente que necesito el estado de
//           los trámites…" — HUMAN_REQUEST_RE no cubría los CLÍTICOS ("pasa(?:me|r)?" ≠ "pásale") →
//           el gate lo trató como trámite puro y ABANDONÓ el handoff → batch guard bloqueó el tema.
//           Fix: clíticos pasale/pasalo/pasala + dile/decile en el resolver. (2) la confirmación
//           EXPRESA "Sí, autorizo que compartas esos datos… ¿el agente tiene límite?" caía al veto T7
//           (pregunta a cuestas) → judge → REJECTED → el LLM prometía en falso. Fix: prefijo
//           explícito ("sí"+autorizo/confirmo/acepto/procede…) acepta y EJECUTA determinístico; el
//           T7 para acks débiles ("ok y ¿cómo va el mío?") queda intacto. Las 2 notas "El ciudadano
//           solicita…" que se veían en el dashboard son NOTAS PRIVADAS del AddPrivateNoteTool (el
//           ciudadano nunca las vio — no es fuga). + 69.5.40b (réplica local 841 web chat): el guard
//           ANTI-ALUCINACION (embedding) suprimía respuestas GROUNDED (faq con 5 resultados por
//           keyword-fallback) porque kb_confidently_empty? con la sigla cruda daba vacío FALSO. Fix:
//           un turno grounded JAMÁS se suprime (rag_grounded==false requerido) + kb_confidently_empty?
//           usa la MISMA query expandida que el backstop (coherencia). Validado E2E en web chat local
//           con chats frescos: 840 (pásale→confirmación→"Sí, autorizo…+pregunta"→ENCOLADO team=simple
//           queued_at+confirmed) y 841 ("contacto de mivi"→contacto real del KB). Specs 210/0.
//           + 69.5.40c (réplica E2E batería): el R5 del backstop (aviso de institución deshabilitada)
//           TAMBIÉN cede ante una institución HABILITADA nombrada en el mismo mensaje (la habilitada
//           gana, mismo criterio que el gate del engine): "dirección de minsal Y de la defensoría del
//           consumidor" embebía más cerca de una FAQ de minsal apagada y el aviso canónico se robaba
//           el turno entero. enabled_institution_named_in? (primitivas Institution::Extraction) → nil
//           (turno del LLM). Batería E2E local por el pipeline real, chats frescos: 841-mivi, 841-MH,
//           837-cerrar (follow-up fiel → resolved sin loop), 835-DC y 840-transferencia = 5/5 PASS.
// 69.5.41 — conv #1318 (prueba de SIGLAS en web chat): "el contacto de MIVI" servía el contacto del
//           Ministerio de SALUD (Calle Arce 827 / 2591-7000 / salud.gob.sv) por Vivienda. Causa raíz:
//           el KB-backstop expande la sigla (MIVI→"Ministerio de Vivienda", 69.5.39) y busca
//           assistant-wide; como Vivienda/Hacienda no tienen FAQ de contacto fuerte propia, la FAQ
//           canónica de minsal (Salud) embebía más cerca (0.419) y el backstop servía SALUD. NO era
//           anti-bleed (todas ON) sino MISROUTE de institución. Fix: named_enabled_team_ids +
//           SCOPE del backstop — si el mensaje nombra una institución habilitada, el match SOLO puede
//           ser de esa institución (o General/nil compartido); si la nombrada no tiene match
//           confiable, nil (→ "no tengo") en vez de la equivocada. La vía primaria faq_lookup_team
//           (LLM) sigue sirviendo la institución correcta (DC-sigla verde en vivo). Verificado con el
//           método REAL kb_backstop_answer: MIVI/MH/DC → nil (ya no Salud). Specs 26/0 (2 candados
//           nuevos anti-misroute) + agent_runner 64/0.
// 69.5.42 — conv #853 (investigación 10-jul): el LLM ANUNCIÓ "Transfiriendo a otro agente para mayor
//           asistencia" pero NO ejecutó ningún handoff (rt=nil, cero audits, team/assignee nil → el
//           ciudadano esperando a nadie). La transferencia SIEMPRE la ejecuta el gate/los tools de
//           handoff, NUNCA la prosa libre del LLM (misma clase que #840/#747, pero suelta en el texto
//           del modelo). Nota: el turno cayó al LLM porque el toggle de dc (que acababa de deshabilitar
//           en el test) aún no se había propagado al worker — prod lee teams de la RÉPLICA (lag) +
//           account cache 5min; el guard de deshabilitada en sí está OK (probado determinístico). Fix:
//           nuevo guard anti-FALSA-PROMESA en process_agent_result — si la respuesta ANUNCIA una
//           transferencia a un humano (gerundio/progresivo/futuro-inmediato/perfecto: "transfiriendo",
//           "te estoy conectando", "voy a transferirte", "te he conectado") y NO se ejecutó un handoff/
//           assign REAL este turno (excluye los handoff_to_* internos del framework), se reemplaza por
//           una OFERTA honesta que sí rutea por el gate el próximo turno. Gateado igual que los otros
//           anti-alucinación (rag_grounded==false + sin canned de tool). El regex EXCLUYE a propósito
//           ofertas/preguntas ("¿que te transfiera?", "si querés te paso") → cero falsos positivos.
//           Specs 11 nuevos + agent_runner+gate+war_room 161/0.
// 69.5.44 — Base de Conocimiento (pedido 10-jul): la caja de búsqueda no funcionaba bien — el backend
//           hacía ILIKE de la FRASE ENTERA como substring ("direccion vivienda"=0 resultados,
//           "contacto defensoria"=0 porque la KB dice "Defensoría" con tilde y el orden no coincidía).
//           Fix backend (assistant_responses_controller#apply_text_search): buscador MASIVO por
//           PALABRAS (AND entre palabras, cada una matchea pregunta O respuesta, orden-independiente)
//           e insensible a TILDES en ambos lados vía translate() (sin extensión unaccent; ~1k filas,
//           instantáneo). sanitize_sql_like escapa %/_; tope 8 palabras. Medido: "direccion vivienda"
//           0→4, "contacto defensoria" 0→16, "teléfono hacienda" 0→3. Fix frontend (responses/
//           Index.vue): el input crudo se reemplaza por el MISMO componente Input compartido del
//           Historial Ciudadano (ContactHeader: prefix icon + clear + estilos). Validado E2E en el
//           dashboard local (GOES): "direccion vivienda"→4 elementos, "contacto defensoria"→16.
//           Specs 5 nuevos (multi-palabra/orden/tildes/AND/escape de comodines) con admin (el resto
//           del archivo spec está roto PRE-existente por la factory captain_document vs plan limits).
// 69.5.43 — conv #856 (10-jul): con minsal HABILITADA (audits: ON desde 9-jul 21:09, cero cambios),
//           la respuesta al "¿sobre qué tema?" para el agente de minsal ("quiero saber sobre
//           enfermedades virales") recibía el canned de DESHABILITADA. Causa: el sticky de
//           deshabilitada (#768, recent_disabled_context_team) escaneaba los últimos 5 incoming
//           buscando SOLO apagadas — encontró "agente de MH" (13:47, apagada) e IGNORÓ que el
//           ciudadano ya se había RE-ANCLADO a minsal ("bueno con un agente de minsal", 13:48,
//           habilitada, con el propio bot ofreciendo la transferencia). Fix: EL ANCLA MÁS RECIENTE
//           MANDA (misma filosofía #835): escaneando del más nuevo al más viejo, un mensaje que
//           nombra una institución HABILITADA rompe el sticky (return nil) antes de que una apagada
//           más vieja lo dispare; dentro del mismo mensaje, la habilitada gana. El sticky #768
//           (contexto solo-apagada) queda intacto. Nota: la transferencia de #856 al final SÍ se
//           ejecutó (encolada 13:52) — el bug era el canned falso intermedio. Specs 4 candados
//           nuevos (réplica #856 unit+integrada, #768 intacto, habilitada-gana) + trámites 248/0.
// 69.5.45 — ANÁFORA DE DESTINO (conv #856/#1326, diseño global por ESTADO — pedido del usuario:
//           sin regex por frase). Un pedido de HUMANO a institución APAGADA dejaba morir la intención:
//           el siguiente "bueno con un agente de minsal" iba al LLM → assign_team REJECTED → promesa
//           falsa (o el canned genérico del guard 42). Ahora: (1) handle_disabled_institution persiste
//           "destino pendiente" (TTL de oferta) si el turno bloqueado era human_request; (2) el gate
//           (resolve_pending_destination!) re-ancla cuando el ciudadano nombra una institución
//           HABILITADA — via Institution::Extraction + captain_available? (dueños únicos) — y retoma
//           check_or_request! (tema → confirmación → cola); con consulta a cuestas va como TEMA
//           (KB-help + SÍ/NO). Veto #1148 (trámite jamás se ata a handoff) + apagada re-nombrada deja
//           el pending vivo. (3) Hook: rama AWAITING_TOPIC faltante (el gate ya preguntó; el LLM no
//           duplica). Validado E2E web chat local: MH apagada → "Bueno con un agente de minsal" →
//           pregunta de TEMA → confirmación → ENCOLADA (team=minsal, confirmed). Specs 8 nuevos +
//           gate/golden/engine/closing 128/0.
// 69.5.46 — SIGLA→ESCENARIOS BIEN ESTABLECIDOS (feedback 10-jul, réplica MIVI). Tres eslabones
//           determinísticos sobre el catálogo cerrado (edbc1f1b2): (1) reconocimiento DIRIGIDO —
//           si el LLM dice "no tengo información sobre X"/pide el nombre completo pero la sigla
//           del ciudadano SÍ está en el catálogo, se reemplaza por "Te refieres a <nombre>, ¿verdad?"
//           y se marca la institución como ACLARADA (TTL 5 min); (2) backstop ACLARADO — el follow-up
//           sin institución ("si, el contacto porfa") re-embebe la consulta con el nombre descriptivo
//           y scopea el KB-backstop a la institución aclarada; (3) guard de contacto ENDURECIDO —
//           se elimina kb_confidently_empty? del guard anti-contacto: con la expansión de siglas la
//           KB "no vacía" dejaba pasar contactos INVENTADOS del entrenamiento (caso real: "contacto
//           de MIVI" → El Pedregal/FONAVIPO/2248-7300, 0 FAQs con esos datos). Sin grounding el LLM
//           no recibió NINGUNA FAQ ese turno → teléfono/URL se suprime SIEMPRE (el backstop scoped +
//           expandido + aclarado ya tuvo su oportunidad de servir el dato real antes). Validado E2E
//           web chat local: "necesito el telefono de MIVI" → faq_lookup team 'mivi' (5 FAQs) →
//           contacto REAL del KB (2280-9800, KM. 5 1/2 Santa Tecla) textual. Specs catálogo 11 +
//           suite agent_runner 89/0.
//           + 69.5.46b (prueba de 0 en chat nuevo, mismos hallazgos): (4) CONTINUIDAD DE
//           INSTITUCIÓN — "y en que horario atienden?" tras hablar de MIVI iba al backstop GLOBAL
//           y servía la FAQ de horarios de OTRA institución (minsal). Ahora la institución del
//           hilo persiste (marca aclarada, TTL 5 min) desde DOS fuentes: faq_lookup exitoso
//           (mark_kb_continuity! en el tool) y el backstop cuando el ciudadano nombra UNA
//           habilitada (aunque no rescate); el follow-up sin institución scopea a la marcada —
//           jamás contenido cross-institución. (5) REPETICIÓN LEGÍTIMA — "dame de nuevo el
//           contacto" repetía el dato del historial sin re-llamar faq → el guard endurecido lo
//           suprimía; ahora si TODO teléfono/URL de la respuesta ya fue servido por el bot en
//           este hilo (comparación por tokens: teléfonos por dígitos, URLs normalizadas), es
//           repetición verificada y pasa. Validado E2E web chat local (chat nuevo): contacto
//           grounded → "y tienen sitio web?" suprimido honesto (la KB de mivi no lo tiene) →
//           "me repites el contacto" → REPETIDO servido → "y los horarios del MIVI?" scoped
//           (sin fuga de minsal) → "y atienden los sabados?" → ACLARADA 'mivi' + repetición.
//           Specs: continuidad tool 3 + backstop re-ancla 3 + repetición 2; suite 94/0.
//           + 69.5.46c (reintento web chat pedido por el usuario): (6) R5 FALSO-DESHABILITADA —
//           en hilo anclado a mivi, "y en que horario atienden?" sin match scoped caía al R5 y
//           la FAQ de horarios de minsal (HABILITADA, dist 0.401) disparaba el aviso de
//           "institución deshabilitada": el find de apagada asumía "fuera de eligibles = apagada",
//           supuesto roto por el scope #1318/aclarada que también excluye OTRAS habilitadas.
//           Fix doble: (a) el R5 exige DESHABILITADA de verdad (enabled_team_ids.exclude?);
//           (b) con el hilo ANCLADO a una habilitada (nombrada/aclarada), el aviso de una
//           tercera apagada tampoco aplica — el ancla manda, turno del LLM. R5 sin ancla
//           intacto (spec de regresión). Validado E2E chat nuevo: contacto+horario de MIVI
//           servidos GROUNDED textuales (la KB tenía el horario: L-V 7:30-15:30 + regionales).
//           Specs R5-scope 3; suite 97/0.
//           + 69.5.46d (feedback réplica #856): (7) LA KB MANDA: GENERALIZA — "Que tipo de
//           informacion ofrece la DC" respondía "no tengo información específica" SIN llamar
//           faq en todo el turno, con 212 FAQs approved de la DC en la KB. Doble capa:
//           (a) prompt-catálogo: PROHIBIDO decir no-info de una institución del catálogo sin
//           consultar su KB; ante pregunta general, consultar y RESUMIR temas; (b) garantía
//           determinística kb_topics_index — no-info + institución del catálogo nombrada +
//           faq NO intentado este turno (si consultó y no había, el no-info es honesto y pasa)
//           → ÍNDICE de temas reales (questions de sus FAQs approved, top 5 por cercanía a la
//           consulta re-embebida) + marca de continuidad; los reemplazos DIRIGIDOS (alucinación
//           de catálogo / sigla no reconocida) también sirven el índice cuando hay KB. Validado
//           E2E: "Que tipo de informacion ofrece la DC" → "Sí atiendo defensoría del consumidor.
//           …puedo ayudarte, por ejemplo, con: [5 temas reales]" → "que es un aviso de
//           infraccion?" → FAQ real servida. Specs índice 3; suite 100/0.
//           + 69.5.46e (feedback final): GENERALIZACIÓN REAL — los temas del índice son los
//           NOMBRES DE LOS DOCUMENTOS de origen de las FAQs ("Ventanillas de atención",
//           "Tribunal Sancionador", "Urbanismo y construcción"…), no questions crudas, y el
//           wording es ciudadano (sin "base de conocimiento"). Dos modos: faq NO intentado →
//           "Sí, atiendo {X} 😊 Puedo ayudarte con información sobre: …"; faq intentado sin
//           match (dato puntual inexistente) → HÍBRIDO honesto "Ese dato específico no lo
//           tengo… Sobre {X} puedo ayudarte con información de: …". El índice marca GROUNDED
//           (es data textual de la KB): sin eso el AI-Guard L2 bloqueaba la lista como
//           off-topic → strike diplomático injusto (visto en réplica). Deshabilitadas JAMÁS
//           reciben índice (matcher solo habilitadas + canned del engine antes). Validado E2E
//           local: DC (212 FAQs/20 docs) índice+híbrido, MIVI (48/5) índice por sigla, MH
//           apagada → canned sin fuga; réplica #856 completa 9/9 con encolada real y Salir.
//           Sin regresión: suite agent_runner 102/0; gate×4+golden+engine+status 160/0;
//           captain 811 (65 fallos = periferia con diff VACÍO vs prod = deuda pre-existente).
// 69.5.46f — TODO VERDE + flancos conversacionales (10-jul, cierre del paquete):
//           (1) MOTOR DE TRÁMITES 590/0 — specs stale actualizados al contrato real (handoff_tool
//           al candado NO_DESTINATION_CONFIRMED rev55/61; faq_lookup_team a pgvector real +
//           anti-enumeración 69.5.41; configs controller a Pundit 403 + timeout_seconds); el
//           check CSRF custom ahora honra allow_forgery_protection (arregla TODO request spec
//           mutante del repo — deuda transversal); BUG REAL cazado por el spec de asociaciones:
//           Captain::StatusInquiry#conversation resolvía el MÓDULO Captain::Conversation (namespace
//           de los jobs) en vez del modelo → ArgumentError en runtime; class_name '::Conversation'.
//           (2) FLANCOS E2E (pipeline real): off-topic→escalera diplomática, injection→strike
//           role_play sin fuga, trámite→slot+regex de formato+webhook real honesto+DUI sin censura,
//           KB grounded, handoff ancla y pide tema, despedida→cierre canónico. DOS falsos positivos
//           del juez cazados y cerrados: (a) repregunta legítima del bot ("¿Me podrías compartir tu
//           ubicación?") bloqueada → 3.er strike injusto → verbos de petición ampliados en el bypass
//           rev53 (candado estructural intacto); (b) cortesía de despedida ("De nada, con gusto…")
//           bloqueada → bypass TURNO SOCIAL: si el mensaje del ciudadano clasifica despedida/saludo/
//           agradecimiento en la biblioteca de intenciones (misma señal del engine), el juez no
//           castiga la cortesía — evaluado solo en block, off-topic reales intactos. Sanitizer 51/0.
// 69.5.47 — BÚSQUEDA NÍTIDA en la Base de Conocimiento (queja de usuarios: "todo parpadea
//           al terminar de escribir y el puntero se sale de la caja"). Dos causas: (1)
//           shouldShowDropdown dependía de !isFetching → cada búsqueda DESMONTABA el bloque
//           de controles completo (incluida la caja) → foco perdido + flash del input; (2)
//           PageLayout con is-fetching desmontaba la lista entera y mostraba el Spinner de
//           página en cada refetch → parpadeo total. Fix (patrón estándar de buscadores):
//           hasLoadedOnce — el spinner de página SOLO en la primera carga; después los
//           controles quedan montados SIEMPRE, la lista previa permanece visible mientras
//           llega la nueva (Vue diffea las cards) y un spinner sutil DENTRO de la caja
//           (lado derecho) indica la búsqueda en curso. El debounce de 300ms existente pasa
//           a sentirse como live-search real. Validado E2E en el dashboard local: tipeo en
//           dos tandas con fetches intermedios → activeElement permaneció en el input con
//           el texto completo ("direccion vivienda"), resultados 1048→52→4 refinándose en
//           vivo, cero parpadeo.
// 69.5.48 — conv prod #861 (13-jul): con la oferta de transferencia ANCLADA A DC esperando
//           SÍ/NO, "me gustaria que me atienda un agente de MIVI" encolaba a DC — la
//           institución EQUIVOCADA. Cadena raíz: el guard de cambio-de-institución mid-oferta
//           existía, pero su extractor (GateUserStrict P3) exigía nombres de 5+ chars y
//           descartaba alias de una palabra → la sigla 'mivi' (4 chars) era INVISIBLE → el
//           mensaje caía a la rama del #767 (pedido de humano = aceptación de lo ofrecido) y
//           confirmaba a DC. Fix doble: (1) P3 acepta SIGLAS (2-4 chars, name/display/alias de
//           una palabra) por PALABRA COMPLETA sobre el texto reference-stripped del engine —
//           "agente de MIVI" re-ancla; "MIVI-2024-000777" (referencia) sigue sin contar; el
//           anti-falso-positivo del gap #748 ("vivienda social") intacto porque los alias de
//           una palabra LARGOS siguen descartados. (2) Al cambiar de institución mid-oferta,
//           el tema guardado NO se arrastra (era de la institución anterior): la confirmación
//           va limpia ("¿Quieres que te pase con un agente de X? SÍ o NO"). Validado E2E local
//           réplica exacta del 861: awaiting_confirmation(DC) + "agente de MIVI" → re-oferta a
//           MINISTERIO DE VIVIENDA → "si" → ENCOLADA A MIVI (conv_team=mivi, confirmed=mivi).
//           Specs: réplica 861 4 (re-ancla, apagada, #767 intacto, misma institución acepta) +
//           caracterización P3 siglas 3; gate+golden+engine+status 229/0.
// 69.5.49 — conv prod #876 (13-jul): "Hola me ayudas con un agente de minsal" respondía el
//           CONTACTO de Salud (KB) en vez del flujo de oferta de transferencia (→ y en prod con
//           auto-asignación smart + agente online, la conv #875 se transfería DIRECTO sin
//           oferta/tema/confirmación). Bug PRE-EXISTENTE (probado: idéntico en 69.5.47 revertido;
//           el .48 tocó SOLO gate+GateUserStrict, no el motor). Causa RAÍZ (no un verbo faltante):
//           la biblioteca de intenciones semántica media la INTENCIÓN por embedding, pero la
//           mención institucional ARRASTRA el vector — "…agente de minsal" quedaba a 0.383 del
//           cluster institucion/info y 0.397 del handoff/humano (threshold 0.35 → intent nil →
//           LLM → KB). Fix ELEGANTE (sin regex de frases): perfil P6 Neutralize — antes de embeber
//           para la biblioteca, las menciones del catálogo (name/display/alias, data-driven por
//           cuenta, insensible a acentos, respeta reference-strip) se reemplazan por "la
//           institución"; SIMÉTRICO query↔ejemplos (IntentExample.match + update_intent_embedding).
//           Así el vector mide intención sin sesgo de nombre propio → generaliza a CUALQUIER
//           verbo/institución. + seeds handoff/humano (8) e institucion/info-contacto (6) con el
//           placeholder + backfill de embeddings neutralizados (seeder idempotente, replicable a
//           prod). Validado E2E web chat local: "me ayudas con un agente de minsal" → oferta →
//           tema (KB-help Esquema Vacunación 2025) → "si" → ENCOLADA a minsal; no-regresión: "el
//           contacto de MIVI?" sigue KB (no handoff), "que es el minsal?"→info, tramite→estado.
//           Specs caracterización P6 (7) + intent/gate/engine 191/0.
// 69.5.50 — conv prod #881/#883 (13-jul): DESASTRE de dos fallos encadenados en respuestas de KB.
//           (1) TRANSFERENCIA FANTASMA — al servir una respuesta de KB el backstop asignaba el team
//           (assign_team_from_backstop_match!, existe desde 69.3.3), y ese cambio de team disparaba
//           la auto-asignación smart del inbox → con un agente online (Liliana, auto_offline=false =
//           online permanente) conectaba "Te he conectado con un agente humano" SIN que nadie lo
//           pidiera, a veces ANTES de responder. NO es regresión de código (el backstop-asigna-team
//           es viejo); lo destapó el agente fijado online. Fix: Current.captain_bot_kb_routing — el
//           bot asigna team SOLO para rutear KB; AssignmentHandler NO auto-asigna agente por ese
//           cambio. La transferencia a humano es SOLO por el gate explícito (oferta→confirmación).
//           (2) CROSS-INSTITUCIÓN — "Ofertas de Empleo del Ministerio de Trabajo" (institución NO
//           configurada) → el backstop servía la FAQ de "Ofertas de Empleo" de SALUD (dist 0.352).
//           Fix (regla del usuario: basarse ENTERAMENTE en la lista de instituciones activas): si el
//           mensaje nombra una institución FUERA del catálogo y ninguna del catálogo → el backstop
//           devuelve nil → el LLM responde honesto ("¿me indicas el nombre completo…?"). Validado
//           E2E web chat local: "Ofertas de Empleo del Ministerio de Trabajo" → pide aclaración, sin
//           FAQ de Salud, sin transferencia. Specs: frontera catálogo 3 + flag auto-assign 2; suite
//           agent_runner/gate/auto-assign 32/0 (los 2 fallos de round_robin son deuda pre-existente).
// 69.5.81 — SE ELIMINA LA BIBLIOTECA SEMÁNTICA DE INTENCIONES del camino de decisión.
//           Convs #1517/#1518: "soy gay me puedes atender" → el bot ofrecía CONECTAR CON UN
//           AGENTE en vez de atender. La biblioteca decide ANTES de que cualquier prompt vea el
//           mensaje, así que ninguna regla de comportamiento podía corregirla.
//           Medido: el daño lo ponía 100% ella (5/8 formulaciones dañinas → handoff, la peor a
//           dist 0.147); el núcleo léxico acertó 8/8 porque EXIGE sustantivo humano explícito.
//           Su "cobertura extra" era un espejo: de 3 frases legítimas que el léxico no cubría, 2
//           eran sus propias semillas (0.0 contra sí mismas) y la 3.ª ni matcheaba. La única real
//           se mudó a HUMAN_REQUEST_RE como existencial (hay/existe/habrá/hubiera/tienen).
//           Supersede el piso de confianza 0.25 de 38bdf9800 (#2266): ese piso corta 3 de las 6
//           formulaciones dañinas y deja pasar las TRES MÁS CERCANAS (0.147/0.191/0.198). El daño
//           no está en la cola de baja confianza — está en el centro del cluster, y por eso
//           ningún umbral lo separa. Se conserva su hallazgo (derivar pesa más que responder) y
//           su caso de regresión ("necesito informacion del MINSAL" NO deriva).
//           `social_turn?` reimplementado léxico (deja de gastar un embedding por turno).
//           La tabla captain_intent_examples, su modelo, seeder y CRUD de super_admin QUEDAN
//           INTACTOS — se quitaron los consumidores, no el almacenamiento. Ver
//           docs/biblioteca-intenciones/README.md para la ruta de reimplementación.
//           Verificado: decisión 15/15, social_turn? 9/9, suite dirigida 89/89, barrido Captain
//           1251 ejemplos con las MISMAS 27 fallas que HEAD limpio (probado en worktree), y E2E
//           real conv #1520 → "Claro que sí, con mucho gusto te atiendo".
// 69.5.87 — EL ESFUERZO DE RAZONAMIENTO LLEGA A TODOS LOS PROVEEDORES (+ gemini disponible).
//           HALLAZGO EN PRODUCCIÓN (29-jul, medido en aks-wtgoes-stg, no supuesto): el
//           escalonado de 69.5.79 (weak→minimal, solid→medium, complex→high) se inyectaba en UN
//           solo lugar y con UNA sola condición: `model.start_with?('gpt-5')`. Pero el tramo
//           COMPLEX —el ÚNICO que pide `high`, el de los trámites— corre en claude-sonnet-5 por
//           Azure AI Foundry, que sale por el provider :anthropic y NO pasa por ese payload.
//           124 turnos COMPLEX en 2.4h con el `high` calculado, logueado, guardado en Redis…
//           y descartado antes de salir a la red. El panel ya lo documentaba desde 69.5.79.
//           Azure 24h: oai-wtgoes-stg 599 llamadas (gpt) · aif-wtgoes-stg 170 (claude) — o sea
//           ~22% del tráfico iba a un modelo sin control de profundidad.
//           FIX: nueva Captain::Llm::ReasoningCapabilities — TABLA DE MEDICIONES, no condiciones
//           escritas a mano. Dos dialectos, dos perillas:
//             · OpenAI-compatible → `reasoning_effort` (gpt-5.x hasta 5.2, y AHORA gemini)
//             · Anthropic nativo  → `output_config.effort` (nuevo CaptainAnthropicReasoningEffortPayload)
//           MEDIDO con tools en el payload (reasoning_tokens por nivel minimal/low/medium/high):
//             gpt-5.1              0 / 56 / 65 / 114     razona salvo en minimal
//             gemini-3.5-flash   172 / 68 / 150 / 274    razona en los cuatro
//             gemini-3.1-flash-lite 139/115/263/251      razona en los cuatro
//             claude-sonnet-5    400✗/ 0 / 0 / 0         NO razona por el dialecto OpenAI
//           claude por gateway (llmapi→bedrock) RECHAZA 'minimal' con 400 y da CERO reasoning
//           en todos los niveles → por ese dialecto NO es capaz, y así queda declarado.
//           En cambio por Foundry (su camino REAL) SÍ: effort low ⇒ 53 tok sin bloque thinking
//           (1.33s); medium 107; high 112; xhigh 135. `budget_tokens` da 400 y el propio error
//           nos dio la perilla correcta. NO apagamos el pensamiento: thinking=disabled midió
//           4.92s y 397 tokens contra 2.02s y 110 del default — apagarlo sale MÁS caro y MÁS
//           lento. El ladder de Anthropic no tiene 'minimal': weak se traduce a 'low'.
//           + GEMINI DISPONIBLE en los selectores (gemini-3.5-flash, gemini-3.1-flash-lite) con
//           CANDADO ANTI-404: Azure NO sirve Gemini (catálogo medido con `az cognitiveservices
//           model list`: Cohere, DeepSeek, FLUX, Kimi, Llama, Ministral — Google no está), así
//           que con el proveedor en Azure el <select> los deshabilita Y el controlador rechaza
//           el guardado. El guard mira el proveedor QUE VA A QUEDAR, no el actual: pasar a
//           llmapi y elegir gemini en el mismo submit es válido.
//           + CAPTAIN_REASONING_EFFORT expuesta en el panel (antes el código la leía pero no
//           tenía entrada en el esquema → no era editable desde super admin).
//           + Bug de esquema: `gpt-5.2` estaba DUPLICADO en las opciones de CAPTAIN_COMPLEX_MODEL
//           (la segunda pisaba a la primera).
// 69.5.88 — EL CLASIFICADOR TAMBIÉN. Mismo error de 69.5.87, otro camino.
//           69.5.87 arregló el esfuerzo en el path del AGENTE, pero el CLASIFICADOR —que corre
//           en CADA mensaje— usa otro stack (ruby-openai, `build_chat_params`), y ahí el esfuerzo
//           se inyectaba DENTRO del `if reasoning_model?`, un regex de familia
//           (/\Agpt-5|\Ao[134]/). gemini caía al `else` y se quedaba sin esfuerzo, exactamente
//           como le pasaba a claude en el agente. Ahora la condición es ADITIVA
//           (`reasoning_model?` OR la tabla de capacidades), así ningún modelo que hoy recibe
//           esfuerzo lo pierde y sólo se SUMAN los medidos.
//           + gemini-3.5-flash y gemini-3.1-flash-lite agregados a CAPTAIN_CLASSIFIER_MODEL
//           (faltaban: 69.5.87 sólo los puso en SOLID y COMPLEX). Opciones re-etiquetadas con
//           quién razona y quién no, que es la regla del owner.
//           MEDIDO contra el gateway antes de escribir una línea (goes-dev, 29-jul):
//             · `temperature` + `reasoning_effort` JUNTOS ⇒ 200 en los dos gemini. NO replican
//               el rechazo de OpenAI nativo, así que a gemini NO se le borra la temperature.
//             · gemini razona incluso SIN el parámetro (149 tok el lite, 377 el flash).
//             · `response_format: json_schema strict` ⇒ 200 y RESPETA el schema en ambos. Esto
//               valida el camino del AGENTE para gemini, que el código venía ASUMIENDO desde el
//               synthetic model (supports_structured_output=true para todo lo no-claude/ollama)
//               sin que nadie lo hubiera medido.
//             · tools + schema + effort ⇒ 200 y llama la tool correcta.
//           Validado: 28/28 sobre `build_chat_params` real, incluida la no-regresión de gpt-5-mini.
// 69.5.89 — GEMINI ROMPÍA EN SILENCIO: `strict` DENTRO del schema.
//           SÍNTOMA REPORTADO desde goes-dev: "no siempre manda el modelo que seleccioné en
//           Solid y Complex". El gateway mostraba gateway_error 400, 27ms, 0 tokens:
//             Invalid JSON payload received. Unknown name "strict" at
//             'generation_config.response_schema': Cannot find field.
//           RAÍZ: `RubyLLM::Schema#to_json_schema` mete `strict: true` ADENTRO del objeto
//           schema, y ruby_llm lo pasa tal cual a response_format.json_schema.schema
//           (providers/openai/chat.rb:25-35). El código YA limpiaba eso… pero detrás del gate
//           `RubyLLM.config.openai_api_base.include?('anthropic')`, así que sólo protegía a
//           Anthropic. gemini sale por el gateway y recibía el campo.
//           Por qué no explotaba a la vista: el 400 lo tragaba el fail-soft del motor, así que
//           el turno seguía — simplemente SIN el modelo elegido. Falla silenciosa.
//           FIX: la limpieza de `strict` dentro del schema pasa a ser INCONDICIONAL. El
//           `strict` a nivel json_schema (hermano de schema) se CONSERVA — ahí es el campo
//           legítimo de OpenAI, y Anthropic compat lo exige.
//           MEDIDO contra el gateway con el schema REAL del agente (Captain::ResponseSchema):
//             modelo                  con strict adentro   sin strict
//             gemini-3.1-flash-lite   400                  200
//             gemini-3.5-flash        400                  200
//             gpt-5.1                 200                  200   (le da igual)
//             claude-sonnet-5         200                  200   (le da igual)
//           Quitarlo arregla gemini y no le cambia nada a nadie más.
//           Validado: 15/15 sobre el módulo REAL prependeado a un doble, en los tres destinos
//           (gateway / Azure OpenAI / Anthropic), verificando además que additionalProperties y
//           las propiedades quedan intactos y que sólo Anthropic pierde `temperature`.
//           LECCIÓN DE MEDICIÓN: la sonda anterior había dado 200 porque yo mandaba `strict`
//           sólo al NIVEL de json_schema, no adentro del schema — o sea, medí una aproximación
//           mía en vez del payload real de la gema. Una prueba que reimplementa lo que prueba
//           no prueba nada. Esta vez el payload sale de Captain::ResponseSchema, no de mi
//           cabeza. Mismo motivo por el que el "clasificador con gemini OK" previo era falso:
//           pegaba a Azure (404) y el fail-soft devolvía nil ⇒ el motor caía a :solid.
// 69.5.90 — EL SELECTOR DEJA DE MENTIR, Y EL CLASIFICADOR PUEDE SER EL MISMO MODELO.
//           HALLAZGO EN PRODUCCIÓN (medido con llamadas REALES y el payload final capturado
//           DENTRO del flujo de la app, en ProviderResolver.azure_normalize_params!):
//             el panel decía  CAPTAIN_CLASSIFIER_MODEL = gpt-4.1
//             la red ejecutaba gpt-5-mini, SIN reasoning_effort, con tope 800
//           Porque gpt-4.1 NO está desplegado en Azure (deployments reales: gpt-5.2, gpt-5.1,
//           gpt-5-mini, text-embedding-3-large) y la red de seguridad de 69.5.70 lo SUSTITUYE.
//           O sea: corría un modelo de razonamiento sin decirle cuánto razonar, y con la mitad
//           del presupuesto. De las 4 opciones que ofrecía el clasificador, 3 se sustituían.
//           Tabla medida (PEDIDO -> EJECUTADO | effort | tope):
//             gpt-4.1      -> gpt-5-mini | nil   |  800     <- lo que había
//             gpt-4.1-mini -> gpt-5-mini | nil   |  800
//             gpt-5-nano   -> gpt-5-mini | low   | 1600
//             gpt-5-mini   -> gpt-5-mini | low   | 1600
//             gpt-5.1      -> gpt-5.1    | low   | 1600     <- lo correcto
//             gpt-5.2      -> gpt-5.2    | low   | 1600
//           (1) gpt-5.1 y gpt-5.2 agregados a CAPTAIN_CLASSIFIER_MODEL, y el default pasa a
//               gpt-5.1: con Azure el criterio es UN SOLO modelo generalista para los tres
//               tramos, y lo único que cambia es el esfuerzo (clasificador low por
//               CLASSIFIER_EFFORT, solid medium, complex high). Hasta acá gpt-5.1 estaba en
//               Solid y en Complex pero NO en el clasificador — no se podía elegir.
//           (2) El <select> ahora etiqueta cada opción con lo que REALMENTE corre en el
//               ambiente activo: "⚠️ en Azure corre gpt-5-mini" cuando hay sustitución, y
//               "⛔ no disponible en Azure" para los de sólo-gateway. El mapeo se consulta con
//               el flag correcto por cerebro: clasificador y embeddings salen por el stack
//               ruby-openai (openai_compat_only), Solid/Complex por el agente.
//           CORRECCIÓN a lo dicho en 69.5.87: un modelo no desplegado en Azure NO da 404 — se
//           SUSTITUYE en silencio, que es peor. gemini cae en gpt-5-mini por una colisión de
//           substring ("ge-MINI-ni" matchea el `m.include?('mini')` del mapeo), igual que
//           "MINI-max". El candado del controlador sigue siendo correcto: evita que el panel
//           mienta, no un 404.
//           Validado: YAML íntegro (103 entradas), la vista compila, y la tabla de etiquetas
//           verificada en los DOS proveedores llamando a los métodos reales de la app.
// 69.5.91 — REGRESIÓN DE 69.5.88: le di razonamiento a gemini sin subirle el techo.
//           VISTO EN goes-dev: gemini-3.1-flash-lite con effort medium y tope 1000 gastó
//           957 tokens razonando → 43 para responder → finish_reason "length", respuesta
//           truncada. El propio comentario de `reasoning_model?` ya lo advertía: los que
//           razonan "consumen tokens pensando, así que el presupuesto debe cubrir reasoning +
//           salida". En 69.5.88 moví el effort fuera del `if reasoning_model?` pero dejé el
//           TOPE adentro, así que gemini recibía esfuerzo con el presupuesto pelado.
//           FIX: el headroom sigue a la CAPACIDAD igual que el effort — doble del tope, mínimo
//           1500, el mismo criterio que usan los gpt-5.x desde 69.2.1.
//           Validado 7/7 sobre `build_chat_params` real: gemini pasa de 1000 a 2000, los que
//           NO razonan (gpt-4.1, claude por este stack) siguen en 1000 sin tocar.
// 69.5.92 — GEMINI + TOOLS MULTI-TURNO: preservar la `thought_signature`.
//           Visto en goes-dev: turno 1 llama la tool bien, turno 2 muere con HTTP 400 en 177ms
//           y 0 tokens — "Function call is missing a thought_signature in functionCall parts...
//           `default_api:captain--tools--faq_lookup`, position 8". Como el 400 lo traga el
//           fail-soft, desde afuera se ve como "a veces gemini no responde".
//           RAÍZ: gemini 3 devuelve una firma junto a cada tool call y EXIGE que se la
//           devuelvan en el historial. Viene en tool_calls[i].extra_content.google.
//           thought_signature, pero ruby_llm parsea el tool call a su estructura (id/name/
//           arguments) y DESCARTA el resto (providers/openai/tools.rb). Al rearmar el
//           historial, la firma ya no existe.
//           PROBADO: reenviando el tool_call CRUDO el turno 2 da 200; reconstruido como hace
//           la gema, 400. El problema es la reconstrucción, no el modelo.
//           FIX: recordar `extra_content` por id al PARSEAR y reinyectarlo al FORMATEAR.
//           Es auto-limitado: sólo guarda lo que el proveedor mandó, así que gpt y claude
//           —que no mandan nada— generan exactamente el mismo payload que antes.
//           NOTA DE MEDICIÓN: no pude reproducir el 400 punta a punta por ruby_llm porque la
//           firma es INTERMITENTE (el gateway balancea con weighted-score y no siempre la
//           devuelve) — eso explica que el error aparezca a veces sí y a veces no. Por eso la
//           validación es determinística sobre el módulo real: 8/8 (captura, reinyección,
//           no-invención cuando no hay firma, y tope FIFO).
// 69.5.91 — el ancla de institución deja de encapsular al ciudadano (conv #2356). El ancla
//           CATALOG_CLARIFIED (TTL 5 min) sólo entra si el turno es un seguimiento anafórico;
//           una pregunta nueva sin institución vuelve a búsqueda GENERAL. (El commit 259a6f85b
//           salió sin bump de esta constante; se asienta acá.)
// 69.5.92 — REGLA DURA de alcance: pregunta "¿de qué instituciones me puedes dar información?"
//           (conv #2397) recibía una respuesta creativa que enumeraba TIPOS de institución
//           ("ministerios, alcaldías, hospitales públicos, centros educativos…") con emojis.
//           El hueco era doble: (1) la rama :meta del motor solid decía "menciona los temas
//           generales / indica los temas específicos" — invitación directa a listar; (2) el
//           NO-ENUMERAR de assistant.liquid sólo prohibía instituciones específicas del
//           glosario, no categorías. Fix en AMBOS caminos, sin clasificador nuevo: respuesta
//           canónica única y verbatim "Puedo dar información de varias instituciones públicas,
//           ¿en qué puedo ayudarte?" + prohibición explícita de enumerar tipos/categorías,
//           adornos y emojis. La regla vive en el prompt (estilo), no en detección de intención.
//
// NOTA DE VERSIONADO (merge del PR #37 a main). Las entradas 69.5.90b a 69.5.90e de abajo son
//   las ETIQUETAS DE STAGING de la rama patch-29-jul mientras vivía sobre 69.5.90 —el commit
//   4d9ff3e7 que estaba en producción tras el rollback del 29-jul—. Se eligieron así para
//   distinguirla a simple vista en el panel. Mergeada a main, que ya trae 91 y 92, todo ese
//   trabajo aterriza como 69.5.93; las entradas se conservan porque documentan las cuatro
//   iteraciones y por qué cada una fue necesaria (los dos bugs los encontró el dueño probando).
// 69.5.93 — LA INSTITUCIÓN NO SE PERSISTE PARA INFORMAR. Parche temporal (rama patch-29-jul)
//           pedido por el dueño después de liberar A MANO diez conversaciones de producción:
//           #2400 volvió anclada a `simple` NUEVE veces en menos de dos horas. Liberar a mano
//           no se sostiene porque el anclaje se rehace en CADA turno que responde de la KB.
//           Quien escribió el ancla ya lo había previsto (agent_runner_service.rb, textual):
//           "Anclar sin condición de liberación sería cambiar un problema por otro". La
//           condición nunca se escribió; acá se escribe.
//           TRES escritores de `team_id` cortados —los que anclan como CONSECUENCIA de responder,
//           que el propio código marca con `Current.captain_bot_kb_routing` ("categoriza, NO
//           transfiere")—: anchor_institution_from_kb!, assign_team_from_backstop_match! y
//           sync_conversation_institution! (este último escribía al nombrarla y NUNCA limpiaba →
//           nombrabas Salud en el turno 1 y seguías en Salud en el turno 5).
//           INTACTOS a propósito: la institución de TRANSFERENCIA (assign_team_tool,
//           execute_pending_handoff!) y la de TRÁMITE (status_query_tool, marca
//           status_query_confirmed_team_id). Y si la conversación ya está en cola,
//           AssignmentHandler revierte cualquier cambio de team — ese candado ya existía.
//           SEGUNDO HUECO, el que 69.5.91 no cerró: ese fix le puso compuerta anafórica al
//           backstop pero NO al otro lector de la marca —FaqLookupTeamTool#team_scope_grounded?,
//           fuente (b)— así que el tool aceptaba el scope viejo en CUALQUIER turno. Ahora los DOS
//           lectores comparten criterio y dueño (Resolution.anaphoric_followup?, subido desde el
//           runner), en vez de dos copias que se desincronizan — el error que este proyecto ya
//           pagó con HUMAN_REQUEST_REGEX ≠ HUMAN_REQUEST_RE.
//           EFECTO COLATERAL BUENO, medido en conv #2400: el ancla también DESBLOQUEABA la oferta
//           de agente no pedida. `no_info_canned` ofrece humano sólo si
//           Institution::Resolution.resolved?, y el ancla (fuente 2/3) la hacía resolver sola →
//           el bot ofrecía "¿te paso con un agente de SIMPLE?" a alguien que jamás lo pidió
//           (verificado con el IntentResolver: handoff/humano == false en todo el hilo). Sin
//           ancla entra la rama A y el agente NO se menciona.
//           NO se tocó la ESCRITURA de la marca de continuidad: probé cortarla y cayó
//           agent_runner_841_sigla_backstop_spec:242, que protege un daño real (sin marca, el
//           follow-up "y en qué horario atienden?" se iba al backstop GLOBAL y servía la FAQ de
//           OTRA institución). El encajonamiento nunca fue escribir la marca — fue leerla sin
//           compuerta.
//           Perilla de runtime CAPTAIN_ANCLAR_INSTITUCION_INFORMATIVA (default apagado = parche
//           activo; `true` restaura la conducta previa sin deploy). Falla hacia "no anclar".
//           Verificación: línea base 217/0 medida ANTES de tocar nada, y comparación de la LISTA
//           de ejemplos fallidos (no del total, que puede empatar escondiendo un intercambio) →
//           258 examples, 16 failures, las 16 preexistentes en árbol limpio, CERO regresiones.
//
// 69.5.90c — EL CONECTOR NO CONVIERTE UNA PREGUNTA NUEVA EN SEGUIMIENTO (convs dev #2439/#2440).
//           El predicado anaphoric_followup? de 69.5.90b tenía dos defectos que ningún spec del
//           repo detectaba, y que en dev produjeron exactamente el daño que el parche venía a
//           evitar:
//             ciudadano: "quiero informacion del ministerio de vivienda" → servicios de MIVI  ✓
//             ciudadano: "y cual es la direccion de los hospitales?"     → DIRECCIÓN DE MIVI  ✗
//             ciudadano: "y el ISSS que servicios da?"                   → servicios de MIVI  ✗
//           (a) CORTOCIRCUITO DEL CONECTOR: `return true if FOLLOWUP_OPENER_RE` salía al ver la
//               "y", así que el chequeo de pregunta nueva NUNCA corría. Reordenar no alcanzaba:
//               NEW_QUESTION_OPENER_RE está anclado en \A y la frase empieza con "y", no "cual".
//           (b) CONTEO CIEGO: "ahora decime de ANDA" son 4 palabras ⇒ heredaba, aunque nombre
//               OTRA institución.
//           Ahora el conector se PELA (soporta encadenados: "y también el teléfono") y se juzga
//           el resto: interrogativo/verbo de pedido al frente ⇒ nueva; sigla propia en mayúsculas
//           (RNPN, ANDA, ISSS) ⇒ nueva; más de 4 palabras ⇒ nueva. Sólo un conector más una
//           referencia corta a un ATRIBUTO del turno anterior hereda ("y el horario?").
//           Sesgo deliberado, el de la doctrina: perder precisión en un seguimiento cuesta un
//           turno; heredar mal ENCAPSULA, que es el daño real.
//           Fijado en spec/enterprise/services/captain/institution/anaphoric_followup_spec.rb con
//           las frases REALES de dev — 17/17. Suite: 271 examples, 16 failures (las 16
//           preexistentes), cero regresiones.
//
// 69.5.90d — BÚSQUEDA GENERAL EN EL CAMINO PRINCIPAL; el ancla sobrevive SÓLO en el rescate.
//           Doctrina del dueño, textual: "siempre búsqueda general como tal, de una… ¿por qué
//           heredar? solo y únicamente CUANDO SE PIDA TRANSFERENCIA asegurarse de institución".
//           Aplicada donde corresponde, y MEDIDA donde no:
//
//           · faq_lookup_team_tool (camino PRINCIPAL) → la marca de continuidad ya NO acota. No
//             hace falta: ahí busca el LLM, que ve todo el hilo y arma él el query ("horario del
//             Ministerio de Vivienda"). El ancla era una muleta. Quedan dos fuentes de scope, las
//             dos verdad del turno: (a) conversation.team —que ya sólo escriben transferencia y
//             trámite— y (c) el ciudadano nombró la institución.
//
//           · kb_backstop_answer (RESCATE) → conserva la herencia, y no por comodidad. Quitarla
//             puso 3 specs en rojo, todos el mismo caso: hilo sobre MIVI + "y en que horario
//             atienden?" servía "Se responde los horarios de atención" DE MINSAL, y en la variante
//             con institución apagada contestaba "la atención para esa institución está
//             deshabilitada" hablando de Hacienda en un hilo de Vivienda. Es FUGA
//             CROSS-INSTITUCIÓN (#881/#1318) y el espejo del daño de dev #2439. La razón es
//             estructural: el backstop no tiene LLM ni contexto — embebe SOLO el último mensaje
//             (#728), así que "y en que horario atienden?" matchea los horarios de cualquiera.
//             Sin ancla no libera al ciudadano: le contesta con datos de otra institución.
//
//           MOTOR DE TRANSFERENCIA VALIDADO INTACTO (pedido explícito del dueño): 19 archivos de
//           handoff/transferencia corridos — assign_team_tool, handoff_tool, el gate completo (5),
//           transfer_intent_service, destination_pending, cola. CERO fallas nuevas.
//           Suite: 420 examples, 27 failures — las 27 preexistentes (verificado corriéndolas en
//           4d9ff3e7, el commit que está en producción: assign_team_tool_spec da 26/11 ahí mismo,
//           sin nada de este parche).
//
// 69.5.90e — LA BÚSQUEDA GENERAL EXISTE DE VERDAD: el rechazo manda a buscar, y los dos escenarios
//           que no la tenían la reciben. Cierra el agujero que 69.5.90d DESTAPÓ (no creó).
//
//           SÍNTOMA, reproducido LOCAL con la KB completa (237 FAQs de minsal cargadas):
//             "quiero informacion del ministerio de vivienda" → servicios de MIVI          ✓
//             "y cual es la direccion de los hospitales?"     → "no tengo esa información"  ✗
//             y en el replay de dev #2440, "y el ISSS que servicios da?" → SILENCIO (111 s).
//
//           CAUSA: `Captain::Scenario#tools` se DERIVA de los marcadores tool:// de la instrucción.
//           Dos escenarios —"Usuario pregunta clara con institución" y "Usuario pregunta sobre
//           atención"— sólo tenían faq_lookup_team, que EXIGE team_name. Sin institución que
//           pasarle, el LLM no llamaba nada y devolvía salida VACÍA → el runner caía al bailout de
//           agent_runner_service:321. El ancla tapaba esto dándole siempre un team; al quitarla
//           (69.5.93/90b) quedó al descubierto.
//
//           (1) faq_lookup_team_tool: el mensaje de scope rechazado decía "responde honestamente
//               que no tienes información y ofrece pasar con un agente humano" — doctrina
//               institución-primero de 27-jul, invertida por la del dueño. Ahora ordena usar
//               tool://faq_lookup con la MISMA consulta. Rechazar el scope adivinado sigue igual:
//               eso es lo que evita servir la KB de una institución como si fuera de otra.
//           (2) Los dos templates del seeder reciben tool://faq_lookup + la instrucción de cuándo
//               usarlo, y la migración 20260730050000 los aplica y regenera los escenarios.
//
//           MIGRACIÓN QUIRÚRGICA, NO EL SEEDER COMPLETO: perform! re-siembra los OCHO templates, y
//           el de trámites está DIVERGENTE (repo 19.165 vs prod 19.989, dieta de 4a2e3c7ac). Correr
//           el seeder empujaría esa dieta a prod de arrastre, y la prioridad del dueño es no tocar
//           trámites ni transferencia. La migración actualiza SÓLO los dos templates cambiados,
//           chequea el tope de 20k ANTES de guardar (el seeder es fail-soft: un save inválido deja
//           el escenario viejo y el fix invisible) y verifica el save.
//           Verificado local en las DOS cuentas: len 2230/2574, marcador presente, escenarios con
//           faq_lookup, y team_status_query INTACTO (5645 chars, timestamp viejo).

// Query param que anexamos a las redirecciones post-login y post-logout.
// El backend no lo lee — solo cambia la URL para invalidar la cache de HTML.

// 69.5.94 — CONSOLIDACIÓN main → develop. Une la línea de parches de producción
//           (69.5.90c-90e, 91, 92, 93: ancla de institución, búsqueda general,
//           tuteo, biblioteca de errores del proveedor, salida partida) con la línea
//           de CERO SILENCIO (olas 1-3 + A10: el LLM fuera de la transacción).
//           Doctrina del backstop resuelta a favor de la búsqueda global con
//           ContextRanking; el rescate acotado por institución se agrega como
//           SEGUNDA PASADA por SQL, sin re-embeber la pregunta del ciudadano.
//   69.5.95 — la institución la sabe la KB o el humano: se retira el escaneo de siglas
//            del mensaje del ciudadano y una ficha aprobada lleva institución siempre.
//            TOCA EL FRONTEND: el formulario de fichas exige institución al crear y al
//            aprobar, así que el bump importa — sin él un panel abierto sigue con el JS
//            viejo, no muestra la exigencia y el guardado rebota con 422 seco.
//   69.5.96 — una sola pregunta por turno: el juez de pertinencia juzga contra la misma
//            consulta que trajo los pasajes (cerraba «resume lo que te di» y «no me
//            transfieras, oriéntame» con «no cuento con información»); y la pregunta pegada
//            a una promesa retirada se va con ella («¡Hola! 👋 ¿Confirmas?»). Solo backend:
//            el bump es la marca del despliegue del aval del PDF 31-jul.

export const APP_REVISION = '69.5.96';

// Query param de cache-busting que usa appRevisionGuard.js al reconstruir la URL.
// Se perdió al resolver el conflicto de este archivo en la consolidación de main en develop
// (PR #51): el guard quedó importando una constante que ya no se exportaba y el build de Vite
// falla entero — `entrypoints/widget.js` no llega al manifest y el widget devuelve 500. El valor
// es el original ('_v', desde 41a31d2b03).
export const APP_REVISION_QUERY_PARAM = '_v';
